/* DealRoomsIndexPage.jsx — Operational Transaction Command Centre.
 *
 * iter-177 — Re-architected from a flat CRM list into a live ops layer:
 *   • Hero priority-attention card (deal most at risk)
 *   • Top-right KPI strip (Active / Needs attention / Exchange this week)
 *   • Operational filter/group pills
 *   • Stage groups → each row is a "mini operational command card"
 *       — severity strip · thumb · address
 *       — blocker chip · activity sub-line
 *       — mini stage rail (Offer→AML→Searches→Mortgage→Contracts→Exchange)
 *       — target exchange + due/overdue
 *       — momentum + participant avatars
 *       — quick action menu on hover
 *   • Right operational intelligence rail (snapshot + recent activity)
 *
 * Mobile (<md) keeps the calm vertically stacked card path (iter-172).
 * Desktop (≥md) renders the full transaction command-centre layout.
 *
 * All operational decoration is DETERMINISTIC from `deal.id` so renders
 * stay stable. Wait/momentum tones derive from the canonical helpers
 * (`waitDuration` + `relativeTime`) so this page never diverges from
 * Sales Progression or the Deal Room.
 */
import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronDown, ChevronRight, ChevronLeft, Search as SearchIcon, Home as HomeIcon, Clock,
  Calendar, AlertTriangle, Filter, Plus, MoreHorizontal,
  TrendingUp, TrendingDown, Minus, Sparkles, MessageSquare, FileText,
  Check, CircleDot, Mail, Bell, LogIn, Edit3, Activity, Link2, ArrowRight,
  Upload, Shield, Eye, ArrowUp, ArrowDown, Bookmark, Info, BedDouble, Bath, X,
  Building2,
  /* iter-384 · Approved-design final polish — More Filters
   * trigger uses the SlidersHorizontal glyph so the button
   * reads clearly as a secondary filter surface. */
  SlidersHorizontal,
  /* iter-309 — KPI strip refresh: Pipeline (£), Exchanges, Expected
   * Next Month, Live Transactions. PoundSterling + CalendarCheck +
   * CalendarClock + Activity (Activity already imported above). */
  PoundSterling, CalendarCheck, CalendarClock,
} from "lucide-react";

import { Sidebar } from "../components/Sidebar";
import { PageHeader, WORKSPACE_SHELL_CLASS } from "../components/ui/PageHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useChainStore, openCreateDeal, getDealExchangeTarget } from "../data/chainStore";
import { OperationalEventSheet } from "../components/dealroom/OperationalEventSheet";
import { waitToneFromDays, calendarDaysUntil } from "../lib/waitDuration";
import { relTimeFromIso } from "../lib/relativeTime";
import { useAuth } from "../data/authStore";
/* iter-281 — Deal Health scoring engine. Pure functions, no React.
 * Flag-gated below at the integration sites via
 * isHealthScoringEnabled() so we can sanity-check before flipping
 * the bands live. Default is OFF in preview. */
import { scoreDeal, isHealthScoringEnabled, dealStatusFor, classifyDeal } from "../data/dealHealth";
import { isPipelineEligible, numFromPrice } from "../data/pipelineValue";
/* iter-285 — Participant accountability signals feed the iter-281
 * health scorer so a silent solicitor weighs more than a silent
 * admin chase. Flag-gated; off-flag callers get the legacy
 * scoring behaviour. */
import {
  participantSignalsFor,
  isParticipantAccountabilityEnabled,
} from "../data/participants";
import { useOperatorPrefs } from "../hooks/useOperatorPrefs";
import { useDealConfidence } from "../hooks/useDealConfidence";
import { onPropertyImgError, isUploadedPropertyPhoto } from "../lib/propertyImage";
import { isActiveDeal, needsAttentionDeal } from "../data/dealFilters";
import { formatAgreedFee, resolveAgreedFeeGBP } from "../lib/feeFormat";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { MilestoneEditor } from "../components/deal-rooms/MilestoneEditPopover";
import { StageProgressHint } from "../components/StageProgressHint";

/* ─── Stage taxonomy ─────────────────────────────────────────────── */
const STAGE_LABEL = {
  offer:           "Under offer",
  memo:            "Memo issued",
  aml:             "AML & ID",
  solicitors:      "Solicitors instructed",
  searches:        "Searches & enquiries",
  enquiries:       "Enquiries",
  mortgage_app:    "Mortgage application",
  mortgage_offer:  "Mortgage offered",
  survey:          "Survey",
  exchange_ready:  "Ready to exchange",
  exchanged:       "Exchanged",
  completed:       "Completed",
};

/* Canonical 6-step rail used on every card. */
const RAIL_STEPS = ["Offer", "AML", "Searches", "Mortgage", "Contracts", "Exchange"];

/* P0 · Stage Pill Persistence (2026-02-28) — canonical Set of
 * valid Stage pill identifiers.  Hoisted to module scope so the
 * `useState` initialiser AND the post-mount sanitiser share a
 * single source of truth.  Adding a new pill to this Set makes it
 * both persistable AND validated by the sanitiser in one edit. */
const _VALID_STAGE_IDS = new Set([
  "all", "early", "progress", "exchange", "exchanged", "completed",
]);

/* Map every deal stage → completed-step index on the rail (0..5).
 * The "current" step is one past the last completed step. */
const STAGE_TO_RAIL_IDX = {
  offer: 0, memo: 0,
  aml: 1, solicitors: 1,
  searches: 2, enquiries: 2,
  mortgage_app: 3, mortgage_offer: 3,
  survey: 3,
  exchange_ready: 4, exchanged: 5, completed: 5,
};

/* iter-269 — Pilot Confidence Lock · final tenure-aware vocabulary.
 *
 * Freehold (8 nodes):
 *   Offer → AML → Survey → Contract → Searches → Mortgage →
 *   Exchange → Completion
 *
 * Leasehold (10 nodes, with optional BSA node = 11 when applicable):
 *   Offer → AML → Survey → Contract → [BSA] → Mgmt Pack →
 *   Contract Pack → Searches → Mortgage → Exchange → Completion
 *
 * Vocabulary doctrine:
 *   • Compact labels where space is tight: BSA · Mgmt Pack · Contract.
 *   • Mgmt Pack + Contract Pack ONLY surface on leasehold journeys.
 *   • BSA surfaces ONLY when a deal carries `applicableBSA: true`
 *     (typically high-rise apartments / modern developments >18m).
 *
 * One shared resolver — `railFor(tenure, opts)` — is the SINGLE
 * source of truth for which nodes appear. Every surface (Deal Rooms
 * list, hero, Forecasting micro-rail, Properties timeline chip)
 * MUST call this resolver. No surface may hardcode a milestone list. */
/* iter-291 — Pilot Timeline Unification.
 *
 * The hard-coded rail arrays were removed. Every surface now
 * consumes the single canonical journey module
 * (`/app/frontend/src/data/transactionJourney.js`), which owns:
 *   • Display labels (Mortgage → Searches → Survey → Contract →
 *     [BSA] → [Mgmt Pack] → Enquiries → Report → Exchange →
 *     Complete)
 *   • Stable internal node ids preserved behind the scenes
 *   • Tenure & BSA gating
 *
 * The local `railFor` / `railIdxFor` wrappers below thinly
 * delegate to the shared module — kept solely to preserve the
 * call sites already in place across this page. No surface may
 * hardcode a milestone list. */
import {
  railFor as _journeyRailFor,
  railIdsFor as _journeyRailIdsFor,
  activeIndexFor as _journeyActiveIndexFor,
  activeIndexForDeal as _journeyActiveIndexForDeal,
  isPreJourney as _journeyIsPreJourney,
  optsForDeal as _journeyOptsForDeal,
  resolveMortgage as _journeyResolveMortgage,
  closeCompletedSet as _journeyCloseCompletedSet,
} from "../data/transactionJourney";

function railFor(tenure, opts = {}) {
  return _journeyRailFor(tenure || "freehold", opts);
}
function railIdxFor(tenure, stage, opts = {}) {
  return _journeyActiveIndexFor(tenure || "freehold", stage, opts);
}
/* iter-alignment · P34 — Portfolio ↔ Deal Room parity.
 *
 * The list page's active rail index now reads through the canonical
 * `activeIndexForDeal(deal, opts)` resolver, which shares the SAME
 * completion signals as `resolveJourneyProgress()` in
 * DealRoomCanonicalPage (pilot `completedSteps`, `exchanged_at`,
 * `completed_at`).  Callers still hand in the pre-resolved
 * tenure-aware rail so the tenure default stays consistent. */
function railIdxForDeal(deal, opts = {}) {
  return _journeyActiveIndexForDeal(deal || {}, opts);
}

function readyToExchangeIdx(deal, opts, idx) {
  // Ready to Exchange: Exchange stays current until exchange is recorded (mirrors the Deal Room gate).
  const d = deal || {};
  const stage = String(d.stage || "").toLowerCase();
  const recorded = [...(d.completedSteps || []), ...(d.completed_steps || [])].includes("exchange");
  if (stage !== "exchange_ready" || d.exchangedAt || d.exchanged_at || d.completed_at || d.completedAt || recorded) return idx;
  const tenure = String(d.tenure || d.property?.tenure || d.property_tenure || "freehold").toLowerCase();
  const exIdx = _journeyRailIdsFor(tenure, opts).indexOf("exchange");
  return exIdx >= 0 ? Math.min(idx, exIdx) : idx;
}

const FALLBACK_THUMB =
  "https://images.unsplash.com/photo-1572120360610-d971b9d7767c?auto=format&fit=crop&w=240&q=60";

/* Deterministic hash for stable picks. */
const _hash = (s) => { let h = 0; for (let i = 0; i < (s || "").length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0; return Math.abs(h); };
const pick = (arr, seed) => arr[_hash(String(seed || "")) % arr.length];

/* ─── Deterministic operational decoration ──────────────────────────
 * These libraries echo the operational shape used in Sales Progression
 * so the two pages never diverge. Each row's blocker / momentum /
 * participants are picked deterministically from `deal.id`. */
const BLOCKERS = [
  { label: "Waiting on buyer solicitor",  sub: "No reply for 3 days",     tone: "rose"    },
  { label: "Waiting on management pack",  sub: "Requested 5 days ago",     tone: "amber"   },
  { label: "Waiting on mortgage offer",   sub: "No update for 4 days",     tone: "rose"    },
  { label: "Waiting on seller solicitor", sub: "Reviewing enquiries",      tone: "amber"   },
  { label: "Awaiting AML clearance",       sub: "Documents requested",      tone: "amber"   },
  { label: "Awaiting searches",            sub: "Local authority pending",  tone: "amber"   },
];

const READY_BLOCKERS = [
  { label: "Ready to exchange",  sub: "Contracts received today",  tone: "violet" },
  { label: "Ready to exchange",  sub: "Awaiting final signatures", tone: "violet" },
  { label: "Ready to exchange",  sub: "All parties ready",         tone: "violet" },
  { label: "Ready to exchange",  sub: "Exchange booked",           tone: "violet" },
];

/* iter-alignment · Deal Rooms Waiting-On (2026-02-19) — Canonical
 * stage → Next Best Action map.  Mirrors the FIRST entry of
 * `_RECOMMENDED_BY_STAGE` in `backend/routes_deal_command.py`
 * so the Deal Rooms list surfaces the SAME recommendation the
 * Command Centre would show.  When a deal has no blocker
 * (no `waiting_on` summary from `_batch_journey_evidence`),
 * we pivot the Waiting-On column into a Next Best Action row
 * driven by this taxonomy — never "All gates ticked". */
const STAGE_NEXT_BEST_ACTION = {
  offer:           "Issue Memorandum of Sale",
  aml:             "Issue Memorandum of Sale",
  memo:            "Issue Memorandum of Sale",
  solicitors:      "Confirm both solicitors instructed",
  mortgage_app:    "Progress mortgage application",
  survey:          "Book buyer's survey",
  searches:        "Order local authority searches",
  mgmt_pack:       "Request Management Pack",
  bsa:             "Complete Building Safety Act review",
  enquiries:       "Resolve outstanding enquiries",
  mortgage_offer:  "Chase mortgage offer issuance",
  exchange_ready:  "Exchange Contracts",
  exchanged:       "Confirm completion funds transfer",
  completed:       "Deal completed",
};

/* P0 · Reconcile Deal Rooms rows with the Transaction Journey
 * SSOT (2026-08-18 · turn 2) —
 * ---------------------------------------------------------------
 * The client-side `CANONICAL_STAGE_WAITING` map is RETIRED.
 * The backend `/api/deals` response now carries a `waiting_on`
 * summary computed from the SAME MILESTONES catalogue the
 * detail rail reads (see `routes_deals._batch_journey_evidence`
 * → the "highest-priority incomplete required + applicable
 * milestone in the active stage" rule).  The list surface
 * renders this envelope verbatim — never fabricates a
 * fallback.
 *
 * `annotateDeal` maps the backend `owner_role` to the compact
 * `WAITING_PARTIES` slot the row's kicker consumes.  This is
 * the ONLY per-role vocabulary the list still owns (avatar
 * icon + display name) — everything else (which milestone,
 * which stage, which action) is server-derived.
 */
const OWNER_ROLE_TO_PARTY_KEY = {
  agent:              "agent",
  buyer_solicitor:    "buyer_solicitor",
  seller_solicitor:   "seller_solicitor",
  broker:             "broker",
  mortgage_broker:    "broker",
  mortgage_lender:    "mortgage_lender",
  local_authority:    "local_authority",
  management_agent:   "management_agent",
  buyer:              "buyer",
};

const MOMENTUM = [
  { label: "Rising",   icon: TrendingUp,   tone: "emerald" },
  { label: "Stable",   icon: Minus,        tone: "slate"   },
  { label: "Slipping", icon: TrendingDown, tone: "amber"   },
  { label: "At risk",  icon: AlertTriangle,tone: "rose"    },
];

/* Activity sub-line for momentum context (used inside the row). */
const CHASE_LINES = [
  "Chase sent yesterday",
  "Chase sent 2h ago",
  "Reminder sent today",
  "Email sent 4h ago",
  "Contracts received today",
  "Last update: 3d ago",
];

/* iter-199 — Latest Activity catalogue. The Deal Rooms list now
 * surfaces a concise "what changed since I last worked this" line
 * per row (not a mini activity feed). Each entry maps to:
 *   • a concise operational headline ("Mortgage DIP uploaded")
 *   • an actor name + initials (the human who made the move)
 *   • a calm sub-line ("Documents requested · Email sent 4h ago")
 *   • an icon for the meta line
 * Picked deterministically per deal so renders stay stable. */
const LATEST_ACTIVITY = [
  { headline: "Mortgage DIP uploaded",        actor: "Halifax broker",   initials: "HB", sub: "Documents shared",       subIcon: FileText },
  { headline: "Searches returned",             actor: "Local authority",  initials: "LA", sub: "LA pending",              subIcon: Check },
  { headline: "Contracts received from seller",actor: "Taylor Rose MW",   initials: "TR", sub: "All parties reviewing",   subIcon: FileText },
  { headline: "Solicitor replied",             actor: "JMW Solicitors",   initials: "JS", sub: "Awaiting signatures",     subIcon: Mail },
  { headline: "Management pack uploaded",      actor: "Block Management", initials: "BM", sub: "Documents shared",        subIcon: Upload },
  { headline: "Chase sent",                    actor: "Demo Director",    initials: "DD", sub: "Email sent 4h ago",       subIcon: MessageSquare },
  { headline: "Local authority search update", actor: "Demo Director",    initials: "DD", sub: "LA pending",              subIcon: FileText },
  { headline: "Documents requested",           actor: "Taylor Rose MW",   initials: "TR", sub: "Email sent 4h ago",       subIcon: Mail },
];

/* iter-199 — Workspace resumption wording. The Deal Rooms list is a
 * launcher, not an audit trail. "Last opened" reads as passive
 * operational memory: "Opened 2h ago" / "Viewed yesterday" /
 * "Opened this morning" — never raw timestamps. */
function lastOpenedLabel(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const mins = Math.floor((Date.now() - t) / 60_000);
  if (mins < 5)    return "Just opened";
  if (mins < 60)   return `Opened ${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 6)   return `Opened ${hours}h ago`;
  if (hours < 12)  return "Opened this morning";
  if (hours < 24)  return "Opened today";
  if (hours < 36)  return "Viewed yesterday";
  const days = Math.floor(hours / 24);
  if (days < 7)    return `Viewed ${days}d ago`;
  return "Not opened recently";
}

/* Participant initials — deterministic per deal. Mirrors the
 *  estate-agency four roles: agent · seller solicitor · buyer
 *  solicitor · broker. */
const PARTICIPANT_POOL = ["DD","LS","JS","TB","MW","AC","RH","KP","CO","EW","JP"];

const fmtDateShort = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  /* iter-alignment · P0 · Deal Rooms Refinement (2026-02-20)
     — include the year only when the target date sits in a
     different calendar year to today.  Same-year dates stay
     compact ("4 Jun"); cross-year dates read unambiguously
     ("4 Jun 2027"). */
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("en-GB", sameYear
    ? { day: "numeric", month: "short" }
    : { day: "numeric", month: "short", year: "numeric" });
};

const daysUntil = (iso) => calendarDaysUntil(iso);

/* Tone class maps — restrained, light-mode operational palette. */
const TONE_BAR = {
  rose:    "bg-rose-500",
  amber:   "bg-amber-500",
  emerald: "bg-emerald-500",
  violet:  "bg-violet-500",
  slate:   "bg-slate-300",
};
const TONE_PILL = {
  rose:    "bg-rose-50 text-rose-700 ring-rose-200/70",
  amber:   "bg-amber-50 text-amber-700 ring-amber-200/70",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200/70",
  violet:  "bg-violet-50 text-violet-700 ring-violet-200/70",
  slate:   "bg-slate-100 text-slate-700 ring-slate-200/70",
};
const TONE_TEXT = {
  rose: "text-rose-700", amber: "text-amber-700", emerald: "text-emerald-700",
  violet: "text-violet-700", slate: "text-slate-700",
};

/* P0 · Overall Status Badge (2026-08-18) —
 * ---------------------------------------------------------------
 * A single lifecycle badge rendered on every card + row surface.
 * Reads `ops.overallStatus` (`under_offer` | `exchanged` |
 * `completed`) — a pure derivation from the deal SSOT
 * (stage / exchanged_at / completed_at / completed_steps).  See
 * `annotateDeal` for the exact evidence walk.
 *
 * Vocabulary + tokens locked by the operator:
 *   • Under Offer  → bg #F3E8FF · text #7C3AED · ring #DDD6FE
 *   • Exchanged    → emerald-50 · emerald-700 · emerald-200
 *   • Completed    → emerald-100 · emerald-800 · emerald-300
 *     (deeper green — reads as "banked").
 *
 * Preserves the amber warning tone for the row-level severity
 * strip; this badge is a LIFECYCLE identity, not an urgency
 * signal.  Coloured differently so operators can distinguish
 * lifecycle status from operational health at a glance. */
const OVERALL_STATUS_VOCAB = {
  under_offer: { label: "Under Offer", bg: "#F3E8FF", fg: "#7C3AED", ring: "#DDD6FE" },
  exchanged:   { label: "Exchanged",   bg: "#ECFDF5", fg: "#047857", ring: "#A7F3D0" },
  completed:   { label: "Completed",   bg: "#D1FAE5", fg: "#065F46", ring: "#6EE7B7" },
};
function OverallStatusBadge({ status, dealId, className = "" }) {
  const spec = OVERALL_STATUS_VOCAB[status] || OVERALL_STATUS_VOCAB.under_offer;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10.5px] font-semibold ring-1 leading-[1.25] tracking-[-0.005em] ${className}`}
      style={{ backgroundColor: spec.bg, color: spec.fg, boxShadow: `inset 0 0 0 1px ${spec.ring}` }}
      data-testid={dealId ? `dr-row-overall-status-${dealId}` : "dr-overall-status"}
      data-overall-status={status || "under_offer"}
    >
      {spec.label}
    </span>
  );
}

/* iter-207 — Waiting-on vocabulary.
 *
 * The Waiting On column is the operational core of the page. The
 * helper maps a blocker label onto the canonical conveyancing
 * counterparty (buyer solicitor / mortgage lender / local authority
 * / seller solicitor / search provider / management agent / buyer)
 * and returns a stable role chip + status copy. Calm operational
 * vocabulary, never CRM "tags". */
const WAITING_PARTIES = {
  buyer_solicitor:  { key: "buyer_solicitor",  label: "Buyer solicitor",   initials: "BS" },
  seller_solicitor: { key: "seller_solicitor", label: "Seller solicitor",  initials: "SS" },
  mortgage_lender:  { key: "mortgage_lender",  label: "Mortgage lender",   initials: "ML" },
  broker:           { key: "broker",           label: "Broker",            initials: "BR" },
  agent:            { key: "agent",            label: "Agent",             initials: "AG" },
  local_authority:  { key: "local_authority",  label: "Local authority",   initials: "LA" },
  search_provider:  { key: "search_provider",  label: "Search provider",   initials: "SP" },
  management_agent: { key: "management_agent", label: "Management agent",  initials: "MA" },
  buyer:            { key: "buyer",            label: "Buyer",             initials: "BU" },
};

function deriveWaitingOn(deal, blocker, waitDays) {
  // Completed is terminal: nobody is waiting (chase history is kept separately).
  if (String(deal?.stage || "").toLowerCase() === "completed") {
    return { key: "none", label: "No one", initials: "—", status: "Transaction complete",
             sub: "Deal completed", tone: "emerald", mode: "terminal" };
  }
  const label = String(blocker?.label || "").toLowerCase();
  const mode = String(blocker?.mode || "waiting");
  let party = WAITING_PARTIES.buyer_solicitor;
  /* iter-alignment · Deal Rooms Waiting-On (2026-02-19) —
   * When the blocker is a Next Best Action (no waiting party),
   * we synthesise a party-shaped envelope from the ownerLabel
   * so ContextBlock keeps its two-line shape without touching
   * the layout.  Initials fall back to "NB" (Next Best) when
   * no owner is known. */
  if (mode === "next_best_action") {
    const _owner = blocker?.ownerLabel || "You";
    const _initials = _owner === "You"
      ? "YO"
      : _owner.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "NB";
    party = {
      key: "next_best_action",
      label: _owner,
      initials: _initials,
    };
    // Calm violet tone for a proactive recommendation row.
    return {
      ...party,
      status: blocker?.copy || "Next best action",
      sub: blocker?.sub || blocker?.copy || "Next best action",
      tone: "violet",
      mode,
    };
  }
  /* P0 · Reconcile Deal Rooms rows with the Transaction Journey
   * SSOT (2026-08-18) — prefer the canonical `blocker.partyKey`
   * populated by `annotateDeal`; the regex fall-through survives
   * ONLY as a defensive path for legacy call sites that still
   * pass a raw label-only blocker. */
  const partyKey = blocker?.partyKey;
  if (partyKey && WAITING_PARTIES[partyKey]) {
    party = WAITING_PARTIES[partyKey];
  } else if (/buyer solicitor/.test(label))      party = WAITING_PARTIES.buyer_solicitor;
  else if (/seller solicitor/.test(label))       party = WAITING_PARTIES.seller_solicitor;
  else if (/mortgage|lender|offer/.test(label))  party = WAITING_PARTIES.mortgage_lender;
  else if (/local authority|la pending/.test(label)) party = WAITING_PARTIES.local_authority;
  else if (/search/.test(label))                 party = WAITING_PARTIES.search_provider;
  else if (/management|mgmt pack/.test(label))   party = WAITING_PARTIES.management_agent;
  else if (/aml|id|buyer/.test(label))           party = WAITING_PARTIES.buyer;

  // Short tone-coloured status line — the operational state, never
  // the same string as the blocker label.  Prefer the canonical
  // `blocker.sub` for non-silent rows so the list row surfaces
  // the SAME copy as the detail rail's active stage.
  const tone = waitDays >= 6 ? "rose" : waitDays >= 3 ? "amber" : "emerald";
  const status = waitDays >= 6
    ? `Silent ${waitDays}d`
    : waitDays >= 3
      ? `Silent ${waitDays}d`
      : blocker?.sub
        || (/searches?/.test(label)
          ? "Searches outstanding"
          : /management|mgmt/.test(label)
            ? "Pack pending"
            : /mortgage|offer/.test(label)
              ? "Awaiting offer"
              : /aml/.test(label)
                ? "ID pending"
                : "On track");

  // Calm muted sub-line — what the operator can act on next.
  // Prefer canonical `blocker.sub` (SSOT-aligned with the detail
  // rail) and fall back to the retired regex derivation only
  // when a legacy blocker doesn't carry a canonical sub.
  //
  // iter-alignment · Deal Rooms Waiting-On (2026-02-19) — the
  // legacy "No outstanding items" fallback is retired.  If we
  // ever reach this branch without a canonical sub we surface
  // the SSOT stage label rather than a contradictory copy so
  // "Buyer solicitor · All gates ticked" is architecturally
  // impossible.
  const sub = blocker?.sub
    || (/searches?/.test(label) ? "Awaiting searches"
       : /mortgage|offer/.test(label) ? "Valuation received"
       : /local authority/.test(label) ? "LA pending"
       : /management|mgmt/.test(label) ? "Awaiting pack"
       : /seller solicitor/.test(label) ? "Awaiting contracts"
       : /aml/.test(label) ? "ID pending"
       : /buyer/.test(label) ? "ID verified"
       : blocker?.copy || "Awaiting update");

  return { ...party, status, sub, tone, mode };
}

/* ─── Annotate a deal with everything the row card needs ───────── */
function annotateDeal(deal, findProperty) {
  const stage = String(deal?.stage || deal?.status || "offer").toLowerCase();
  const property = deal?.propertyId && findProperty ? findProperty(deal.propertyId) : null;

  const address = deal?.address
                || property?.address
                || deal?.name
                || "Untitled deal";

  const location = property?.type || deal?.propertyType || deal?.location || "Residential";
  const thumb    = deal?.thumbnail || deal?.imageUrl || property?.thumb || FALLBACK_THUMB;
  const photo    = [deal?.thumbnail, deal?.imageUrl, property?.thumb].find(isUploadedPropertyPhoto) || null;
  // iter-263 — Property metadata threaded through to the cell so
  // the wider 360px identity column can surface a calm
  // Beds · Baths · Parking line beneath the postcode.
  const beds          = property?.beds ?? deal?.beds ?? null;
  const baths         = property?.baths ?? deal?.baths ?? null;
  const parking       = property?.parking ?? deal?.parking ?? null;
  const parkingSpaces = property?.parkingSpaces ?? deal?.parkingSpaces ?? null;

  /* iter-207 — Tenure (Freehold / Leasehold ONLY).
   *
   * Per the iter-207 doctrine the Deal Rooms list is operational
   * infrastructure, not property marketing. We surface tenure if the
   * deal or property explicitly carries it; otherwise `null` so the
   * UI renders a calm em-dash instead of inventing a value. */
  const tenureRaw = String(deal?.tenure || property?.tenure || "").toLowerCase().trim();
  const tenure = tenureRaw === "freehold" || tenureRaw === "leasehold" ? tenureRaw : null;

  // Canonical only (backend: days since last milestone completion with a gate outstanding); null = none.
  const _waitRaw = deal?.waiting_days ?? deal?.waitingDays;
  const waitDays = _waitRaw != null && Number.isFinite(Number(_waitRaw)) ? Number(_waitRaw) : null;
  const ready    = stage === "exchange_ready" || stage === "exchanged" || stage === "completed";
  /* P0 · Reconcile Deal Rooms rows with the Transaction
   * Journey SSOT (2026-08-18 · turn 2) — the blocker is now
   * derived directly from the backend `deal.waiting_on`
   * envelope populated by `_batch_journey_evidence()` from
   * the SAME MILESTONES catalogue the detail rail reads.
   * When the backend returns a summary, the list row surfaces
   * that milestone's owner + label verbatim.  When the summary
   * is `None` (every gate ticked / no active step), the row
   * paints the terminal "Ready to exchange" copy for post-
   * mortgage-preparation deals and stays calm-silent otherwise.
   *
   * The frontend no longer owns a per-stage catalogue — see
   * `OWNER_ROLE_TO_PARTY_KEY` above (a compact display-vocab
   * mapping only, not a source of truth for progress).
   */
  const _waitingSummary = deal?.waiting_on || deal?.waitingOn || null;
  /* iter-alignment · Deep NBA Payload (2026-02-19) — the
   * backend `/api/deals` response now carries a canonical
   * `next_best_action` envelope computed by
   * `_derive_next_best_action` in `routes_deals.py`.  Prefer
   * that shape verbatim; the client-side `STAGE_NEXT_BEST_ACTION`
   * map remains as a defensive fallback for legacy callers
   * (offline snapshots, pilot fixtures) that pre-date the
   * payload. */
  const _serverNba = deal?.next_best_action || deal?.nextBestAction || null;
  let blocker;
  if (_waitingSummary) {
    const _partyKey = OWNER_ROLE_TO_PARTY_KEY[String(_waitingSummary.owner_role || "").toLowerCase()]
                   || "buyer_solicitor";
    blocker = {
      label: _waitingSummary.milestone_label || "Awaiting update",
      copy:  _waitingSummary.milestone_label || "Awaiting update",
      sub:   _waitingSummary.milestone_label || "Awaiting update",
      tone:  "amber",
      partyKey: _partyKey,
      milestoneId: _waitingSummary.milestone_id || null,
      stageId:     _waitingSummary.stage_id || null,
      mode: "waiting",
    };
  } else if (ready) {
    /* Ready-to-advance deals never sit "waiting" on a third
     * party; the next canonical step is on the operator.  Prefer
     * the server-side NBA label; fall through to the legacy map
     * for backwards compatibility. */
    const _readyAction = _serverNba?.label || STAGE_NEXT_BEST_ACTION[stage] || "Complete stage";
    blocker = {
      label: _readyAction,
      copy:  _readyAction,
      sub:   _readyAction,
      tone:  "violet",
      partyKey: null,
      mode: "next_best_action",
      ownerLabel: "You",
    };
  } else {
    /* No blocker on the SSOT AND not ready.  Prefer the
     * canonical NBA from the backend; the frontend map is now a
     * defensive fallback for legacy offline payloads only. */
    const _stageAction = _serverNba?.label || STAGE_NEXT_BEST_ACTION[stage] || "Progress next stage";
    const _ownerLabel  = deal?.assignee_name
                      || deal?.owner_name
                      || deal?.assigneeName
                      || deal?.ownerName
                      || "You";
    blocker = {
      label: _stageAction,
      copy:  _stageAction,
      sub:   _stageAction,
      tone:  "violet",
      partyKey: null,
      mode: "next_best_action",
      ownerLabel: _ownerLabel,
    };
  }
  // Adjust blocker tone for current days waited.  When we're in
  // an active waiting-on row and silence has crossed the rose
  // threshold, promote the mode to "action_needed" so the eyebrow
  // reads ACTION NEEDED and the operator sees the row as
  // needing intervention (not passive waiting).
  if (!ready && _waitingSummary) {
    const tone = waitToneFromDays(waitDays);
    if (tone === "amber" && blocker.tone === "rose") blocker.tone = "amber";
    if (tone === "rose") {
      blocker.mode = "action_needed";
    }
  }

  /* iter-318 — Inline milestone editing. If any milestone has been
   * marked `Blocked` by an operator (via the inline popover), that
   * blocker takes precedence over the seed BLOCKERS pick — it
   * carries the real operator copy + bubbles up the row's severity
   * to rose. Use the FIRST blocked milestone in stage order so the
   * timeline + Context column stay in lockstep. */
  const _milestoneStates = deal?.milestoneStates && typeof deal.milestoneStates === "object"
    ? deal.milestoneStates
    : null;
  if (_milestoneStates) {
    const blockedStageId = Object.keys(_milestoneStates).find(
      (k) => _milestoneStates[k]?.status === "blocked",
    );
    if (blockedStageId) {
      const m = _milestoneStates[blockedStageId];
      blocker.label = `Blocked at ${blockedStageId}`;
      blocker.copy  = m?.note?.trim() || `Blocked at ${blockedStageId}`;
      blocker.sub   = m?.owner
        ? `Waiting on ${String(m.owner).replace(/_/g, " ")}`
        : "Marked blocked";
      blocker.tone  = "rose";
    }
  }

  const completedIdx = STAGE_TO_RAIL_IDX[stage] ?? 0;
  // Current step = completedIdx (still working on this step). For
  // exchange_ready: stage 4 (Contracts done, Exchange current).
  const currentIdx = ready ? 5 : Math.min(5, completedIdx);

  // Target exchange — canonical only; no invented fallback (shown as "Not set").
  const targetIso = deal?.targetExchange || deal?.target_exchange || null;
  const daysToExchange = daysUntil(targetIso);

  // Momentum — derived from rail position + wait tone. Calm, plausible:
  //   • ready (idx 5) → Rising
  //   • waitDays ≥ 6  → At risk
  //   • waitDays ≥ 3  → Slipping
  //   • else          → Stable
  let momentum;
  if (ready)                    momentum = MOMENTUM[0]; // Rising
  else if (waitDays >= 6)       momentum = MOMENTUM[3]; // At risk
  else if (waitDays >= 3)       momentum = MOMENTUM[2]; // Slipping
  else                          momentum = MOMENTUM[1]; // Stable

  // Severity — used by the left edge strip + sort priority.
  //   ready: violet · risk: rose · slipping: amber · stable: slate.
  // Shared classification (flag / blocked milestone / missed target / blocking issue → At Risk;
  // 7d+ without progress or a warning issue → Watch). Age alone never reads At Risk.
  const classification = classifyDeal({ ...deal, milestoneStates: _milestoneStates || deal?.milestoneStates });
  const severity = classification.atRisk ? "rose" : classification.watch ? "amber" : ready ? "violet" : "slate";

  // Participants — 3 visible + "+N" overflow. Deterministic.
  const h = _hash(deal?.id || "");
  const participants = [
    PARTICIPANT_POOL[h % PARTICIPANT_POOL.length],
    PARTICIPANT_POOL[(h >> 3) % PARTICIPANT_POOL.length],
    PARTICIPANT_POOL[(h >> 6) % PARTICIPANT_POOL.length],
  ];
  const participantOverflow = (h % 3) + 1; // 1..3

  // Activity sub-line.
  // P0 · Card ↔ Detail Reconciliation (2026-08-18) — real-activity
  // driven copy.  `deal.updated_at` (or `last_activity_at`) is the
  // canonical SSOT freshness signal derived from `deal_activity`
  // rows on the backend.  Fabricated `CHASE_LINES` copy is retired
  // (mock strings like "Contracts received today" no longer paint
  // a card that has had zero activity today).
  const _activityIsoRaw = deal?.last_activity_at || deal?.lastMovementAt
                       || deal?.updated_at || deal?.updatedAt
                       || null;
  const _activityAgeH = _activityIsoRaw
    ? (Date.now() - Date.parse(_activityIsoRaw)) / 3_600_000
    : null;
  const chaseLine = (() => {
    if (_activityAgeH == null || !Number.isFinite(_activityAgeH)) return null;
    if (_activityAgeH < 6)   return "Activity today";
    if (_activityAgeH < 24)  return "Activity today";
    if (_activityAgeH < 36)  return "Activity yesterday";
    const days = Math.floor(_activityAgeH / 24);
    if (days < 7)  return `Activity ${days}d ago`;
    if (days < 30) return `Activity ${Math.floor(days / 7)}w ago`;
    return "No recent activity";
  })();

  // Last activity ISO for momentum/freshness.
  const lastIso = deal?.last_activity_at || deal?.lastMovementAt
               || deal?.updated_at || deal?.updatedAt || null;
  // Real saved chase timestamp only (null → no "Last chased" caption).
  const lastChasedIso = deal?.last_chased_at || deal?.lastChasedAt || null;

  // iter-199 — workspace resumption signals.
  const lastOpenedIso = deal?.last_opened_at
                     || new Date(Date.now() - ((_hash(deal?.id + "o") % 36) + 1) * 3_600_000).toISOString();
  const lastOpenedAge = (Date.now() - Date.parse(lastOpenedIso || lastIso)) / 3_600_000;
  // "Fresh update since last visit" — purple new-update dot when the
  // latest movement is more recent than the operator's last open.
  const lastActivityAge = (Date.now() - Date.parse(lastIso)) / 3_600_000;
  const hasNewUpdate = lastActivityAge < lastOpenedAge - 0.5;

  // iter-199 — latest operational movement headline + actor.
  // P0 · Card ↔ Detail Reconciliation (2026-08-18) — the
  // fabricated `LATEST_ACTIVITY` catalogue is retired.  Consumers
  // that still read `ops.latestActivity` fall back to a real,
  // activity-age-driven headline so demo rows never advertise
  // "Mortgage DIP uploaded" for a deal that has had no such event.
  const latestActivity = chaseLine
    ? { headline: chaseLine, actor: null, initials: null, sub: null, subIcon: null }
    : null;

  // Fee exposure / agreed price — used by the hero card + Highest
  // Fee sort.  iter-alignment · P0 · Deal Rooms Data & Logic
  // Integrity (2026-02-15) — resolved via the canonical
  // `resolveAgreedFeeGBP` SSOT so percentage deals compute
  // `agreedPrice × feePct` instead of silently returning 0 when
  // Fee Basis Integrity has cleared the stored fixed fee.  The
  // deterministic pilot fallback (`(h % 30 + 8) × 1000`) is kept
  // ONLY for demo rows where BOTH resolvers return null.
  const _resolvedFee = resolveAgreedFeeGBP(deal);
  // Canonical fee only (resolveAgreedFeeGBP); null when the deal has no fee terms — never invented.
  const fee = (_resolvedFee != null && _resolvedFee > 0) ? _resolvedFee : null;

  // iter-205A — completion target (same fallback rhythm as exchange
  // so undated demo deals don't crash sorting). Real deals carry
  // `targetCompletion` from chainStore.
  const completionIso = deal?.targetCompletion || deal?.target_completion || null;
  const daysToCompletion = daysUntil(completionIso);
  // Overdue — exchange / completion date already in the past.
  const overdueDays = (daysToExchange != null && daysToExchange < 0)
    ? Math.abs(daysToExchange)
    : (daysToCompletion != null && daysToCompletion < 0)
      ? Math.abs(daysToCompletion)
      : 0;

  /* iter-207 — Tenure-aware milestone rail.
   *
   * iter-266 — RESTORED. The list page renders the canonical
   * Freehold (7 nodes: Offer→AML→Mortgage→Searches→Contracts→
   * Exchange→Complete) or Leasehold (8 nodes: with Mgmt Pack
   * inserted between Searches + Contracts) rails per the
   * approved reference. The iter-262 override that forced all
   * rows onto a 6-node freehold spine is reverted — the
   * timeline now claims the dominant column width and can
   * comfortably host the full rail.
   *
   * Tenure falls back to "freehold" when unknown so the rail
   * still reads correctly before the operator confirms tenure.
   */
  const railTenure = tenure || "freehold";
  /* iter-306 — Threading `optsForDeal()` here makes the list-page
   * rail finance-aware. Cash buyers skip Mortgage, leasehold deals
   * default to surfacing BSA, and `applicableBSA: false` overrides
   * back to the BSA-free rail.
   *
   * iter-alignment · P34 — Portfolio ↔ Deal Room parity.  The
   * active index now flows through `railIdxForDeal(deal, opts)`,
   * which honours the same `completedSteps` / `exchanged_at` /
   * `completed_at` signals as `resolveJourneyProgress` inside the
   * canonical Deal Room — so the two surfaces stay in lockstep for
   * every non-milestone progression signal. */
  const railOpts = _journeyOptsForDeal(deal || {});
  const railLabels = railFor(railTenure, railOpts);
  let railActiveIdx = railIdxForDeal(deal || {}, railOpts);
  railActiveIdx = readyToExchangeIdx(deal, railOpts, railActiveIdx);
  /* iter-318 — Inline milestone editing. If the operator has
   * marked the canonical active stage (or any earlier stage) as
   * `complete` via the popover, advance the active rail index
   * past the run of completed milestones so the timeline tick
   * + violet ring move with the operator's edits. */
  if (_milestoneStates && Array.isArray(railLabels)) {
    while (railActiveIdx < railLabels.length - 1) {
      const lbl = railLabels[railActiveIdx];
      if (_milestoneStates[lbl]?.status === "complete") {
        railActiveIdx += 1;
      } else {
        break;
      }
    }
  }
  /* iter-alignment · P · Independent In Progress States (2026-08-17)
   * — A stage OTHER than the Current Stage can independently show
   * `In Progress` when the operator has flagged its milestone
   * status via the inline popover.  Derived directly from the
   * canonical `_milestoneStates` SSOT (label-keyed on the list
   * envelope) — no new state, no duplicate wiring.  Completion is
   * TERMINAL and can never surface as In Progress; the Current
   * Stage still resolves via `railActiveIdx` so an in-progress
   * later stage NEVER advances the cursor.  Zero impact on
   * completed / current / neutral rendering. */
  const railInProgressSet = new Set();
  if (_milestoneStates && Array.isArray(railLabels)) {
    for (let _i = 0; _i < railLabels.length; _i += 1) {
      const lbl = railLabels[_i];
      const status = String(_milestoneStates[lbl]?.status || "").toLowerCase();
      if (status !== "in_progress" && status !== "in-progress") continue;
      if (lbl === "Completion") continue;           // never in progress
      if (_i < railActiveIdx) continue;              // completed lane
      if (_i === railActiveIdx) continue;            // current stage
      railInProgressSet.add(lbl);
    }
  }
  // iter-266 — Leasehold context now lives inside the canonical
  // blocker line ("Silent 5d · Awaiting management pack") so the
  // standalone amber strip is retired. We keep the flag false so
  // any in-flight markup that still consults it doesn't render
  // the legacy strip.
  const leaseholdHint = false;

  /* P0 · Deal Rooms Card ↔ Detail SSOT Reconciliation
   * (2026-08-18) — canonical current-step + progress derivation.
   *
   *   • `currentStepId`  — canonical journey step id at
   *     `railActiveIdx` (mirrors the detail's
   *     `steps[currentFocusIdx].id`).
   *   • `currentStepLabel` — display label (Mortgage / Contract /
   *     Mgmt Pack / BSA / Enquiries / Exchange / Completion).
   *   • `currentStepIsInProgress` — true when the deal is
   *     actively working on the current step (default when the
   *     step isn't yet complete) OR when the milestone-override
   *     layer has flipped a stage to `in_progress`.
   *   • `completedStepCount` / `totalStepCount` — derived from
   *     the same closure the detail uses so "2 of 6 complete"
   *     matches the vertical timeline exactly.
   *   • `overallStatus`  — three-tier lifecycle bucket:
   *       - `completed`  → stage=completed / completed_at set
   *       - `exchanged`  → stage=exchanged / exchanged_at set
   *       - `under_offer` → every pre-exchange lifecycle
   *     Read by the new `<OverallStatusBadge/>` renderer and by
   *     the mobile status pill.
   */
  const _railStepIds = _journeyRailIdsFor(railTenure, railOpts);
  const _completedSetForCount = new Set();
  const _fold = (arr) => {
    if (!Array.isArray(arr)) return;
    for (const s of arr) if (s) _completedSetForCount.add(String(s));
  };
  _fold(deal?.completedSteps);
  _fold(deal?.completed_steps);
  _fold(deal?.completedStepsApi); // canonical server-derived steps (store field name) — same source as the Deal Room
  const _stageLc = String(deal?.stage || "").toLowerCase();
  // P0 · Card ↔ Detail Reconciliation (2026-08-18) — the strict
  // exchanged / completed lifecycle branches deliberately EXCLUDE
  // `exchange_ready` (Ready to Exchange) because contracts have
  // NOT yet been exchanged in that state — the deal is still
  // pre-exchange and must paint the "Under Offer" identity.  The
  // journey resolver's closure logic elsewhere still treats
  // exchange_ready as post-exchange for RAIL progression only.
  const _exchangedFlag = !!(deal?.exchanged_at || deal?.exchangedAt)
    || _stageLc === "exchanged"
    || _stageLc === "completed";
  // Classification = confirmed lifecycle only (API stamps / stage); checklist progress never classifies.
  const _completedFlag = !!(deal?.completed_at || deal?.completedAt)
    || _stageLc === "completed";
  // Current focus = first unrecorded stage from the canonical baseline (Deal Room `resolveCurrentFocusIdx` rule);
  // recorded stages are skipped forward, earlier stages are never inferred.
  if (!_exchangedFlag && !_completedFlag) {
    while (railActiveIdx < _railStepIds.length - 1 && _completedSetForCount.has(_railStepIds[railActiveIdx])) railActiveIdx += 1;
  }
  const _closedSet = _journeyCloseCompletedSet({
    nodes: _railStepIds.map((id) => ({ id })),
    completedSet: _completedSetForCount,
    exchangedFlag: _exchangedFlag,
    completedFlag: _completedFlag,
    baselineIdx: railActiveIdx,
  });
  // Rail indices with a RECORDED completion (green tick); passed stages without one render as dashed dots.
  const railRecordedSet = new Set();
  railLabels.forEach((lbl, i) => {
    const id = _railStepIds[i];
    if (_closedSet.has(id) || _milestoneStates?.[lbl]?.status === "complete"
        || (id === "exchange" && (deal?.exchanged_at || deal?.exchangedAt))
        || (id === "complete" && (deal?.completed_at || deal?.completedAt))) railRecordedSet.add(i);
  });
  // Count = recorded completions (green ticks) only; deal status never inflates it.
  const completedStepCount = railRecordedSet.size;
  const totalStepCount = _railStepIds.length;
  const currentStepId = railActiveIdx >= 0 && railActiveIdx < _railStepIds.length
    ? _railStepIds[railActiveIdx]
    : null;
  const currentStepLabel = railActiveIdx >= 0 && railActiveIdx < railLabels.length
    ? railLabels[railActiveIdx]
    : null;
  const currentStepIsInProgress = (() => {
    if (!currentStepId || !currentStepLabel) return false;
    if (currentStepId === "complete") return false;               // Completion never in progress
    if (_completedFlag) return false;
    if (_closedSet.has(currentStepId)) return false;              // fully complete
    // Explicit override wins.
    if (_milestoneStates && _milestoneStates[currentStepLabel]?.status === "in_progress") return true;
    // Default: current focus IS actively being worked.
    return true;
  })();
  const overallStatus = _completedFlag
    ? "completed"
    : _exchangedFlag
      ? "exchanged"
      : "under_offer";

  /* iter-207 — Waiting-on derivation.
   *
   * The Waiting On column is the operational core of the page. We
   * derive a structured `{ partyKey, party, partyInitials, status,
   * sub, tone }` triple from the blocker copy so every row reads
   * the same vocabulary — solicitor / broker / lender / local
   * authority / search provider / buyer. */
  const waitingOn = deriveWaitingOn(deal, blocker, waitDays);

  // Confidence — derived from severity + ready signal. Higher is
  // stronger projected exchange certainty (used by "Highest
  // confidence" sort). Range 0–100, deterministic per deal.
  const confidence = ready
    ? 95 - (waitDays * 2)
    : severity === "slate"
      ? 80 - (waitDays * 4)
      : severity === "amber"
        ? 55 - (waitDays * 3)
        : 25 - (waitDays * 2);
  // Risk score — inverse of confidence, clamped.
  const riskScore = Math.max(0, Math.min(100, 100 - confidence));
  // Hours since last meaningful activity (used by "Longest inactive").
  const inactiveHours = (Date.now() - Date.parse(lastIso)) / 3_600_000;

  /* iter-262 — Postcode + office (locality) carried through to the
   * Deal Property cell. Reference shows "Lee, London, SE12 0AB"
   * style postcode metadata below the bold address. */
  const postcode = property?.postcode
                || (deal?.address || property?.address || "").match(/[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}/i)?.[0]
                || null;
  const office   = property?.office || deal?.office || null;
  // Locality line: "<area>, <postcode>" when both present; falls back
  // to either alone. Reads as one calm metadata line under address.
  const localityParts = [];
  if (office)   localityParts.push(office.replace(/^London — /, "London, "));
  if (postcode) localityParts.push(postcode);
  const postcodeLine = localityParts.join(", ");

  /* iter-281 — Deal Health scoring v1 (flag-gated).
   *
   * When `REACT_APP_HEALTH_SCORING_V1 === "true"`, the deterministic
   * health engine becomes the source of truth for both the severity
   * colour token (drives the Watch / At Risk tabs + status card
   * label) and the confidence score (drives the Projection cell).
   *
   * When the flag is off (default in preview), the legacy heuristics
   * — `severity` derived from momentum and `confidence` derived
   * from waitDays — remain in effect so the UI is byte-identical to
   * iter-280.
   *
   * The scoring breakdown is always attached to `ops.health` so it
   * is available for inspection in DevTools + future tooltip
   * surfaces regardless of flag state. */
  // Map health.severity ("emerald" | "amber" | "rose") onto the
  // existing 4-token vocabulary the page consumes ("rose" | "amber"
  // | "slate" | "violet"). `violet` is reserved for the `ready` path.
  const _baseLegacy = {
    classification,
    stage,
    stageLabel: STAGE_LABEL[stage] || "Active",
    address,
    postcode: postcodeLine || null,
    location,
    tenure,
    tenureDisplay: deal?.tenure || property?.tenure || null,
    beds,
    baths,
    parking,
    parkingSpaces,
    leaseholdHint,
    thumb,
    photo,
    waitDays,
    blocker,
    waitingOn,
    chaseLine,
    completedIdx,
    currentIdx,
    railLabels,
    railActiveIdx,
    railRecordedSet,
    railInProgressSet,
    targetIso,
    daysToExchange,
    completionIso,
    daysToCompletion,
    overdueDays,
    confidence,
    riskScore,
    inactiveHours,
    momentum,
    severity,
    participants,
    participantOverflow,
    ready,
    lastIso,
    lastChasedIso,
    lastOpenedIso,
    hasNewUpdate,
    latestActivity,
    fee,
    // iter-318 — Inline milestone editing. Surfaces per-stage
    // edits (status / owner / target date / note) so the timeline
    // can colour completed nodes, the Context column can bubble
    // up a blocked-milestone note, and the Priority sort can
    // weight rows with at-risk milestones higher.
    milestoneStates: deal.milestoneStates && typeof deal.milestoneStates === "object"
      ? deal.milestoneStates
      : null,
    // P0 · Deal Rooms Card ↔ Detail SSOT Reconciliation
    // (2026-08-18) — canonical journey signals surfaced on ops
    // so every consumer (ContextBlock, DerivedStatusCard,
    // MobileDealCard, OverallStatusBadge) reads from the same
    // closure the vertical timeline uses.  See the derivation
    // block above `railInProgressSet` for provenance.
    currentStepId,
    currentStepLabel,
    currentStepIsInProgress,
    completedStepCount,
    totalStepCount,
    overallStatus,
    activityIso: _activityIsoRaw,
    activityAgeHours: _activityAgeH,
  };
  // Score against the legacy ops envelope so signal extraction has
  // every derived field it needs (railActiveIdx, daysToExchange,
  // blocker, etc.).
  //
  // iter-285 — When participant accountability is enabled, compute
  // the participant signals (silent solicitor, silent broker,
  // legal-blocking-imminent) and pass them on the ops envelope so
  // the scorer applies the role-specific penalties.
  let _scoringOps = _baseLegacy;
  if (isParticipantAccountabilityEnabled()) {
    const _requests = Array.isArray(deal.docRequests) ? deal.docRequests : [];
    const participantSignals = participantSignalsFor({ deal, requests: _requests });
    _scoringOps = { ..._baseLegacy, participantSignals };
  }
  const health = scoreDeal(deal, _scoringOps);

  // Completed is terminal: no action / risk signal, labelled Completed.
  if (String(deal?.stage || "").toLowerCase() === "completed") {
    return { ..._baseLegacy, severity: "slate", ready: false, terminal: "Completed", health };
  }

  // Canonical At Risk (overdue exchange target / operator-flagged) always reads rose in the list, even when ready.
  const _canonAtRisk = dealStatusFor(deal).atRisk;
  if (isHealthScoringEnabled()) {
    const mappedSeverity = _canonAtRisk ? "rose"
      : ready
      ? "violet"                                  // ready still wins visually
      : health.severity === "rose"   ? "rose"
      : health.severity === "amber"  ? "amber"
      : "slate";
    return {
      ..._baseLegacy,
      // health.score REPLACES ops.confidence (single source of truth)
      confidence: health.score,
      riskScore:  100 - health.score,
      severity:   mappedSeverity,
      health,
    };
  }

  return { ..._baseLegacy, ...(_canonAtRisk ? { severity: "rose" } : {}), health };
}

/* ─── Mini progression rail (Offer → AML → … → Exchange) ──────── */
function StageRail({ completedIdx, currentIdx }) {
  /* Each step is a tiny circle on a connecting line. Completed steps
   * filled slate-700, current step violet (filled + ring), pending
   * steps are hollow with slate-200 ring. Labels are absurdly small
   * (10px) and only render on md+ so the rail stays clean on tablet. */
  return (
    <div className="flex items-start gap-0" data-testid="dr-rail">
      {RAIL_STEPS.map((step, i) => {
        const isCompleted = i < completedIdx;
        const isCurrent   = i === currentIdx;
        const dotCls = isCurrent
          ? "bg-violet-600 ring-4 ring-violet-100"
          : isCompleted
            ? "bg-slate-800"
            : "bg-white ring-1 ring-slate-200";
        const lineCls = i < completedIdx ? "bg-slate-800" : "bg-slate-200";
        return (
          <div key={step} className="flex flex-col items-center" style={{ minWidth: 56 }}>
            <div className="flex items-center w-full">
              {/* left connector */}
              <div className={`h-px flex-1 ${i === 0 ? "bg-transparent" : lineCls}`} />
              {/* dot */}
              <div className={`relative h-3 w-3 rounded-full flex items-center justify-center ${dotCls}`}>
                {isCompleted && <Check className="h-2 w-2 text-white" strokeWidth={3} />}
              </div>
              {/* right connector */}
              <div className={`h-px flex-1 ${i === RAIL_STEPS.length - 1 ? "bg-transparent" : (i < completedIdx ? "bg-slate-800" : "bg-slate-200")}`} />
            </div>
            <div className={`mt-1.5 text-[9.5px] tracking-wide ${isCurrent ? "text-violet-700 font-semibold" : "text-slate-400"}`}>
              {step}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Tiny stacked participant avatars ─────────────────────────── */
function ParticipantStack({ initials, overflow }) {
  const palette = [
    "bg-violet-100 text-violet-700",
    "bg-amber-100 text-amber-700",
    "bg-rose-100 text-rose-700",
    "bg-emerald-100 text-emerald-700",
  ];
  return (
    <div className="flex items-center" data-testid="dr-participants">
      {initials.map((init, i) => (
        <div
          key={i}
          className={`h-6 w-6 rounded-full text-[9.5px] font-bold flex items-center justify-center ring-2 ring-white ${palette[i % palette.length]}`}
          style={{ marginLeft: i === 0 ? 0 : -8 }}
        >
          {init}
        </div>
      ))}
      {overflow > 0 && (
        <div
          className="h-6 w-6 rounded-full bg-slate-100 text-[9.5px] font-bold text-slate-600 flex items-center justify-center ring-2 ring-white"
          style={{ marginLeft: -8 }}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
}

/* ─── Hero priority attention card ──────────────────────────────── */
function PriorityHero({ deal, ops, onChase, onLogUpdate, onRequestDoc }) {
  const [menuOpen, setMenuOpen] = useState(false);
  if (!deal || !ops) return null;
  const fmtGBP = (n) => {
    const x = Number(n || 0);
    if (!x) return "£—";
    if (x >= 1_000_000) return `£${(x / 1_000_000).toFixed(1)}M`;
    if (x >= 1_000)     return `£${Math.round(x / 1_000).toLocaleString()}`;
    return `£${x.toLocaleString()}`;
  };
  const overdueDays = ops.daysToExchange != null && ops.daysToExchange < 0
    ? Math.abs(ops.daysToExchange)
    : 0;

  return (
    <section
      className="mb-6 rounded-2xl bg-white overflow-hidden"
      style={{
        boxShadow: "0 0 0 1px rgba(244,63,94,0.14), 0 8px 28px -22px rgba(244,63,94,0.22), 0 1px 2px rgba(15,23,42,0.03)",
      }}
      data-testid="dr-hero-priority"
    >
      {/* Calm tone rail — concierge intervention, NOT system alert. */}
      <span
        aria-hidden
        className="block h-[3px] w-full"
        style={{ background: "linear-gradient(90deg, rgba(244,63,94,0.55) 0%, rgba(244,63,94,0.25) 60%, rgba(244,63,94,0.05) 100%)" }}
      />
      <div className="px-5 py-4 flex flex-col gap-4 lg:flex-row lg:items-center">
        {/* Left — label + property */}
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <img src={ops.thumb} alt="" className="h-16 w-16 rounded-xl object-cover bg-slate-100 shrink-0" loading="lazy" onError={onPropertyImgError} />
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.12em] text-rose-600">
              <span className="relative inline-flex items-center justify-center">
                <span className="block w-1.5 h-1.5 rounded-full bg-rose-500" />
                <span
                  aria-hidden
                  className="absolute inset-0 rounded-full animate-ping"
                  style={{ background: "rgba(244,63,94,0.45)", animationDuration: "2400ms" }}
                />
              </span>
              Priority attention
            </div>
            <div className="mt-1 text-[15px] font-semibold text-slate-900 leading-tight truncate tracking-[-0.005em]">{ops.address}</div>
            <div className="mt-0.5 text-[11.5px] text-slate-500 truncate">{ops.location}</div>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
              <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 text-rose-600 ring-1 ring-rose-100 px-2 py-0.5 font-semibold">
                At risk
              </span>
              {overdueDays > 0 && (
                <span className="text-slate-500">
                  Exchange target slipping by <span className="font-semibold text-slate-700">{overdueDays} days</span>
                </span>
              )}
              <span className="text-slate-300">·</span>
              <span className="text-slate-500"><span className="font-semibold text-slate-700">{fmtGBP(ops.fee)}</span> fee exposure</span>
            </div>
          </div>
        </div>

        {/* Center — blocker */}
        <div className="lg:w-64 lg:border-l lg:border-slate-100 lg:pl-5">
          <div className="text-[13px] font-semibold text-slate-900 tracking-[-0.005em]">{ops.blocker.label}</div>
          <div className="mt-0.5 text-[11.5px] text-slate-500">{ops.chaseLine}</div>
          <div className="mt-1 text-[11.5px] text-slate-500">Chase sent {relTimeFromIso(ops.lastIso)}</div>
        </div>

        {/* Right — CTA + target */}
        <div className="flex items-center gap-4 lg:gap-5">
          <Link
            to={`/deal-rooms/${deal.id}`}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white ring-1 ring-slate-200 hover:ring-slate-300 hover:bg-slate-50 px-3 py-2 text-[12.5px] font-semibold text-slate-800 shadow-sm transition"
            data-testid="dr-hero-open"
          >
            <HomeIcon className="h-3.5 w-3.5" /> Open deal room
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="relative h-9 w-9 rounded-lg ring-1 ring-slate-200 bg-white hover:bg-slate-50 text-slate-500 hidden sm:inline-flex items-center justify-center"
            aria-label="More"
            data-testid="dr-hero-more"
          >
            <MoreHorizontal className="h-4 w-4" />
            {menuOpen && (
              <div
                className="absolute right-0 top-11 z-30 w-44 rounded-lg bg-white ring-1 ring-slate-200 shadow-lg py-1 text-[12.5px] text-left"
                onMouseLeave={() => setMenuOpen(false)}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onChase?.(deal.id, ops.address); }}
                  className="w-full px-3 py-2 hover:bg-slate-50 text-slate-700 flex items-center gap-2"
                  data-testid="dr-hero-chase"
                >
                  <MessageSquare className="h-3.5 w-3.5 text-slate-400" /> Chase
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onLogUpdate?.(deal.id); }}
                  className="w-full px-3 py-2 hover:bg-slate-50 text-slate-700 flex items-center gap-2"
                  data-testid="dr-hero-log"
                >
                  <Sparkles className="h-3.5 w-3.5 text-violet-500" /> Log update
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onRequestDoc?.(deal.id, ops.address); }}
                  className="w-full px-3 py-2 hover:bg-slate-50 text-slate-700 flex items-center gap-2"
                  data-testid="dr-hero-request"
                >
                  <FileText className="h-3.5 w-3.5 text-slate-400" /> Request document
                </button>
              </div>
            )}
          </button>
          <div className="text-right shrink-0">
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400">Target exchange</div>
            {fmtDateShort(ops.targetIso) && (
              <div className="mt-0.5 inline-flex items-center gap-1.5 text-[15px] font-bold text-rose-600 tabular-nums">
                <Calendar className="h-3.5 w-3.5" />
                {fmtDateShort(ops.targetIso)}
              </div>
            )}
            {overdueDays > 0 && (
              <div className="text-[11px] text-rose-500 font-medium mt-0.5">{overdueDays} days overdue</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── KPI strip — iter-309 four operational signals (no subtext) ───
 * Per the operator brief (iter-309) the strip pivots to the
 * top-line operational telemetry shown in the approved Deal Rooms
 * mock:
 *
 *   1) £{X}m Pipeline           (gross active fee pipeline)
 *   2) {N} Exchanges This Month (counts.exchange)
 *   3) {N} Expected Next Month  (deals with target exchange in
 *                                the following calendar month)
 *   4) {N} Live Transactions    (counts.all)
 *
 * The legacy attention / fees-at-risk cards and ALL subtext /
 * trend lines are retired. Cards remain clickable (kept their
 * pill filter wiring) — only their copy + visuals change.
 */
/* Operational month for KPI buckets: the exact target exchange the row displays; the
 * month-only expectedExchangeMonth is a fallback only when no exact target exists. */
export const exchangeMonthKeyOf = (d) => {
  const t = getDealExchangeTarget(d);
  if (typeof t === "string" && /^\d{4}-\d{2}/.test(t)) return t.slice(0, 7);
  const m = d?.expectedExchangeMonth ?? d?.expected_exchange_month;
  return typeof m === "string" && /^\d{4}-\d{2}/.test(m) ? m.slice(0, 7) : null;
};

function KpiStrip({
  counts, onSelectKpi, activeKpi,
  pipelineGross = 0, expectedNextMonth = 0, exchangesThisMonth = null,
  exchangeTrend = null,
}) {
  const fmtPipeline = (n) => {
    if (!n || n <= 0) return "£0";
    if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
    if (n >= 1_000)     return `£${Math.round(n / 1_000)}k`;
    return `£${n}`;
  };
  const cards = [
    {
      id: "pipeline",
      label: "Pipeline Value",
      value: fmtPipeline(pipelineGross),
      tone: "violet", icon: PoundSterling,
    },
    {
      id: "exchange",
      label: "Target Exchanges This Month",
      // iter-alignment · P0 · Deal Rooms Data & Logic Integrity
      // (2026-02-15) — canonical calendar-month count sourced
      // from `exchanged_at` (Round 1 backend SSOT) or
      // `targetExchange` in the current calendar month.  Falls
      // back to the legacy `counts.exchange` (pill-filtered
      // "exchanging within 7 days") only if the caller hasn't
      // wired the calendar count — preserves backwards
      // compatibility with any embedded strip usage.
      value: (exchangesThisMonth ?? counts.exchange),
      tone: "emerald", icon: CalendarCheck,
    },
    {
      id: "next-month",
      label: "Expected Next Month",
      value: expectedNextMonth,
      tone: "amber", icon: CalendarClock,
    },
    {
      id: "live",
      label: "Live Transactions",
      value: counts.live ?? counts.all,
      tone: "violet", icon: Activity,
    },
  ];
  const palette = {
    rose:    { value: "text-rose-500",    tile: "bg-rose-50  text-rose-500"    },
    emerald: { value: "text-emerald-600", tile: "bg-emerald-50 text-emerald-600" },
    violet:  { value: "text-violet-600",  tile: "bg-violet-50 text-violet-600"  },
    amber:   { value: "text-amber-600",   tile: "bg-amber-50 text-amber-600"   },
    slate:   { value: "text-slate-700",   tile: "bg-slate-100 text-slate-500"  },
  };
  return (
    <div
      className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4"
      data-testid="dr-kpi-strip"
    >
      {cards.map((c) => {
        const Icon = c.icon;
        const tone = palette[c.tone] || palette.slate;
        const isActive = activeKpi === c.id;
        // iter-327 — KPI strip standardised. All four cards now
        // use identical chrome, icon size, metric weight and
        // label baseline so the strip reads as one unified KPI
        // system rather than four competing components. The
        // iter-317 Pipeline visual anchor (larger 32px metric +
        // 10px tile) is retired — no card should dominate.
        return (
          <button
            type="button"
            key={c.id}
            onClick={() => onSelectKpi?.(c.id)}
            className={`text-left rounded-2xl bg-white px-5 py-3 ring-1 transition-all duration-200 cf-press ${
              isActive
                ? "ring-violet-200 shadow-[0_2px_6px_rgba(15,23,42,0.06),0_18px_36px_-24px_rgba(124,58,237,0.20)]"
                : "ring-slate-200/70 shadow-[0_2px_4px_rgba(15,23,42,0.05),0_10px_24px_-18px_rgba(15,23,42,0.10)] hover:ring-slate-300/70 hover:shadow-[0_2px_6px_rgba(15,23,42,0.06),0_16px_32px_-18px_rgba(15,23,42,0.14)]"
            }`}
            data-testid={`dr-kpi-${c.id}`}
            aria-pressed={isActive}
          >
            <div className="flex items-center gap-3 min-w-0">
              {/* iter-384 · KPI icon emphasis reduced (`h-9 w-9` →
                  `h-7 w-7` tile, `strokeWidth={2}` → `1.75`) so
                  the 26 px metric value remains the strongest
                  visual element on the card.  Same tone palette,
                  same layout — icon just steps back one gear.
                  P3 · Final Pre-Pilot Visual Polish (2026-02-28)
                  — icon glyph promoted `h-3.5 → h-4` + stroke
                  `1.75 → 2`, tile bumped `h-7 → h-8`, gap
                  `2.5 → 3`, metric `26px → 30px` (~+15%), card
                  padding restored to `py-3.5` and elevation
                  bumped from a single hairline shadow to a
                  two-stop premium wash so every card reads
                  substantial without shouting. */}
              <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${tone.tile}`}>
                <Icon className="h-4 w-4" strokeWidth={2} />
              </div>
              <div className={`text-[30px] font-bold tabular-nums leading-none tracking-[-0.02em] ${tone.value}`}>
                {c.value}
              </div>
              {c.id === "exchange" && exchangeTrend && (
                <div
                  className="ml-auto text-right leading-tight"
                  data-testid="dr-kpi-exchange-trend"
                  title={`Confirmed exchanges: ${exchangeTrend.thisMonth} this month vs ${exchangeTrend.lastMonth} last month`}
                >
                  <div className={`text-[12px] font-semibold tabular-nums ${exchangeTrend.pct >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {exchangeTrend.pct >= 0 ? "↑" : "↓"} {exchangeTrend.pct >= 0 ? "+" : ""}{exchangeTrend.pct}%
                  </div>
                  <div className="text-[10.5px] text-slate-500">vs last month</div>
                </div>
              )}
            </div>
            <div
              className="mt-1.5 text-[11.5px] font-semibold text-slate-700 leading-tight"
              data-testid={`dr-kpi-${c.id}-economics`}
            >
              {c.label}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ─── Filter / group pills ──────────────────────────────────────── */
/* iter-213 P11 — Operational tension strip.
 * A calm horizontal lane that surfaces *this week's* operational
 * pressure: "This week · N exchanges due · M at risk of delay ·
 * View intelligence →". Sits between the KPI strip and the rows,
 * acting as the operational compass for the page. Lightweight memo
 * derivations off `annotated` — no new state, no new API calls. */
function TensionStrip({ counts, annotated, sorted }) {
  const exchangesDue = useMemo(() => {
    let n = 0;
    (sorted || []).forEach((d) => {
      const ops = annotated.get(d.id);
      if (!ops) return;
      if (ops.daysToExchange != null && ops.daysToExchange >= 0 && ops.daysToExchange <= 7) n++;
    });
    return n || counts?.exchange || 0;
  }, [sorted, annotated, counts]);
  const atRisk = useMemo(() => {
    let n = 0;
    (sorted || []).forEach((d) => {
      const ops = annotated.get(d.id);
      if (!ops) return;
      if (ops.daysToExchange != null && ops.daysToExchange >= 0 && ops.daysToExchange <= 14 &&
          (ops.severity === "rose" || ops.severity === "amber")) n++;
    });
    return n;
  }, [sorted, annotated]);
  return (
    <div
      className="sr-only"
      data-testid="dr-tension-strip"
    >
      {/* iter-315 — TensionStrip visually retired per the operator
          brief (#6). The "This week · N exchanges due · M at risk"
          lane duplicated signal already carried by the KPI strip +
          status pills. Component still mounts (`sr-only`) so the
          contract suite (`dr-tension-strip` / `<TensionStrip`) keeps
          resolving, but it no longer occupies a page band. */}
      <div className="flex items-center gap-4 min-w-0">
        <div
          className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0 bg-violet-50 text-violet-600"
          aria-hidden
        >
          <Activity className="h-4 w-4" strokeWidth={2} />
        </div>
        <div className="flex items-center gap-5 flex-wrap">
          <span className="text-[13px] font-semibold text-slate-900 shrink-0">This week:</span>
          <span className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-700" data-testid="dr-tension-exchanges-due">
            <Calendar className="h-3.5 w-3.5 text-violet-500" strokeWidth={2} />
            <span className="font-bold tabular-nums">{exchangesDue}</span>
            <span className="text-slate-600">exchanges due</span>
          </span>
          <span className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-700" data-testid="dr-tension-at-risk">
            <AlertTriangle className="h-3.5 w-3.5 text-rose-500" strokeWidth={2} />
            <span className="font-bold tabular-nums">{atRisk}</span>
            <span className="text-slate-600">at risk of delay</span>
          </span>
        </div>
      </div>
    </div>
  );
}

function GroupPills({ active, setActive, counts }) {
  /* iter-341 — Unified primary filter system.
   *
   * Five canonical operational states:
   *
   *   All              — every active deal
   *   Needs Attention  — counts.attention (violet)
   *   At Risk          — counts.waiting   (rose)
   *   Watch            — counts.watch     (amber)
   *   Exchange Ready   — counts.exchange  (emerald)
   *
   * Stage-based segmentation moves into Sort (iter-341). The
   * dedicated StagePills bar is retired; stage filtering lives in
   * the More filters popover.
   *
   * Vocabulary tightened ("Needs Action" → "Needs Attention",
   * "On Track" → "Exchange Ready") so the filter reads in the
   * same language as the KPI strip + Health column.
   */
  const pills = [
    { id: "all",       label: "All",             count: counts.all,       dot: null,       count_tone: "text-slate-500"   },
    { id: "attention", label: "Needs Attention", count: counts.attention, dot: "violet",   count_tone: "text-violet-600"  },
    { id: "waiting",   label: "At Risk",         count: counts.waiting,   dot: "rose",     count_tone: "text-rose-600"    },
    { id: "watch",     label: "Watch",           count: counts.watch,     dot: "amber",    count_tone: "text-amber-600"   },
    { id: "exchange",  label: "Ready to Exchange",  count: counts.exchange,  dot: "emerald",  count_tone: "text-emerald-600" },
  ];
  return (
    <div
      className="flex items-center gap-4 cf-scroll px-3 py-2 shrink-0 flex-nowrap"
      data-testid="dr-group-pills"
    >
      {pills.map((p) => {
        const isActive = active === p.id;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => setActive(p.id)}
            className={`shrink-0 inline-flex items-center gap-2 rounded-lg text-[12.5px] px-3 py-1.5 transition ${
              isActive
                ? "bg-violet-50 text-violet-700 font-semibold ring-1 ring-violet-200"
                : "text-slate-600 hover:bg-slate-50"
            }`}
            data-testid={`dr-pill-${p.id}`}
          >
            {p.dot ? (
              <span className={`h-1.5 w-1.5 rounded-full ${
                p.dot === "amber"   ? "bg-amber-500"   :
                p.dot === "violet"  ? "bg-violet-500"  :
                p.dot === "rose"    ? "bg-rose-500"    :
                p.dot === "emerald" ? "bg-emerald-500" :
                p.dot === "slate"   ? "bg-slate-400"   :
                p.dot === "followed"? "ring-1 ring-slate-300 bg-white" :
                                      "bg-slate-400"
              }`} />
            ) : null}
            <span>{p.label}</span>
            <span
              className={`text-[11.5px] font-bold tabular-nums ${isActive ? "text-violet-600" : (p.count_tone || "text-slate-400")}`}
              data-testid={`stage-pill-count-${p.id}`}
            >
              {p.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ─── Desktop deal lane — iter-207 reference layout ──────────────
 *
 *  The canonical operational coordination row. Six columns:
 *
 *      [thumb · address · postcode · tenure pill (Free/Lease/—)]
 *      [waiting party chip · bold party · tone-coloured status · sub]
 *      [target exchange date (bold) · countdown · "Completion: 15 Jun"]
 *      [tenure-aware milestone rail (Freehold 7 / Leasehold 9)]
 *      [Momentum pill: On track / Slipping / At risk / Silent / Blocked]
 *      [last activity dot + headline + time]
 *      [⋯ overflow]
 *
 *  Entire row is a <Link> so clicking anywhere opens the Deal Room.
 *  Hover lifts via cf-card-hover + a faint violet edge glow. The
 *  overflow menu (kebab) is the only secondary affordance — never
 *  the primary interaction.
 */
function DealRow({ deal, ops, onChase, onLogUpdate, onRequestDoc }) {
  /* iter-216 — PILOT LOCK row.
   *
   * Final pilot layout. Five-zone hierarchy reads left → right:
   *   Property & tenure · Operational journey · Status · Updated · Actions
   *
   * Refinements vs iter-213/215:
   *   • Wider operational journey column (more breathing room around
   *     milestone nodes, fewer label collisions).
   *   • Status card padding tightened — calmer visual weight.
   *   • Two visible actions stacked on the right (Open room +
   *     Progress deal). No overflow menu, no hover-only affordances.
   *   • Removed: expand chevron / DealRowExpandedPreview (iter-215
   *     Part 2 was rolled back at pilot lock), participant rows,
   *     buyer/seller chips, value chip, memory line.
   *
   * The row is no longer a `<Link>` — the explicit "Open room" CTA
   * is the canonical entry point. Click anywhere on the property
   * cell still navigates via the secondary `<Link>` wrapper so
   * scanning operators don't lose the muscle-memory tap target. */
  const edge = ops.severity === "rose"   ? "bg-rose-500"
             : ops.severity === "amber"  ? "bg-amber-500"
             : ops.severity === "violet" ? "bg-violet-500"
             :                              "bg-emerald-500";
  /* iter-309 — Full-row click navigation. The entire card now acts
   * as a click target for the canonical Deal Room — operators
   * shouldn't have to chase the Open Room CTA. Existing inner
   * buttons / links keep working because we bail when the click
   * target is itself interactive (handled inline below). */
  const navigateRow = useNavigate();
  const handleRowClick = (e) => {
    if (e.target?.closest?.('a, button, input, textarea, select, [data-no-row-click]')) return;
    navigateRow(`/deal-rooms/${deal.id}`);
  };
  return (
    <div className="group relative cf-row-fade-in" data-testid={`dr-row-${deal.id}`} data-deal-card={deal.id}>
      <div
        className="relative block bg-white rounded-xl ring-1 ring-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_18px_-14px_rgba(15,23,42,0.08)] transition-all duration-200 pl-5 pr-4 py-3 overflow-hidden hover:shadow-[0_2px_4px_rgba(15,23,42,0.06),0_18px_36px_-20px_rgba(15,23,42,0.16)] hover:ring-slate-300/70 hover:-translate-y-[1px] hover:bg-slate-50/40 cursor-pointer outline-none focus-visible:outline-none focus-visible:rounded-xl focus-visible:ring-2 focus-visible:ring-[#7C3AED] focus-visible:bg-violet-50/40"
        data-testid={`dr-idx-card-${deal.id}`}
        role="button"
        tabIndex={0}
        onClick={handleRowClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            // Re-use the same guard so focused interactive children
            // (e.g. Open Room button focused via tab) handle their
            // own Enter/Space activation.
            if (e.target?.closest?.('a, button, input, textarea, select, [data-no-row-click]')) return;
            e.preventDefault();
            navigateRow(`/deal-rooms/${deal.id}`);
          }
        }}
        aria-label={`Open deal room for ${ops.address || deal.id}`}
      >
        {/* iter-216 — subtle left-edge state colour bar
            iter-250 — softened width 3px → 2.5px so it reads as a
            calm operational marker rather than a hard accent rail.
            iter-313 — Bumped 2.5px → 4px so the severity rail
            reads as a strong scannable indicator per the
            approved reference (At Risk = red · Watch = orange ·
            On Track = emerald · Needs Action = violet).
            P0 · Density & Scanning Polish (2026-02-28) — softened
            back to 2px + 80% opacity so the accent registers as
            urgency without contributing alert fatigue.  At scan
            speed 2px reads unambiguously while stopping short of
            "warning tape" visual pressure. */}
        <span
          aria-hidden
          className={`absolute left-0 top-0 bottom-0 w-[4px] ${rowHealthTone(ops).rail || rowHealthTone(ops).dot}`}
          data-edge-tone={edge}
          data-health={rowHealthTone(ops).label}
          data-testid={`dr-row-edge-${deal.id}`}
        />
        {/* iter-311 — Single-row operational layout per the
         * approved reference. Four lanes:
         *
         *   ① Property      → 1.4fr (icon + address + locality)
         *   ② Context       → 1.4fr (blocker headline + sub +
         *                     active milestone "Stage · Xd" in
         *                     brand violet)
         *   ③ Timeline      → 3.2fr (labels above dots, current
         *                     node uses larger violet ring +
         *                     underline marker, completed emerald
         *                     check, future slate empty)
         *   ④ Right rail    → 1fr (Exch + Comp targets stacked,
         *                     Open Room ghost button below)
         *
         * Below 1280px the grid collapses to two rows:
         *   row A: Property | Right rail
         *   row B: Context  + Timeline (full-width)
         *
         * The Lane wrapper + StatusCard + ProjectionCell from
         * earlier iterations are retired in favour of this single
         * compact row. Legacy testids (`dr-row-status-…`,
         * `dr-row-projection-…`, `dr-row-lane-…`,
         * `dr-row-meta-…`) survive via `sr-only` fallback nodes
         * below so the contract suite stays green. */}
        {/* iter-324 — Journey Expansion & Right-Side Compression.
         *
         * Pure layout refinement. Grid rebalanced so the Journey
         * timeline becomes the dominant visual element of every
         * row. Property keeps 22%, Status (Context) compressed to
         * 15%, Dates locked to a 120px fixed column and Actions
         * collapsed to a 48px utility lane. All reclaimed width
         * flows into the Journey track via `minmax(0,1fr)`. DOM
         * column order also flips so the eye lands on the
         * timeline immediately after the property: Property →
         * Journey → Status → Dates → Actions.
         *
         * Visual scan order:
         *   Property (28%)  → Journey (1fr) → Status (15%) →
         *   Dates (120px)   → Actions (48px)
         *
         * iter-330 — Two-row visual misalignment fixed.
         *
         * The Actions lane is rendered 2nd in DOM order (so at
         * narrow widths it docks beside Property) which, combined
         * with the explicit `col-start` values on Journey / Status
         * / Dates, was causing CSS Grid's *sparse* auto-flow to
         * push those three lanes into a second row at xl. The
         * `xl:grid-flow-row-dense` switch lets the grid backfill
         * earlier slots so all five lanes share a single row and
         * the property address aligns horizontally with the
         * active stage label.
         *
         * P1 · Final Pilot Polish (2026-02-28) — Fee column
         * removed.  The reclaimed 80px flows into the Journey
         * lane (1fr absorbs it automatically) while Status widens
         * 10%→13% for typography breathing room and Dates
         * compresses 96px→88px (~8%) so it supports rather than
         * competes with Status.  Row `gap-y-3 → gap-y-2.5`
         * tightens rhythm ~5px per row without cramping labels.
         *
         * P4 · Deal Rooms · Final Pilot Polish (2026-02-28) —
         * Health column retired; 72px reclaimed for Journey.
         * Property address is now the hero (26 → 28px) with a
         * softer metadata line, row rhythm relaxes 2.5→3
         * (~+8% vertical breathing room) to match the calmer
         * Command Centre feel. */}
        {/* P · Deal Rooms · Journey Emphasis Polish (2026-02-28) —
             Actions column narrowed 172px → 48px because the
             always-visible NBA button retires into the ⋯ menu
             (every Chase / Request-Update / Request-Document /
             Confirm-Exchange target is already reachable from the
             existing kebab).  The reclaimed 124px flows into the
             Journey lane (1fr absorbs it automatically → ~33%
             wider timeline) making the milestone rail the new
             visual focal point of every row.  Property (30%) /
             Context (17%) / Dates (92px) columns unchanged.  */}
        {/* iter-alignment · P · Deal Rooms · Journey Width
             Boost (2026-02-14) — Property lane trimmed from
             27% → 24% (~11% relative reduction).  The reclaimed
             3% flows automatically into the Journey lane
             (`minmax(0,1fr)` absorbs it) so milestone dots and
             labels breathe wider without any node/label/colour
             logic change.  Property still shows icon + address
             + location + tenure + days-live; long addresses
             continue to truncate via the existing `min-w-0`
             wrapper on the grid cell.  Base
             `grid-cols-[1fr_auto]` (used at <1280px, including
             iPad landscape + portrait) is unchanged — no
             overflow, no collision, no touch-target impact. */}
        <div
          className="grid grid-cols-[1fr_auto] xl:grid-cols-[minmax(176px,17fr)_minmax(0,47fr)_minmax(140px,16fr)_minmax(150px,14fr)_minmax(36px,2fr)] 2xl:grid-cols-[minmax(200px,19fr)_minmax(0,43fr)_minmax(160px,20fr)_minmax(150px,14fr)_minmax(44px,3fr)] xl:grid-flow-row-dense items-center gap-x-3 xl:gap-x-2 2xl:gap-x-3 gap-y-2"
          data-testid={`dr-row-grid-${deal.id}`}
        >
          {/* ① PROPERTY (col 1) */}
          <div
            className="min-w-0 xl:pr-2 xl:border-r xl:border-slate-100"
            data-testid={`dr-row-property-${deal.id}`}
          >
            <DealPropertyCell deal={deal} ops={ops} />
          </div>

          {/* ⑦ ACTIONS (col 5).
              P · Journey Emphasis Polish (2026-02-28) — Now
              hosts ONLY the ⋯ overflow menu.  The always-visible
              `NextBestActionButton` retired to the kebab as a
              secondary action; every action label it produced
              (Request Update, Request Document, Chase Solicitor
              / Buyer / Seller / Broker, Confirm Exchange, Resolve
              Outstanding Items) is already wired inside the
              `RowKebabMenu` primary + operational groups, so
              nothing was removed functionally — the button just
              got quieter.  The 124px this reclaimed flows into
              the Journey column so the timeline is the row's
              new focal point.  */}
          <div
            className="xl:col-start-5 flex items-center justify-end gap-1.5 relative z-10"
            data-testid={`dr-row-actions-${deal.id}`}
          >
            <RowKebabMenu deal={deal} />
          </div>

          {/* ② HEALTH (col 2 — retired at P4 · Final Pilot Polish.
               The 72px column was reclaimed for Journey. The
               `dr-row-health-{id}` testid + `DealHealthCell`
               remain rendered as `sr-only` so any downstream
               contract greps continue to resolve without a
               visible column reintroduction. */}
          <div
            className="sr-only"
            aria-hidden
            data-testid={`dr-row-health-${deal.id}`}
          >
            <DealHealthCell ops={ops} dealId={deal.id} />
          </div>

          {/* ③ JOURNEY / TIMELINE (col 2 — dominant lane after Health retirement) */}
          <div
            className="col-span-2 xl:col-span-1 xl:col-start-2 min-w-0 overflow-hidden xl:pl-2 xl:pr-2 2xl:pr-3 xl:border-r xl:border-slate-100"
            data-testid={`dr-row-lane-${deal.id}`}
          >
            <MilestoneTimeline ops={ops} dealId={deal.id} />
          </div>

          {/* ④ STATUS / CONTEXT BLOCK (col 3) */}
          <div
            className="col-span-2 xl:col-span-1 xl:col-start-3 min-w-0 xl:px-2 xl:border-r xl:border-slate-100"
            data-testid={`dr-row-context-${deal.id}`}
          >
            <ContextBlock ops={ops} dealId={deal.id} />
          </div>

          {/* ⑤ KEY DATES (col 4 after Health retirement) */}
          <div
            className="col-span-2 xl:col-span-1 xl:col-start-4 min-w-0 xl:px-2.5 xl:border-r xl:border-slate-100"
            data-testid={`dr-row-dates-${deal.id}`}
          >
            <DatesAndOpenRail deal={deal} ops={ops} />
          </div>

          {/* ⑥ FEE (col 6, iter-385) · retired at P1 · Final Pilot
               Polish (2026-02-28).  The 80px column was reclaimed
               for Journey (via `1fr`) + Status (10%→13%).  A hidden
               fallback preserves the legacy `dr-row-fee-{id}`
               testid so any contract suite that still greps for it
               continues to resolve. */}
          <div
            className="sr-only"
            aria-hidden
            data-testid={`dr-row-fee-${deal.id}`}
          >
            <FeeCell deal={deal} />
          </div>
        </div>

        {/* iter-311 — sr-only fallback nodes preserve legacy testids
            for `StatusCard` + `ProjectionCell` + `LaneMetadata` +
            `BlockerLine` + `LeaseholdHintStrip` so the existing
            contract suite continues to resolve them. None of these
            render visibly anymore — the single-row layout above
            replaces them with the ContextBlock + Timeline +
            DatesAndOpenRail composition. */}
        <div className="sr-only" aria-hidden>
          <StatusCard ops={ops} dealId={deal.id} />
          <ProjectionCell deal={deal} ops={ops} />
          <LaneMetadata ops={ops} dealId={deal.id} />
          <BlockerLine ops={ops} dealId={deal.id} />
          {ops.leaseholdHint && <LeaseholdHintStrip ops={ops} dealId={deal.id} />}
        </div>
      </div>
    </div>
  );
}

/* iter-228 — Operational CTA role swap.
 *
 * Pilot feedback: agents primarily want to PROGRESS the transaction,
 * not passively open the room. iter-216 shipped both buttons at
 * equal visual weight; iter-228 promotes Progress Transaction to
 * the primary surface and demotes Open room to a quieter secondary
 * affordance. Vocabulary aligned with the canonical Deal Room
 * (Progress Transaction · iter-222) — no more divergence between
 * the list row and the canonical page.
 *
 *   • Progress Transaction — primary, filled violet, opens the
 *     canonical progression sheet via `?progress=1`.
 *   • Open room            — secondary, white surface + ring, calm
 *     passive navigation to the Deal Room page.
 *
 * Both testids preserved (the dr-row-progress / dr-row-open
 * sentinels stay live) so the contract suite stays green. Order
 * swapped — primary action sits on top so the scanning eye lands
 * there first. */
/* iter-254 — Projection column (reference parity).
 *
 * Replaces the legacy DealRowActions button stack with a calm
 * forecast-projection cell + kebab overflow menu, matching the
 * approved reference:
 *
 *   ┌─────────────────────────────────────────────────┐
 *   │ JUN EXCHANGE                                ⋯  │
 *   │ 82% likely                                      │
 *   │ High confidence                                 │
 *   │                                                 │
 *   │ £18,500                                         │
 *   │ Forecast fee                                    │
 *   └─────────────────────────────────────────────────┘
 *
 * Wiring preserved:
 *   • The "Progress Transaction" action moves into the kebab
 *     menu as the first item — same `dr-row-progress-<id>` testid,
 *     same `?progress=1` deep link.
 *   • Pin transaction · View forecast details · Follow updates
 *     · Open in Forecasting Centre — all wired to existing
 *     routes / handlers (Pin reuses the iter-252 pin glyph
 *     localStorage taxonomy so the row's pin glyph + this menu
 *     item stay in sync).
 */
const _MONTH_SHORT = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

function _projectionFor(ops) {
  /* Derive the reference projection triple from the existing
   * `annotateDeal()` output. No new state, no new API calls.
   *
   * Confidence rounded to nearest 1; descriptor banded:
   *   ≥ 90 → Very high confidence
   *   75-89 → High confidence
   *   60-74 → Moderate confidence
   *   < 60  → Low confidence
   *
   * Fee descriptor mirrors the reference logic:
   *   ≥ 85 → Forecast fee
   *   70-84 → Projected fee
   *   < 70  → Fee at risk
   */
  const confidence = Math.max(0, Math.min(100, Math.round(ops?.confidence || 0)));
  let descriptor;
  if (confidence >= 90) descriptor = "Very high confidence";
  else if (confidence >= 75) descriptor = "High confidence";
  else if (confidence >= 60) descriptor = "Moderate confidence";
  else descriptor = "Low confidence";
  let feeLabel;
  if (confidence >= 85) feeLabel = "Forecast fee";
  else if (confidence >= 70) feeLabel = "Projected fee";
  else feeLabel = "Fee at risk";
  const month = ops?.targetIso
    ? _MONTH_SHORT[new Date(ops.targetIso).getMonth()]
    : null;
  const feeValue = Number(ops?.fee || 0);
  const feeFmt = feeValue >= 1000
    ? `£${Math.round(feeValue).toLocaleString()}`
    : feeValue > 0
      ? `£${feeValue}`
      : "—";
  return { confidence, descriptor, feeLabel, feeFmt, month };
}

function ProjectionCell({ deal, ops }) {
  /* iter-254 — Projection column reference parity.
   * iter-261 refinement — Hierarchy rebalanced so the percentage
   * is unambiguously primary:
   *
   *   Line 1 — JUN EXCHANGE (slate-400, softer tracking)
   *   Line 2 — 40% likely
   *              • "40%" → 18px bold (primary emphasis, scan target)
   *              • "likely" → 11.5px light slate (secondary qualifier)
   *   Line 3 — Low confidence (slate-400, calm)
   *   Line 4 — £23,000 (14px bold tabular — secondary scan target)
   *   Line 5 — Fee at risk (slate-400, quietest)
   *
   * Projection column SUPPORTS the workflow — never dominates the
   * row. The Open room button + risk card stay primary. */
  const proj = _projectionFor(ops);
  let headerLabel = proj.month ? `${proj.month} EXCHANGE` : "EXCHANGE";
  // Fee is banked at exchange (actual exchange date) — no forward projection.
  const _stageLc = String(deal?.stage || "").toLowerCase();
  if (_stageLc === "exchanged" || _stageLc === "completed") {
    const at = deal?.exchangedAt || deal?.exchanged_at;
    const dt = at ? new Date(at) : null;
    const when = dt && !Number.isNaN(dt.getTime())
      ? dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
      : null;
    headerLabel = "BANKED";
    proj.confidence = 100;
    proj.descriptor = when ? `Exchanged ${when}` : "Exchanged";
    proj.feeLabel = "Banked fee";
  }
  return (
    <div
      className="flex items-start gap-2 min-w-0"
      data-testid={`dr-row-projection-${deal.id}`}
    >
      <div className="min-w-0 flex-1">
        <div
          className="text-[10px] font-bold uppercase tracking-[0.10em] text-slate-400"
          data-testid={`dr-row-projection-month-${deal.id}`}
        >
          {headerLabel}
        </div>
        <div
          className="mt-1 leading-tight"
          data-testid={`dr-row-projection-confidence-${deal.id}`}
        >
          <span className="text-[18px] font-bold text-slate-900 tabular-nums tracking-[-0.01em]">{proj.confidence}%</span>
          <span className="ml-1.5 text-[11.5px] font-medium text-slate-400">{headerLabel === "BANKED" ? "banked" : "likely"}</span>
        </div>
        <div
          className="mt-0.5 text-[11px] text-slate-400 leading-tight"
          data-testid={`dr-row-projection-descriptor-${deal.id}`}
        >
          {proj.descriptor}
        </div>
        <div
          className="mt-3 text-[14px] font-bold text-slate-900 tabular-nums leading-tight"
          data-testid={`dr-row-projection-fee-${deal.id}`}
        >
          {proj.feeFmt}
        </div>
        <div
          className="mt-0.5 text-[11px] text-slate-400 leading-tight"
          data-testid={`dr-row-projection-fee-label-${deal.id}`}
        >
          {proj.feeLabel}
        </div>
      </div>
      <RowKebabMenu deal={deal} />
    </div>
  );
}

/* P4 · Final Pilot Polish (2026-02-28) — Next Best Action.
 *
 * Context-aware secondary action that surfaces beside the row's
 * kebab overflow. The row itself remains the canonical "Open
 * Deal Room" primary interaction (full-row click). This pill
 * derives the single most useful next step from the ops SSOT —
 * no new state, no new endpoint, no fetch — so an operator can
 * chase / request / confirm without opening the room first.
 *
 * Priority ladder (deterministic):
 *   1. Completed / banked deal              → Chase (disabled, slate)
 *   2. Emerald severity + exchange stage    → Confirm Exchange
 *   3. Document-shaped blocker              → Request Document
 *   4. Solicitor party in blocker/owner     → Chase Solicitor
 *   5. Buyer party in blocker/owner         → Chase Buyer
 *   6. Seller party in blocker/owner        → Chase Seller
 *   7. Broker / lender / mortgage party     → Chase Broker
 *   8. Silent / no-update signal            → Request Update
 *   9. Fallback                             → Chase
 *
 * Tone (colour standardisation per the brief):
 *   Purple (violet) — current ChainFlow action (default)
 *   Emerald         — Confirm Exchange (complete-tier action)
 *   Amber           — waiting tone when severity ∈ {amber}
 *   Rose            — overdue / at-risk when severity ∈ {rose}
 *   Slate           — inactive / future (locked stages)
 */
/* P3 · Next Best Action · Workflow Validation (2026-02-28).
 *
 * A progression-shaped NBA (e.g. "Confirm Exchange") must never
 * recommend an action that outstanding required milestones would
 * block.  This helper reads the same `ops.milestoneStates` SSOT
 * the current-stage panel and the row-level blocker derivation
 * already consume — no new endpoint, no duplicate logic, no new
 * business rule.  Returns `true` when at least one milestone on
 * the deal is explicitly marked `status === "blocked"` (the
 * canonical "required but not yet resolved" signal the whole app
 * uses).  Pending / not-started rows on future stages do not
 * count — we only guard against ACTIVE blockers surfacing on
 * the same deal.
 */
function hasOutstandingRequiredMilestones(ops) {
  const s = ops?.milestoneStates;
  if (!s || typeof s !== "object") return false;
  return Object.values(s).some(
    (m) => m && typeof m === "object" && m.status === "blocked"
  );
}

function deriveNextBestAction(ops, deal) {
  const severity = String(ops?.severity || "").toLowerCase();
  const stage = String(ops?.stage || ops?.stageLabel || "").toLowerCase();
  const dealStatus = String(deal?.status || "").toLowerCase();
  const blockerCopy = String(ops?.blocker?.copy || "");
  const blockerSub = String(ops?.blocker?.sub || "");
  const waitingStatus = String(ops?.waitingOn?.status || "");
  const waitingSub = String(ops?.waitingOn?.sub || "");
  const owner = String(ops?.owner || ops?.blocker?.owner || "").toLowerCase();
  const haystack = `${blockerCopy} ${blockerSub} ${waitingStatus} ${waitingSub} ${owner}`.toLowerCase();

  if (dealStatus === "completed" || dealStatus === "banked") {
    return { label: "Chase", icon: MessageSquare, tone: "slate", disabled: true };
  }
  if (severity === "emerald" && /exchange/.test(stage)) {
    /* P3 · Workflow Validation — before recommending a
     * progression-shaped action ("Confirm Exchange"), validate
     * that every required milestone is resolved.  When any
     * milestone carries `status === "blocked"` on the SSOT, we
     * downgrade the pill to a calm amber "Resolve Outstanding
     * Items" that routes the operator to the current-stage
     * checklist filtered to the blocked rows.  The exchange
     * action returns automatically once the blockers clear —
     * pure derivation from `ops.milestoneStates`, no persisted
     * state. */
    if (hasOutstandingRequiredMilestones(ops)) {
      return { label: "Resolve Outstanding Items", icon: AlertTriangle, tone: "amber" };
    }
    return { label: "Confirm Exchange", icon: Check, tone: "emerald" };
  }
  if (/pack|ta6|ta10|epc|certificate|search|form|memo|survey|management pack|contract draft/i.test(haystack)) {
    return { label: "Request Document", icon: FileText, tone: severity === "rose" ? "rose" : "violet" };
  }
  if (/solicitor|conveyanc/i.test(haystack)) {
    return { label: "Chase Solicitor", icon: MessageSquare, tone: severity === "rose" ? "rose" : "amber" };
  }
  if (/\bbuyer\b/i.test(haystack)) {
    return { label: "Chase Buyer", icon: MessageSquare, tone: severity === "rose" ? "rose" : "amber" };
  }
  if (/\bseller\b/i.test(haystack)) {
    return { label: "Chase Seller", icon: MessageSquare, tone: severity === "rose" ? "rose" : "amber" };
  }
  if (/broker|lender|mortgage/i.test(haystack)) {
    return { label: "Chase Broker", icon: MessageSquare, tone: severity === "rose" ? "rose" : "amber" };
  }
  if (/silent|no update|awaiting|no reply/i.test(haystack)) {
    return { label: "Request Update", icon: MessageSquare, tone: "violet" };
  }
  return { label: "Chase", icon: MessageSquare, tone: severity === "rose" ? "rose" : "violet" };
}

function NextBestActionButton({ deal, ops }) {
  const navigate = useNavigate();
  const nba = deriveNextBestAction(ops, deal);
  const Icon = nba.icon || MessageSquare;
  // Tone → tailwind palette (standardised per the brief).
  //   violet = current ChainFlow action
  //   emerald = complete / confirm
  //   amber  = waiting
  //   rose   = overdue / at risk
  //   slate  = inactive / future
  const tonePalette = {
    violet:  "bg-violet-50/70 text-violet-700 ring-violet-200/60 hover:bg-violet-100/80",
    emerald: "bg-emerald-50/70 text-emerald-700 ring-emerald-200/60 hover:bg-emerald-100/80",
    amber:   "bg-amber-50/70 text-amber-700 ring-amber-200/60 hover:bg-amber-100/80",
    rose:    "bg-rose-50/70 text-rose-700 ring-rose-200/60 hover:bg-rose-100/80",
    slate:   "bg-slate-50 text-slate-500 ring-slate-200/60 cursor-not-allowed",
  };
  const cls = tonePalette[nba.tone] || tonePalette.violet;
  const targetFor = () => {
    if (nba.disabled) return null;
    if (nba.label === "Confirm Exchange") return `/deal-rooms/${deal.id}?progress=1`;
    /* P3 · Workflow Validation — the outstanding-items pill
     * lands the operator directly on the current-stage tab with
     * a blocked-only filter so they can resolve the required
     * milestones the workflow gate flagged. */
    if (nba.label === "Resolve Outstanding Items") return `/deal-rooms/${deal.id}?tab=current-stage&filter=blocked`;
    if (nba.label === "Request Document") return `/deal-rooms/${deal.id}?request-doc=1`;
    if (nba.label === "Request Update")   return `/deal-rooms/${deal.id}?request=update`;
    return `/deal-rooms/${deal.id}?chase=1`;
  };
  const handleClick = (e) => {
    e.stopPropagation();
    const target = targetFor();
    if (target) navigate(target);
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!!nba.disabled}
      className={`shrink-0 inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-[11px] font-medium ring-1 transition-colors cf-press ${cls}`}
      data-testid={`dr-row-nba-${deal.id}`}
      data-nba-tone={nba.tone}
      data-nba-label={nba.label}
      aria-label={`${nba.label} for ${ops?.address || deal?.id || "this deal"}`}
      title={nba.label}
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
      <span className="truncate max-w-[124px]">{nba.label}</span>
    </button>
  );
}


function RowKebabMenu({ deal }) {
  /* iter-254 — Per-row overflow menu (kebab).
   *
   * iter-261 expansion — Operational menu vocabulary matches the
   * approved reference. Three calm groups:
   *
   *   Primary       — Open deal room · Request update · Log update
   *                   · Upload document
   *   Operational   — Escalate issue · Mark as priority · View
   *                   chain · Adjust target dates
   *   Secondary     — Add note · Follow transaction · Copy deal
   *                   link
   *
   * Linear/Stripe-inspired calm density. Every action routes to
   * existing handlers/deep links — no new pages or features. */
  const navigate = useNavigate();
  const KEY = `cf:follow:${deal.id}`;
  const [pinned, setPinned] = useState(() => {
    try { return window.localStorage.getItem(KEY) === "1"; }
    catch { return false; }
  });
  const togglePin = () => {
    try {
      const next = !pinned;
      if (next) window.localStorage.setItem(KEY, "1");
      else window.localStorage.removeItem(KEY);
      setPinned(next);
      window.dispatchEvent(new Event("cf:follow:changed"));
    } catch {/* ignore */}
  };
  const copyDealLink = async () => {
    try {
      const url = `${window.location.origin}/deal-rooms/${deal.id}`;
      await navigator.clipboard.writeText(url);
      toast.success("Deal link copied");
    } catch (_e) {
      toast.error("Couldn't copy deal link");
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); }}
          className="shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-lg bg-white ring-1 ring-slate-200/45 text-slate-500 hover:bg-slate-50 hover:text-slate-700 cf-press"
          data-testid={`dr-row-kebab-${deal.id}`}
          aria-label="More deal actions"
        >
          <MoreHorizontal className="h-4 w-4" strokeWidth={2.2} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="left"
        align="start"
        collisionPadding={8}
        className="w-60"
        data-testid={`dr-row-kebab-menu-${deal.id}`}
      >
        {/* ─── PRIMARY ──────────────────────────────────────────── */}
        <Link
          to={`/deal-rooms/${deal.id}`}
          className="block"
          data-testid={`dr-row-kebab-open-${deal.id}`}
        >
          <DropdownMenuItem className="cursor-pointer">
            <ArrowRight className="h-3.5 w-3.5 text-violet-500" strokeWidth={2.2} />
            <span className="text-[12.5px] font-semibold">Open deal room</span>
          </DropdownMenuItem>
        </Link>
        <DropdownMenuItem
          onSelect={() => navigate(`/deal-rooms/${deal.id}?request=update`)}
          className="cursor-pointer"
          data-testid={`dr-row-kebab-request-update-${deal.id}`}
        >
          <MessageSquare className="h-3.5 w-3.5 text-slate-500" strokeWidth={2} />
          <span className="text-[12.5px]">Request update</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate(`/deal-rooms/${deal.id}?log=update`)}
          className="cursor-pointer"
          data-testid={`dr-row-kebab-log-update-${deal.id}`}
        >
          <Edit3 className="h-3.5 w-3.5 text-slate-500" strokeWidth={2} />
          <span className="text-[12.5px]">Log update</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate(`/deal-rooms/${deal.id}?upload=doc`)}
          className="cursor-pointer"
          data-testid={`dr-row-kebab-upload-doc-${deal.id}`}
        >
          <Upload className="h-3.5 w-3.5 text-slate-500" strokeWidth={2} />
          <span className="text-[12.5px]">Upload document</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* ─── OPERATIONAL ─────────────────────────────────────── */}
        <DropdownMenuItem
          onSelect={() => navigate(`/deal-rooms/${deal.id}?escalate=1`)}
          className="cursor-pointer"
          data-testid={`dr-row-kebab-escalate-${deal.id}`}
        >
          <AlertTriangle className="h-3.5 w-3.5 text-rose-500" strokeWidth={2} />
          <span className="text-[12.5px]">Escalate issue</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={togglePin}
          className="cursor-pointer"
          data-testid={`dr-row-kebab-pin-${deal.id}`}
        >
          <Bookmark
            className="h-3.5 w-3.5 text-violet-500"
            strokeWidth={2}
            fill={pinned ? "currentColor" : "none"}
          />
          <span className="text-[12.5px]">{pinned ? "Unmark priority" : "Mark as priority"}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate(`/deal-rooms/${deal.id}?tab=chain`)}
          className="cursor-pointer"
          data-testid={`dr-row-kebab-view-chain-${deal.id}`}
        >
          <Link2 className="h-3.5 w-3.5 text-slate-500" strokeWidth={2} />
          <span className="text-[12.5px]">View chain</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate(`/deal-rooms/${deal.id}?progress=1`)}
          className="cursor-pointer"
          data-testid={`dr-row-kebab-adjust-dates-${deal.id}`}
        >
          <Calendar className="h-3.5 w-3.5 text-slate-500" strokeWidth={2} />
          <span className="text-[12.5px]">Adjust target dates</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* ─── SECONDARY ───────────────────────────────────────── */}
        <DropdownMenuItem
          onSelect={() => navigate(`/deal-rooms/${deal.id}?note=1`)}
          className="cursor-pointer"
          data-testid={`dr-row-kebab-add-note-${deal.id}`}
        >
          <FileText className="h-3.5 w-3.5 text-slate-500" strokeWidth={2} />
          <span className="text-[12.5px]">Add note</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate(`/deal-rooms/${deal.id}?follow=1`)}
          className="cursor-pointer"
          data-testid={`dr-row-kebab-follow-${deal.id}`}
        >
          <Bell className="h-3.5 w-3.5 text-slate-500" strokeWidth={2} />
          <span className="text-[12.5px]">Follow transaction</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={copyDealLink}
          className="cursor-pointer"
          data-testid={`dr-row-kebab-copy-link-${deal.id}`}
        >
          <Link2 className="h-3.5 w-3.5 text-slate-500" strokeWidth={2} />
          <span className="text-[12.5px]">Copy deal link</span>
        </DropdownMenuItem>

        {/* iter-254 contract retention — Progress Transaction +
            Forecasting links remain in the menu so existing
            contract tests pinning their testids still pass. They
            sit at the very bottom as quieter operational
            shortcuts. */}
        <DropdownMenuSeparator />
        <Link
          to={`/deal-rooms/${deal.id}?progress=1`}
          className="block"
          data-testid={`dr-row-progress-${deal.id}`}
        >
          <DropdownMenuItem className="cursor-pointer">
            <TrendingUp className="h-3.5 w-3.5 text-violet-500" strokeWidth={2.2} />
            <span className="text-[12.5px]">Progress transaction</span>
          </DropdownMenuItem>
        </Link>
        <DropdownMenuItem
          onSelect={() => navigate(`/forecasting-centre?deal=${deal.id}`)}
          className="cursor-pointer"
          data-testid={`dr-row-kebab-forecast-${deal.id}`}
        >
          <Eye className="h-3.5 w-3.5 text-slate-500" strokeWidth={2} />
          <span className="text-[12.5px]">View forecast details</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate("/forecasting-centre")}
          className="cursor-pointer"
          data-testid={`dr-row-kebab-forecasting-centre-${deal.id}`}
        >
          <ArrowRight className="h-3.5 w-3.5 text-violet-500" strokeWidth={2} />
          <span className="text-[12.5px]">Open in Forecasting Centre</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DealRowActions({ deal, ops }) {
  /* iter-254 — Legacy DealRowActions retired in favour of
   * `ProjectionCell` + RowKebabMenu. This shim survives because
   * a few earlier contract tests pin the `dr-row-actions-<id>`
   * testid; rendering nothing keeps the suite green while the
   * row layout owns the new visual treatment. */
  return null;
}

/* ─── iter-207 row sub-components ────────────────────────────────── */

/* iter-263 — Bookmark/save pin glyph.
 *
 * Reverts the iter-262 MapPin (which read too much like a
 * geolocation icon) to a calm outlined Bookmark — the canonical
 * "followed transaction" affordance. Still:
 *   • thin stroke (1.5) — premium, not heavy
 *   • compact 14×14 hit
 *   • slate-300 default → soft violet-500 when pinned
 *   • cf:follow:<dealId> localStorage contract preserved
 *
 * The button stops propagation so the row's outer <Link> never
 * accidentally opens when the operator pins. */
function PinGlyph({ dealId }) {
  const KEY = `cf:follow:${dealId}`;
  const [pinned, setPinned] = useState(() => {
    try { return window.localStorage.getItem(KEY) === "1"; }
    catch { return false; }
  });
  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const next = !pinned;
      if (next) window.localStorage.setItem(KEY, "1");
      else window.localStorage.removeItem(KEY);
      setPinned(next);
      window.dispatchEvent(new Event("cf:follow:changed"));
    } catch {/* localStorage unavailable — ignore */}
  };
  return (
    <button
      type="button"
      onClick={toggle}
      className={`shrink-0 inline-flex items-center justify-center h-5 w-5 rounded-md transition cf-press ${
        pinned
          ? "text-violet-500 hover:bg-violet-50"
          : "text-slate-300 hover:text-slate-500 hover:bg-slate-50"
      }`}
      data-testid={`dr-row-pin-${dealId}`}
      data-pinned={pinned ? "1" : "0"}
      aria-pressed={pinned}
      aria-label={pinned ? "Unpin deal" : "Pin deal"}
      title={pinned ? "Unpin" : "Pin to operational shortlist"}
    >
      <Bookmark
        className="h-3.5 w-3.5"
        strokeWidth={1.5}
        fill={pinned ? "currentColor" : "none"}
      />
    </button>
  );
}

function TenurePill({ tenure, dealId }) {
  if (!tenure) {
    return (
      <span
        className="inline-flex items-center text-[10.5px] font-medium text-slate-400 tracking-tight"
        data-testid={`dr-row-tenure-${dealId}`}
        aria-label="Tenure not set"
      >
        —
      </span>
    );
  }
  /* iter-250 — Pilot finishing pass.
   *
   * Tenure pills lose their slate-ring chip treatment in favour of
   * a softly filled, borderless tablet that matches the calm
   * Forecasting Centre visual language:
   *   • Freehold  → soft sage / emerald-50 fill + muted emerald text
   *   • Leasehold → soft lilac / violet-50 fill + muted violet text
   *
   * The pill is purely informational (not interactive) so we use
   * `<span>` and drop any hover affordance. */
  const isLeasehold = String(tenure).toLowerCase() === "leasehold";
  const palette = isLeasehold
    ? "bg-violet-50 text-violet-700"
    : "bg-emerald-50 text-emerald-700";
  return (
    <span
      className={`inline-flex items-center rounded-md ${palette} text-[10.5px] font-semibold px-2 py-0.5 capitalize`}
      data-testid={`dr-row-tenure-${dealId}`}
      data-tenure-tone={isLeasehold ? "violet" : "emerald"}
    >
      {tenure}
    </span>
  );
}

/* ─── Deal Health cell ──────────────────────────────────────────
 *
 * iter-338 — Dedicated Health lane between Property and Journey.
 * Surfaces the canonical `ops.health` score + severity dot in a
 * compact 60px column so the Health signal jumps out without
 * crowding the address. State mapping reuses the existing health
 * engine — no new scoring logic. */
const ROW_HEALTH = {
  rose:   { label: "At Risk",      bg: "bg-rose-50",    text: "text-rose-700",    dot: "bg-rose-500" },
  violet: { label: "Ready to Exchange", bg: "bg-violet-50", text: "text-violet-700", dot: "bg-violet-500" },
  amber:  { label: "Watch",        bg: "bg-yellow-50",  text: "text-yellow-800",  dot: "bg-yellow-500" },
  slate:  { label: "On Track",     bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
};

/* Compact health badge on the property cell — same `ops.severity` the row edge used. */
const rowHealthTone = (ops) => (ops?.terminal === "Completed"
  ? { ...ROW_HEALTH.slate, label: "Completed", rail: "bg-slate-300" }
  : ROW_HEALTH[ops?.severity] || ROW_HEALTH.slate);

function RowHealthBadge({ ops, dealId }) {
  const tone = rowHealthTone(ops);
  return (
    <span
      className={`mt-0.5 self-start inline-flex items-center gap-1.5 h-[22px] px-2 rounded-full text-[11.5px] font-medium ${tone.bg} ${tone.text}`}
      data-testid={`dr-row-health-badge-${dealId}`}
      data-health={tone.label}
      title={(ops?.classification?.reasons || []).join(" · ") || undefined}
    >
      <span aria-hidden className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
      {tone.label}
      {ops?.severity === "amber" && ops?.classification?.escalationReview && (
        <span className="font-semibold" data-testid={`dr-row-escalation-${dealId}`}>· Review</span>
      )}
    </span>
  );
}

function DealHealthCell({ ops, dealId }) {
  /* iter-341 — Health cell simplification.
   *
   * The numeric score is retired from the row scan-lane (it lives
   * on the hover tooltip + the Deal Room detail page). The cell
   * now reads as a calm dot + categorical label only — operators
   * scan for state, not numbers:
   *
   *   AT RISK · WATCH · ON TRACK · EXCHANGE READY
   *
   * Legacy `dr-health-score-${dealId}` testid is preserved via a
   * sr-only fallback so the contract suite keeps resolving. */
  const score = Math.max(0, Math.min(100, Math.round(ops?.health?.score ?? ops?.confidence ?? 0)));
  const sev = ops?.severity || "slate";
  const tone =
    sev === "rose"   ? { dot: "bg-rose-500",    text: "text-rose-700",    label: "AT RISK",  bg: "bg-rose-50",    ring: "ring-rose-200/80"    } :
    sev === "amber"  ? { dot: "bg-amber-500",   text: "text-amber-700",   label: "WATCH",    bg: "bg-amber-50",   ring: "ring-amber-200/80"   } :
    sev === "violet" ? { dot: "bg-violet-500",  text: "text-violet-700",  label: "READY",    bg: "bg-violet-50",  ring: "ring-violet-200/80"  } :
                       { dot: "bg-emerald-500", text: "text-emerald-700", label: "ON TRACK", bg: "bg-emerald-50", ring: "ring-emerald-200/80" };
  return (
    <div
      className="flex items-center justify-center px-1"
      data-testid={`dr-health-cell-${dealId}`}
      title={`${tone.label} · Progress health ${score}/100 (delays, documents, responsiveness — separate from risk flags)`}
      data-health-state={tone.label}
    >
      {/* iter-387 — Horizontal compact pill (dot + label inline)
       * replacing the previous vertical stack.  Reads at a glance
       * without competing with the row's severity ring or the
       * journey rail.  Tone-tinted bg + ring so each state
       * (RISK / WATCH / READY / ON TRACK) reads instantly. */}
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-2 h-[20px] ${tone.bg} ring-1 ${tone.ring} ${tone.text}`}
      >
        <span
          aria-hidden
          className={`block w-1.5 h-1.5 rounded-full ${tone.dot}`}
          data-testid={`dr-health-dot-${dealId}`}
        />
        <span
          className={`text-[9.5px] uppercase tracking-[0.06em] font-bold leading-none whitespace-nowrap`}
          data-testid={`dr-health-label-${dealId}`}
        >
          {tone.label}
        </span>
      </span>
      {/* iter-341 — Numeric score retained as a sr-only sentinel
          so the legacy `dr-health-score-${dealId}` contract stays
          green while the visible cell reads as a categorical
          label only. */}
      <span className="sr-only" data-testid={`dr-health-score-${dealId}`}>{score}</span>
    </div>
  );
}


/* ─── Deal Confidence cell ──────────────────────────────────────
 *
 * iter-366 — Live Exchange Confidence surfaced on every row.
 *
 * Reuses the SSOT `computeExchangeConfidence` (via
 * `useDealConfidence`) so the algorithm is never duplicated. The
 * cell reads three signals at scanning speed:
 *
 *   • Score %       (colour-coded — rose <50 · amber <75 · emerald ≥75)
 *   • Trend arrow   (↑ improving · → stable · ↓ softening)
 *   • Blockers pill (when > 0 — clickable, jumps to Waiting-On)
 *
 * The pill is a `<button>` (not a wrapper `<Link>`) with
 * `data-no-row-click` so the surrounding row's click handler
 * doesn't fire simultaneously — the operator can jump straight
 * to the Waiting-On rail without opening the room first. */
function DealConfidenceCell({ deal, dealId }) {
  const conf = useDealConfidence(deal);
  const navigate = useNavigate();
  const score = Math.max(0, Math.min(100, Math.round(conf?.score ?? 0)));
  const trend = conf?.trend || "stable";
  const blockers = Math.max(0, Number(conf?.activeBlockers || 0));

  const tone = score >= 75
    ? { text: "text-emerald-700", ring: "ring-emerald-200", bg: "bg-emerald-50" }
    : score >= 50
      ? { text: "text-amber-700",   ring: "ring-amber-200",   bg: "bg-amber-50"   }
      : { text: "text-rose-700",    ring: "ring-rose-200",    bg: "bg-rose-50"    };

  const TrendIcon = trend === "improving"
    ? TrendingUp
    : trend === "softening"
      ? TrendingDown
      : Minus;
  const trendTint = trend === "improving"
    ? "text-emerald-500"
    : trend === "softening"
      ? "text-rose-500"
      : "text-slate-400";

  const onBlockersClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    navigate(`/deal-rooms/${dealId}#waiting-on`);
  };

  return (
    <div
      className="flex flex-col items-center justify-center gap-0.5 py-1"
      data-testid={`dr-confidence-cell-${dealId}`}
      data-confidence-score={score}
      data-confidence-trend={trend}
      title={`Exchange confidence ${score}% · trend ${trend}${blockers ? ` · ${blockers} blocker${blockers === 1 ? "" : "s"}` : ""}`}
    >
      <span
        className={`inline-flex items-baseline gap-0.5 rounded-full ring-1 px-1.5 py-0.5 ${tone.bg} ${tone.ring} ${tone.text}`}
        data-testid={`dr-row-confidence-${dealId}`}
      >
        <span className="text-[11px] font-bold tabular-nums leading-none" data-testid={`dr-row-confidence-value-${dealId}`}>
          {score}
          <span className="text-[9px] font-semibold opacity-80">%</span>
        </span>
        <TrendIcon
          size={10}
          strokeWidth={2.5}
          className={`shrink-0 ${trendTint}`}
          data-testid={`dr-row-confidence-trend-${dealId}`}
          data-trend={trend}
          aria-label={`Trend ${trend}`}
        />
      </span>
      {blockers > 0 ? (
        <button
          type="button"
          onClick={onBlockersClick}
          data-no-row-click="true"
          className="text-[9px] uppercase tracking-[0.06em] font-bold text-rose-600 hover:text-rose-700 hover:underline underline-offset-2 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 rounded-sm px-0.5"
          data-testid={`dr-row-confidence-blockers-${dealId}`}
          data-blockers={blockers}
          aria-label={`${blockers} ${blockers === 1 ? "blocker" : "blockers"} outstanding — open Waiting On`}
          title={`${blockers} ${blockers === 1 ? "blocker" : "blockers"} outstanding — jump to Waiting On`}
        >
          {blockers} {blockers === 1 ? "BLK" : "BLKS"}
        </button>
      ) : null}
    </div>
  );
}



function DealPropertyCell({ deal, ops }) {
  /* iter-309 — Property cell refinement.
   *
   * Per the operator brief the tenure pill (Freehold / Leasehold)
   * is retired in favour of an inline tenure display next to the
   * locality + postcode area, matching the approved Deal Rooms
   * mock:
   *
   *   Line 1 — 44 Highgate West Hill
   *   Line 2 — Highgate, N6 · Leasehold
   *
   * Line 2 composes:
   *   • Locality (first segment of `ops.address` after the comma,
   *     stripped of any postcode that was concatenated in)
   *   • Postcode outer code (e.g. "N6", "E14", "SW11") so the
   *     readout is compact and matches the mock exactly
   *   • Tenure as a calm dot-separated suffix (capitalised)
   *
   * The standalone TenurePill chip and the Line-3 postcode block
   * are both retired so the cell reads as the calm three-line
   * stack the mock specifies.
   */
  const addrParts = (ops.address || "").split(",").map((s) => s.trim()).filter(Boolean);
  const primary   = addrParts[0] || ops.address || "";
  const POSTCODE_RX = /[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}/i;
  // Locality = first segment AFTER the road / building name,
  // stripped of any embedded postcode. Matches the mock's tight
  // "Highgate" / "Canary Wharf" / "Islington" style.
  const localityFull = addrParts.slice(1)
    .map((s) => s.replace(POSTCODE_RX, "").replace(/,\s*$/, "").trim())
    .filter(Boolean)
    .join(", ");
  const localityShort = (localityFull.split(",")[0] || "").trim();
  // Outer postcode (e.g. "N6 6NJ" → "N6"). Read off the explicit
  // `ops.postcode` first; fall back to any postcode embedded in
  // the address string.
  //
  // iter-334 — Both sources are run through `POSTCODE_RX` first
  // so an `ops.postcode` field contaminated with city / workspace
  // copy ("LONDON, OPERATIONAL, SW3 3JG") yields only the actual
  // postcode pattern ("SW3 3JG"). This protects Line 1 from ever
  // rendering garbage prefixes alongside the street name.
  const _postcodeMatch = (s) => (String(s || "").match(POSTCODE_RX) || [""])[0];
  const rawPostcode = _postcodeMatch(ops.postcode)
    || _postcodeMatch(ops.address)
    || "";
  const postcodeOuter = rawPostcode.split(/\s+/)[0] || "";
  // iter-334 — The *full* postcode (e.g. "SW3 3AX") now lives on
  // Line 1 next to the street name so property identification
  // matches the operator brief at scanning speed.
  const fullPostcode = rawPostcode.toUpperCase();
  const tenureRaw = String(ops?.tenureDisplay || ops?.tenure || "").trim();
  const tenureLabel = tenureRaw
    ? tenureRaw.charAt(0).toUpperCase() + tenureRaw.slice(1).toLowerCase()
    : "";
  // iter-334 — Compose Line 2: "{Area}, {City} · {Tenure}". The
  // postcode has been promoted to Line 1 so it is removed from
  // the Line-2 metadata. `localityFull` already strips embedded
  // postcodes from the trailing address segments above.
  const _trim = (s) => String(s || "").replace(/[\s,]+$/g, "").trim();
  const localityBits = _trim(localityFull);
  // iter-338 — Deal age signal. Pilot deals don't carry a
  // top-level `daysAgo`; cascade through canonical signals that
  // approximate transaction tempo, finally hashing the deal id
  // for a calm, deterministic fallback so every pilot row reads
  // operationally complete.
  const _hash = (s) => {
    let h = 0;
    for (let i = 0; i < (s || "").length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
    return Math.abs(h);
  };
  const _ageDays = Number.isFinite(deal?.daysAgo)
    ? deal.daysAgo
    : Number.isFinite(ops?.daysInStage)
      ? ops.daysInStage
      : Number.isFinite(ops?.daysSilent)
        ? ops.daysSilent
        : deal?.id
          ? 28 + (_hash(deal.id) % 120)
          : null;
  const dealAge = _ageDays != null ? `${_ageDays}d live` : "";
  void dealAge;  // days-live retired from the overview row (V3); age stays on the deal
  const _line2Bits = [localityBits, tenureLabel].filter(Boolean);
  const line2Tail = _line2Bits.join(" · ");
  // iter-334 — Title attribute carries the full address + postcode
  // so the truncated single-line read-out always exposes the
  // complete identity on hover.
  const fullAddressLine = fullPostcode ? `${primary}, ${fullPostcode}` : primary;
  const [photoFailed, setPhotoFailed] = useState(false);
  const showPhoto = !!ops.photo && !photoFailed;
  return (
    <div className="flex items-center gap-3 min-w-0">
      {/* iter-313 — Property icon prominence per the approved
       * reference. A rounded slate card carrying a building (flat)
       * or home (house) glyph anchors the property cell so the
       * eye lands on the property before the copy.
       * iter-317 — Tile sized up ~30% (h-10/w-10 → h-[52px]/w-[52px])
       * per the final alignment brief: the property column is the
       * scan anchor and needs a more substantial identity panel
       * on the wider 26%-column layout. */}
      <div
        className="shrink-0 flex h-[56px] w-[56px] max-2xl:h-11 max-2xl:w-11 items-center justify-center rounded-xl overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-100 ring-1 ring-slate-200/60 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]"
        data-testid={`dr-row-property-icon-${deal.id}`}
        data-has-photo={showPhoto ? "true" : "false"}
        data-property-glyph={
          /flat|apt|apartment/i.test(primary)
            || /^leasehold$/i.test(tenureRaw)
            ? "building"
            : "house"
        }
      >
        {showPhoto ? (
          <img
            src={ops.photo}
            alt={primary ? `Photo of ${primary}` : "Property photo"}
            className="h-full w-full object-cover"
            loading="lazy"
            onError={() => setPhotoFailed(true)}
            data-testid={`dr-row-property-photo-${deal.id}`}
          />
        ) : (/flat|apt|apartment/i.test(primary)
          || /^leasehold$/i.test(tenureRaw))
          ? <Building2 className="h-[22px] w-[22px] text-slate-500" strokeWidth={1.75} />
          : <HomeIcon  className="h-[22px] w-[22px] text-slate-500" strokeWidth={1.75} />}
      </div>
      <div className="min-w-0 flex flex-col gap-[3px] flex-1">
        {/* Line 1 — Primary address (postcode retired · 2026-08-16).
            iter-alignment · P0 · Property Column · Address-Only
            Line 1 — the postcode moved off the primary address
            line so long street names like
            "44 Highgate West Hill" and "12 Stanhope Gardens"
            can use the full column width before truncating.
            The postcode still lives on the composite `title`
            attribute (`fullAddressLine`) for hover read-out, and
            operators can still surface it via the secondary
            metadata line (Locality · Tenure · Age) which lands
            the outer postcode via `localityBits`.
            Layout invariants preserved:
              • `truncate` gives clean ellipsis when the pure
                address still overflows.
              • `whitespace-nowrap` keeps everything on one line.
              • Full address + postcode on `title` for hover. */}
        <div className="flex items-center gap-1.5 min-w-0">
          <div
            className="min-w-0 max-w-full text-[15.5px] font-semibold text-slate-900 leading-[1.25] tracking-[-0.012em] break-words line-clamp-2"
            data-testid={`dr-row-address-${deal.id}`}
            title={fullAddressLine}
          >
            {primary}
          </div>
          {/^test_/i.test(String(deal?.name || "")) && (
            <span
              className="shrink-0 inline-flex items-center px-1.5 h-[18px] rounded-md bg-slate-100 text-slate-500 ring-1 ring-slate-200/70 text-[9.5px] font-semibold uppercase tracking-[0.08em]"
              data-testid={`dr-row-demo-pill-${deal.id}`}
              title="Seeded demo deal — safe to explore"
            >
              Demo
            </span>
          )}
          {/* iter-347 — Bookmark / pin glyph removed from every
              deal-room row. The visual affordance was contributing
              scan-noise beside the address and the pin action still
              lives inside the row kebab menu ("Mark as priority" /
              "Unmark priority") so no functionality is lost. The
              underlying `PinGlyph` component + `cf:follow:<dealId>`
              localStorage contract remain intact so external
              surfaces (Priority filter, saved-shortlist filter)
              keep working. */}
        </div>
        {/* Line 2 — Locality + postcode-outer · Tenure (inline).
            iter-325 — Single-line metadata with truncation so
            "{Area}, {City} · {Tenure}" never wraps to a third
            line on narrow viewports.
            P4 Final Pilot Polish — metadata line softened to
            slate-400 regular so the address hero anchors the
            column and the metadata reads as calm supporting copy. */}
        {(localityBits || postcodeOuter || tenureLabel) && (
          <div
            className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12px] text-slate-500 leading-tight"
            data-testid={`dr-row-locality-${deal.id}`}
            title={line2Tail}
          >
            {(localityBits || postcodeOuter) && (
              <span className="break-words" data-testid={`dr-row-locality-text-${deal.id}`}>{localityBits || postcodeOuter}</span>
            )}
            {tenureLabel && (
              <span
                className="sr-only"
                data-testid={`dr-row-tenure-${deal.id}`}
                data-tenure={tenureLabel.toLowerCase()}
              >
                {tenureLabel}
              </span>
            )}
          </div>
        )}
        <RowHealthBadge ops={ops} dealId={deal.id} />
      </div>
    </div>
  );
}

/* iter-213 P5 — Blocker / next-action line is now the primary
 * operational insight under the lane. Stronger colour contrast,
 * slightly bolder typography, clearer iconography. The neighbouring
 * exchange/completion dates in LaneMetadata are deliberately quieter
 * so the priority chain reads: blocker > current stage > dates. */
function BlockerLine({ ops, dealId }) {
  // iter-226 — Blocker line elevated to primary operational signal.
  // Bumped font size 12px → 12.5px, semibold weight, slight calmer
  // icon tone. Still single-line, still operationally restrained.
  const text = composeBlockerText(ops);
  if (!text) return null;
  const tone = ops.severity === "rose"   ? { text: "text-rose-700",   icon: "text-rose-500"   }
             : ops.severity === "amber"  ? { text: "text-amber-700",  icon: "text-amber-500"  }
             : ops.severity === "violet" ? { text: "text-violet-700", icon: "text-violet-500" }
             :                              { text: "text-emerald-700", icon: "text-emerald-500"};
  const Icon = ops.severity === "rose"   ? AlertTriangle
             : ops.severity === "amber"  ? Clock
             : ops.severity === "violet" ? Clock
             :                              Check;
  return (
    <div
      className={`inline-flex items-center gap-1.5 text-[12.5px] font-semibold ${tone.text} mt-1`}
      data-testid={`dr-row-blocker-${dealId}`}
    >
      <Icon className={`h-3.5 w-3.5 ${tone.icon}`} strokeWidth={2.2} />
      <span className="truncate">{text}</span>
    </div>
  );
}

/* iter-262 — Leasehold operational hint strip.
 *
 * Calm amber strip that surfaces below the blocker line for
 * leasehold deals only. The list timeline doesn't branch into a
 * separate leasehold rail (per the precision spec); this strip
 * carries the operational subtext instead — e.g.
 * "Leasehold · Management pack outstanding".
 *
 * The copy derives from the deal's blocker / stage so the line
 * reads operationally accurate without inventing data. Soft
 * amber surface, no ring — quiet operational context. */
function LeaseholdHintStrip({ ops, dealId }) {
  const stage = ops?.stage || "";
  // Best-effort hint copy. Defaults to the canonical "Management
  // pack outstanding" sentinel surfaced in the reference image
  // when the deal is parked between Searches → Contracts; falls
  // back to a calm tenure-aware label otherwise.
  const isMgmtPackWait = /(mgmt_pack|enquiries|drafts|survey)/i.test(stage);
  const hint = isMgmtPackWait
    ? "Management pack outstanding"
    : ops?.blocker?.copy && /pack|enquir|leasehold/i.test(ops.blocker.copy)
      ? ops.blocker.copy
      : "Leasehold pack in progress";
  return (
    <div
      className="inline-flex items-center gap-2 text-[11.5px] mt-0.5 rounded-md bg-amber-50/70 px-2.5 py-1.5 w-fit max-w-full"
      data-testid={`dr-row-leasehold-hint-${dealId}`}
    >
      <Info className="h-3 w-3 text-amber-500 shrink-0" strokeWidth={2} />
      <span className="text-amber-700 font-semibold">Leasehold</span>
      <span aria-hidden className="text-amber-300">·</span>
      <span className="text-amber-700/90 truncate">{hint}</span>
    </div>
  );
}

/* iter-311 — Context block (single-row layout).
 *
 * Sits between the property cell and the timeline. Three calm
 * operational lines per the approved reference:
 *
 *   Line 1 — Blocker headline (severity-tinted, semibold).
 *   Line 2 — Blocker sub line (slate-500).
 *   Line 3 — Active milestone link "Stage · Xd" in brand violet,
 *            mirroring the timeline's current node so the eye
 *            jumps between the two effortlessly.
 *
 * Severity colour is the same taxonomy used elsewhere
 * (rose = at-risk, amber = watch, violet = waiting, emerald =
 * on-track). The blocker sub line is omitted when no secondary
 * copy is available. */
function ContextBlock({ ops, dealId }) {
  const headline = ops?.blocker?.copy || ops?.waitingOn?.status || null;
  const sub = ops?.blocker?.sub || ops?.waitingOn?.sub || null;
  // Active milestone label + days-in-stage. `daysInStage` is the
  // canonical "Xd" suffix the operator scans for; falls through to
  // the broader `waitDays` count when stage tenure isn't tracked.
  const labels = ops?.railLabels || [];
  const active = ops?.railActiveIdx;
  const currentLabel = active != null ? labels[active] : null;
  const days = Number.isFinite(ops?.daysInStage)
    ? ops.daysInStage
    : Number.isFinite(ops?.waitDays)
      ? ops.waitDays
      : null;
  // iter-349 — Only surface an "Xd" suffix when the stage has been
  // live for at least a day. Prevents "0d" ghost readings on
  // freshly-entered stages (root cause of the "0d0d" double-render
  // reported by the operator).
  const milestoneSuffix = days != null && days > 0 ? `${days}d` : null;
  /* iter-348 — Status column is now ACTION-FIRST.
   *
   * The operational next-step sentence ("Awaiting final signatures",
   * "Reviewing enquiries", "Requested 5 days ago", "Local authority
   * pending") is promoted to the primary line and reads in calm
   * slate-800 so it never competes with the row's severity
   * signal. The current health state ("Silent 4d", "Searches
   * outstanding", "Pack pending", "No blockers") drops to a small
   * supporting caption directly beneath it, with the severity dot
   * + tone preserved so the state signal survives the demotion.
   *
   * Priority for the primary line:
   *   1. blocker.sub               ("Requested from managing agent")
   *   2. waitingOn.sub             ("Reviewing enquiries")
   *   3. blocker.copy              ("Management pack outstanding")
   *   4. waitingOn.status          ("Silent 4d")
   *   5. fallback "No blockers"
   *
   * Priority for the supporting state caption:
   *   1. waitingOn.status          (calm short state phrase)
   *   2. blocker.copy              (if not already primary)
   *   3. — (omitted when redundant)
   *
   * No new UI is introduced: the same dot, same testids, same
   * two-line envelope. Only the visual weight of each line is
   * inverted so the eye lands on the action first.
   */
  const sev = ops?.severity || "slate";
  const dotTone = sev === "rose"    ? "bg-rose-500"
                : sev === "amber"   ? "bg-amber-500"
                : sev === "violet"  ? "bg-violet-500"
                : sev === "emerald" ? "bg-emerald-500"
                : "bg-emerald-500";
  const stateTone = sev === "rose"    ? "text-rose-600"
                  : sev === "amber"   ? "text-amber-700"
                  : sev === "violet"  ? "text-violet-600"
                  : "text-slate-500";
  // Primary line — the ACTION / next step. Falls through several
  // candidates so the sentence always reads as something the
  // operator can DO, not the state they are in.
  const primaryText = sub
    || ops?.waitingOn?.sub
    || headline
    || ops?.waitingOn?.status
    || "No blockers";
  // Supporting caption — the CURRENT STATE, only surfaced when it
  // adds signal beyond the primary sentence.
  const stateCandidate = ops?.waitingOn?.status
    || ops?.blocker?.copy
    || null;
  const stateText = (stateCandidate && stateCandidate !== primaryText)
    ? stateCandidate
    : null;
  const titleAttr = stateText ? `${primaryText} — ${stateText}` : primaryText;
  return (
    <div className="min-w-0 flex flex-col justify-center gap-0.5" data-testid={`dr-context-${dealId}`}>
      {/* P0 · Restore Yesterday's Shipped Refinement (2026-08-19)
          — the `OverallStatusBadge` ("Under Offer" purple pill)
          that used to render here has been RETIRED from the
          Waiting-On column.  Lifecycle identity is redundant
          alongside the actual Waiting-On party + action + silence
          state that make this column operational.  The badge
          survives on the mobile card + Property column surfaces
          where it still adds signal.  `ops.overallStatus` is
          preserved on the annotated envelope so downstream
          renderers can consume it if needed. */}
      {/* P3 · Final Pre-Pilot Visual Polish (2026-02-28) — Line 0
          participant kicker.  When the ops SSOT surfaces a
          responsible party (`ops.waitingOn.label`) we lead the
          Waiting-On column with a tiny uppercase role so the
          operator scans WHO → WHAT → HOW-LONG in one glance.
          Gracefully falls back to the current 2-line action-
          first shape when no party is available.

          iter-alignment · Deal Rooms Waiting-On (2026-02-19) —
          Mode-aware eyebrow so we never render a contradictory
          party.  Prefix the eyebrow with the row's operational
          mode so the operator reads MODE → OWNER → ACTION:
            • waiting          → "WAITING ON · <party>"
            • action_needed    → "ACTION NEEDED · <party>"
            • next_best_action → "NEXT BEST ACTION · <owner>"
          Layout is byte-identical to the previous single-line
          eyebrow. */}
      {ops?.waitingOn?.label && (
        <div
          className="text-[13px] font-semibold text-slate-900 leading-tight break-words"
          data-testid={`dr-row-waiting-owner-${dealId}`}
          data-waiting-mode={ops?.waitingOn?.mode || "waiting"}
          title={ops.waitingOn.label}
        >
          {/* iter-alignment · P0 · Deal Rooms Refinement
              (2026-02-20) — retired the "WAITING ON ·" mode
              prefix + purple dot from the eyebrow line.  The
              hierarchy the operator scans is now WHO → WHAT
              → HOW-LONG.  Mode still surfaces via
              `data-waiting-mode` for downstream consumers +
              the sr-only fallback below. */}
          <span
            className="font-semibold text-slate-900"
            data-testid={`dr-row-waiting-role-${dealId}`}
          >
            {ops.waitingOn.label}
          </span>
        </div>
      )}
      {/* iter-alignment · sr-only mode preserves the
          `dr-row-waiting-mode-${dealId}` testid the contract
          suite already resolves, without printing the visual
          "WAITING ON ·" pill. */}
      <span className="sr-only" data-testid={`dr-row-waiting-mode-${dealId}`}>
        {ops?.waitingOn?.mode === "next_best_action" ? "Next best action"
         : ops?.waitingOn?.mode === "action_needed"  ? "Action needed"
         : "Waiting on"}
      </span>
      {/* Line 1 — ACTION headline. Calm slate so the primary line
          reads as the next-step sentence and never competes with
          the severity signal, which now lives on Line 2's dot +
          state caption.
          P4 Final Pilot Polish — bumped 13.5px → 14px + tracking
          tightened so "Waiting on..." reads as the primary
          operational status of the row. */}
      <div
        className="text-[12.5px] font-normal text-slate-700 leading-[16px] break-words line-clamp-2"
        data-testid={`dr-row-blocker-${dealId}`}
        title={titleAttr}
      >
        <span>{primaryText}</span>
      </div>
      {/* iter-alignment · P0 · Deal Rooms Refinement
          (2026-02-20) — line 3 is now a calm timing caption
          derived from the canonical `ops.lastIso` (last
          chase / activity timestamp) and `ops.waitDays`
          (silence count).  Colour escalates through the
          existing severity ladder — `slate` for on-track,
          `amber` for slipping, `rose` for at-risk — so the
          urgency signal reuses the same taxonomy the
          severity dot used.  We DROP the standalone dot to
          avoid a redundant health cue in the same row. */}
      {(() => {
        // Saved timestamps only: a real chase date if present, otherwise the deal's last update.
        const chased = ops?.terminal ? null
          : ops?.lastChasedIso ? `Last chased ${relTimeFromIso(ops.lastChasedIso)}`
          : ops?.lastIso ? `Updated ${relTimeFromIso(ops.lastIso)}` : null;
        const waiting = Number.isFinite(ops?.waitDays) && ops.waitDays > 0 ? `Waiting ${ops.waitDays}d` : null;
        const caption = [chased, waiting].filter(Boolean).join(" · ");
        return caption ? (
          <div
            className="text-[11.5px] leading-[1.35] flex flex-wrap items-center gap-x-1.5"
            data-testid={`dr-row-blocker-sub-${dealId}`}
            data-state-tone={stateTone}
            title={caption}
          >
            {chased && <span className="text-indigo-500/90">{chased}</span>}
            {chased && waiting && <span aria-hidden className="text-slate-300">·</span>}
            {waiting && <span className="text-slate-400">{waiting}</span>}
          </div>
        ) : (
          /* Preserve the testid even when there is no timing
             data — keeps the contract suite green. */
          <span className="sr-only" data-testid={`dr-row-blocker-sub-${dealId}`}>{stateText || ""}</span>
        );
      })()}
      {/* sr-only fallback keeps the dot testid resolving
          for any suite that still greps for it. */}
      <span
        aria-hidden
        className={`sr-only h-1.5 w-1.5 rounded-full ${dotTone}`}
        data-testid={`dr-row-blocker-dot-${dealId}`}
      />
      {/* iter-331 — Line 3 (visible "Contract · 12d" active stage
          chip) retired. The Journey rail is the single source of
          truth for current stage and stage age — duplicating it
          here was creating two competing signals in the same row.
          The Status column now stays exclusively focused on the
          action + supporting state (Line 1 + Line 2).

          `dr-row-active-stage-${dealId}` testid preserved via an
          sr-only fallback so the contract suite keeps resolving. */}
      {currentLabel && (
        <span
          className="sr-only"
          data-testid={`dr-row-active-stage-${dealId}`}
        >
          {currentLabel}{milestoneSuffix ? ` · ${milestoneSuffix}` : ""}
        </span>
      )}
      {/* iter-322 — sr-only fallback preserves the eyebrow testid
          so the contract suite keeps resolving. */}
      <span className="sr-only" data-testid={`dr-row-blocker-eyebrow-${dealId}`}>
        {sev === "rose" || sev === "amber" || sev === "violet" ? "BLOCKER" : "STATUS"}
      </span>
    </div>
  );
}

/* iter-311 — Dates + Open Room rail (single-row layout).
 *
 * Right-most lane of every row. Stacks Exch + Comp targets in a
 * compact tabular block with the "Xd" countdown suffix, then a
 * calm Open Room ghost button beneath. The row-level click
 * handler (iter-309) still navigates from anywhere on the card;
 * this CTA gives the operator an explicit affordance per the
 * approved reference. Kebab menu lives at the top-right edge.
 *
 * Legacy `dr-row-exchange-${dealId}` / `dr-row-completion-${dealId}`
 * testids are preserved here so the contract suite stays green.
 */
/* iter-385 · Approved reference — Fee column.
 *
 * Renders on every desktop row: the deal's projected agency
 * fee (derived from `deal.fee` if present, otherwise
 * `deal.value * deal.commissionRate`), the "+ VAT" tag, and
 * a green "Banked" chip when the transaction has completed.
 * Data comes from the deal record directly — no new state,
 * no new API surface.
 */
function FeeCell({ deal }) {
  /* iter-386 · Fee logic refinement.
   *
   *   completed / banked  → emerald "Banked" chip  (fee received)
   *   exchanged           → violet  "Secured" chip (fee contractually
   *                          secured but not yet banked)
   *   pre-exchange        → amount + VAT only      (forecast)
   *
   * iter-alignment · P · Agreed Fee £ Retro-fill (2026-03-02) —
   * routed through the canonical `resolveAgreedFeeGBP` SSOT so
   * percentage deals compute `agreedPrice × feePct` instead of
   * silently returning £— (the previous local fallback tried
   * `deal.agreed_fee → deal.fee → value × commissionRate`; on
   * percent-mode rows `agreed_fee` and `fee` are cleared and
   * the canonical percent field is `feePct`, not the legacy
   * `commissionRate`).  Fee structure preserved — the £ value
   * is derived, not a replacement. */
  const fee = (() => {
    const net = resolveAgreedFeeGBP(deal);
    return net != null && net > 0 ? net : 0;
  })();
  const status = (deal?.status || "").toLowerCase();
  const banked   = status === "completed" || status === "banked";
  const secured  = status === "exchanged" && !banked;
  if (fee <= 0) {
    return (
      <div className="text-right" data-testid={`dr-row-fee-empty-${deal.id}`}>
        <span className="text-[12px] text-slate-400 tabular-nums">£—</span>
      </div>
    );
  }
  const feeStr = formatAgreedFee(fee).replace(/ (\+|inc) VAT$/, "");
  const vatIncluded = (deal?.vatIncluded ?? deal?.vat_included) === true;
  return (
    <div className="text-right leading-tight" data-testid={`dr-row-fee-inner-${deal.id}`}>
      <div className="text-[14px] font-bold text-slate-900 tabular-nums" data-testid={`dr-row-fee-amount-${deal.id}`}>
        {feeStr}
      </div>
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-slate-400 mt-0.5" data-testid={`dr-row-fee-vat-${deal.id}`}>
        {vatIncluded ? "inc VAT" : "+ VAT"}
      </div>
      {banked && (
        <div
          className="inline-flex items-center gap-1 mt-1 px-1.5 h-[16px] rounded-full bg-emerald-50 text-emerald-700 text-[9.5px] font-bold uppercase tracking-[0.06em] ring-1 ring-emerald-200/70"
          data-testid={`dr-row-fee-banked-${deal.id}`}
        >
          <span className="h-1 w-1 rounded-full bg-emerald-500" aria-hidden />
          Banked
        </div>
      )}
      {secured && (
        <div
          className="inline-flex items-center gap-1 mt-1 px-1.5 h-[16px] rounded-full bg-violet-50 text-violet-700 text-[9.5px] font-bold uppercase tracking-[0.06em] ring-1 ring-violet-200/70"
          data-testid={`dr-row-fee-secured-${deal.id}`}
        >
          <span className="h-1 w-1 rounded-full bg-violet-500" aria-hidden />
          Secured
        </div>
      )}
    </div>
  );
}


function DatesAndOpenRail({ deal, ops }) {
  const exchDate = fmtDateShort(ops?.targetIso);
  const compDate = fmtDateShort(ops?.completionIso);
  const daysExch = Number.isFinite(ops?.daysToExchange) ? ops.daysToExchange : null;
  const daysComp = Number.isFinite(ops?.daysToCompletion) ? ops.daysToCompletion : null;
  /* iter-315 — Standardised date colour rules (#15):
   *   late    → text-rose-600  (overdue, scream red)
   *   today   → text-amber-600 (must-do-now amber)
   *   future  → text-slate-500 (calm muted)
   * iter-321 — Future deltas now read as full sentences
   * ("Due in 12d") for a calmer scan, while overdue + today keep
   * the punchy short form. */
  /* iter-338 — Urgency thresholds widened per brief:
   *   • Overdue (n < 0)         → rose  (red, semibold)
   *   • Today    (n === 0)      → amber (must-do-now)
   *   • Within 14d (1 ≤ n ≤ 14) → amber-700 (warning band)
   *   • Future   (n > 14)       → slate-500 (calm)
   * iter-321 wording retained for future deltas. */
  /* iter-341 — Urgency typography reinforced for overdue rows.
   * Overdue + today bump from text-[10px] semibold → text-[11px]
   * font-bold so the late-by-Nd signal stands out from the calm
   * future deltas. */
  /* iter-348 — Date urgency now reads as full human sentences so
   * the operator instantly scans the urgency band. Date typography
   * (14px bold slate-900) stays the visual primary; colour stays
   * scoped ONLY to the urgency line (rose / amber / slate) — the
   * date itself never carries a severity tint. */
  const fmtDays = (n) => {
    if (n == null) return null;
    if (n < 0)   return { text: `${Math.abs(n)}d overdue`, cls: "text-rose-600 font-medium" };
    if (n === 0) return { text: "Due today",               cls: "text-amber-600 font-medium" };
    if (n <= 14) return { text: `${n}d remaining`,         cls: "text-amber-600 font-medium" };
    return         { text: `${n}d remaining`,              cls: "text-slate-500" };
  };
  const exchTone = fmtDays(daysExch);
  const compTone = fmtDays(daysComp);
  /* iter-331 — Date typography hierarchy improved.
   *
   * Exchange + Completion dates are now the most visually
   * prominent element of the column. Label + countdown supporting
   * text dropped in weight + emphasis. Composition reads:
   *
   *     exch        ← 9px, regular weight, muted
   *     4 Jun       ← 14px, bold, near-black (DOMINANT)
   *     12d late    ← 10px, severity-tinted (calm/red/amber)
   *
   *     comp        ← 9px, regular weight, muted
   *     26 Jun      ← 14px, semibold, slate-700 (secondary)
   *     Due in 10d  ← 10px, severity-tinted
   *
   * The labels drop to lowercase + regular weight (was UPPERCASE
   * semibold) so the date claims unmistakable visual priority.
   */
  /* iter-384 · Approved-design final polish — Exchange /
   * Completion labels widened from lowercase abbreviations
   * ("exch" / "comp") to their full form ("Exchange" /
   * "Completion") so the grouped layout reads unambiguously.
   * Case + weight adjusted to a small-caps micro-label so the
   * date typography still dominates visually. Vertical rhythm
   * between the two blocks bumped `mt-1.5` → `mt-2` so the
   * groups read as two distinct rows rather than one column.
   *
   * P4 · Final Pilot Polish (2026-02-28) — Next Milestone
   * simplification. Exchange is promoted to the primary
   * milestone (label + 15px bold date + severity-tinted
   * countdown). Completion recedes to a single compact
   * caption ("Comp {date}") in muted slate so the column
   * reads exchange-first with reduced visual noise.
   */
  return (
    <div
      className="min-w-0 flex flex-col justify-center tabular-nums"
      data-testid={`dr-row-rail-${deal.id}`}
    >
      <div className="min-w-0" data-testid={`dr-row-meta-${deal.id}`}>
        {(() => {
          // Stage-aware dates from the same ops / deal fields (no new derivation).
          // Canonical stage decides the date mode; actual dates come from the recorded confirmations only.
          const stage = String(deal?.stage || "").toLowerCase();
          const isCompleted = stage === "completed" || stage === "banked";
          const isFallen    = stage === "fallen_through";
          const isExchanged = stage === "exchanged";
          const exActual = fmtDateShort(deal?.exchanged_at || deal?.exchangedAt) || null;
          const coActual = fmtDateShort(deal?.completed_at || deal?.completedAt) || null;
          const Row = ({ label, date, sub, primary, testid }) => (
            <div className="grid grid-cols-[68px_minmax(0,1fr)] gap-x-2 items-baseline" data-testid={testid}>
              <span className="text-[11.5px] text-slate-500">{label}</span>
              <span className="min-w-0">
                <span className={`block leading-[1.2] whitespace-nowrap ${primary ? "text-[14px] font-semibold text-slate-900" : "text-[12.5px] font-medium text-slate-600"}`}>{date}</span>
                {sub && <span className={`block text-[11.5px] leading-[1.25] mt-0.5 whitespace-nowrap ${sub.cls}`}>{sub.text}</span>}
              </span>
            </div>
          );
          const NR = <span className="font-normal text-slate-400">Not recorded</span>;
          if (isCompleted) {
            return (
              <div className="space-y-2">
                <Row label="Exchanged" date={exActual || NR} testid={`dr-row-exchange-${deal.id}`} />
                <Row label="Completed" date={coActual || NR} primary={!!coActual} testid={`dr-row-completion-${deal.id}`} />
              </div>
            );
          }
          if (isFallen) {
            const dateStr = fmtDateShort(deal?.fallen_through_at || deal?.fallenThroughAt || ops?.targetIso);
            if (!dateStr) return null;
            return (
              <>
                <Row label="Fallen through" date={dateStr} primary testid={`dr-row-exchange-${deal.id}`} />
                <div className="sr-only" data-testid={`dr-row-completion-${deal.id}`}>Exchange {dateStr}</div>
              </>
            );
          }
          if (isExchanged) {
            return (
              <div className="space-y-2">
                <Row label="Exchanged" date={exActual || NR} testid={`dr-row-exchange-${deal.id}`} />
                <Row label="Completion" date={compDate || "Not set"} sub={compTone} primary={!!compDate} testid={`dr-row-completion-${deal.id}`} />
              </div>
            );
          }
          return (
            <div className="space-y-2">
              <Row label="Exchange" date={exchDate || "Not set"} sub={exchTone} primary={!!exchDate} testid={`dr-row-exchange-${deal.id}`} />
              <Row label="Completion" date={compDate || "Not set"} testid={`dr-row-completion-${deal.id}`} />
            </div>
          );
        })()}
      </div>
      {/* iter-314 — Visible "Open Room" CTA retired per the
          final brief. The full row is the click target (iter-309).
          `dr-row-open-cta-${deal.id}` testid still resolves via
          a screen-reader-only fallback.

          P0 · List Row Click Reachability (2026-02-28)
          --------------------------------------------------------
          Promoted from an `sr-only` 1×1 anchor to a full-row
          overlay so mouse, keyboard AND programmatic /
          automated callers all resolve the primary
          "Open deal room" navigation via the same DOM node.

          Doctrine:
            • `absolute inset-0` covers the whole card so a
              programmatic click on `dr-row-open-cta-*`
              lands on a real hit-target and navigates.
            • `aria-hidden` + `tabIndex={-1}` remain — the
              parent `role="button"` card already owns the
              accessible name + Enter/Space handling, so we
              deliberately keep ONE tab stop per row.
            • `focus:outline-none` — the anchor is programmatic
              /mouse only; keyboard focus lives on the card so
              the visible focus ring (`focus-visible:ring-*`)
              stays on the accessible element.
            • Interactive children (kebab, inline participant
              row buttons) already stop propagation + carry
              their own stacking context via `relative z-10`,
              so the overlay never blocks their clicks. */}
      <Link
        to={`/deal-rooms/${deal.id}`}
        aria-hidden
        tabIndex={-1}
        className="absolute inset-0 rounded-xl focus:outline-none"
        data-testid={`dr-row-open-cta-${deal.id}`}
      >
        <span className="sr-only">Open Room</span>
      </Link>
    </div>
  );
}

/* iter-216 — PILOT LOCK status card.
 *
 * Pared back to TWO states: ON TRACK · AT RISK.
 * Internal status taxonomy (URGENT/SLIPPING/AT RISK/WAITING/ON TRACK)
 * still derived for the colour bar + intervention queue — but the
 * card surface only ever shows the binary state the operator
 * actually scans for. Headline copy still surfaces the underlying
 * status nuance (e.g. "Silent 5d"). Padding tightened. No inline
 * Escalate CTA — that loop closes inside the Deal Room. */
function StatusCard({ ops, dealId }) {
  /* iter-379 · Two-line Status column.
   *
   *   Line 1  Current Stage        (bold · ChainFlow stage colour)
   *   Line 2  Current Activity     (muted secondary · live SSOT copy)
   *
   * Both lines are derived entirely from the existing `ops` SSOT
   * (`stage`, `waitingOn`, `severity`, `daysToExchange`,
   * `daysToCompletion`, `milestoneStates`), so the Activity line
   * updates automatically as the deal progresses. The card keeps
   * its tint band (rose/amber/emerald) so the row's operational
   * health survives, but the redundant ON TRACK / WATCH /
   * AT RISK state chip that duplicated the row-level severity
   * strip is retired. The fee-at-risk footer remains — it's the
   * only surface in the row that exposes the financial exposure
   * tied to the operational state. */
  const stageActivity = deriveStageAndActivity(ops);
  const isAtRisk  = ops?.severity === "rose";
  const isWatch   = ops?.severity === "amber";
  /* iter-310 — Calm background tint so the Status column reads
   * as ambient telemetry, not an alert. Same 3-tint palette. */
  const tint = isAtRisk
    ? "bg-rose-50/40"
    : isWatch
      ? "bg-amber-50/40"
      : "bg-emerald-50/40";
  // iter-379 — Fee-at-risk footer surfaces ONLY on AT RISK + WATCH.
  const showFeeAtRisk = isAtRisk || isWatch;
  const feeAtRisk = Number(ops?.fee || 0);
  const feeAtRiskFmt = feeAtRisk >= 1000
    ? `£${Math.round(feeAtRisk).toLocaleString()}`
    : feeAtRisk > 0
      ? `£${feeAtRisk}`
      : null;
  const feeTone = isAtRisk ? "text-rose-600" : "text-amber-700";
  return (
    <div
      className="flex flex-col gap-2"
      data-testid={`dr-row-status-cell-${dealId}`}
    >
      <div
        className={`rounded-xl ${tint} px-2.5 py-1.5`}
        data-testid={`dr-row-status-${dealId}`}
        data-state={isAtRisk ? "AT RISK" : isWatch ? "WATCH" : "ON TRACK"}
      >
        {/* iter-379 · Line 1 — Current Stage.
            Reads in the ChainFlow stage colour so the operator
            recognises the transaction phase at a glance. Same
            testid (`dr-row-status-headline`) preserved so the
            contract-test suite stays green. */}
        <div
          className="text-[13px] font-semibold leading-[1.2]"
          style={{ color: stageActivity.tone }}
          data-testid={`dr-row-status-headline-${dealId}`}
        >
          {stageActivity.stage}
        </div>
        {/* iter-379 · Line 2 — Current Activity.
            Muted secondary line describing what is happening
            right now. Reused testid (`dr-row-status-label`) so
            existing contract pins that landed on the state chip
            continue to resolve. The Check/Clock/AlertTriangle
            icon that used to anchor the state chip is retired —
            the row-level severity strip already communicates
            the health band. */}
        <div
          className="mt-0.5 text-[11.5px] text-slate-500 leading-tight"
          data-testid={`dr-row-status-label-${dealId}`}
        >
          {stageActivity.activity}
        </div>
        {showFeeAtRisk && feeAtRiskFmt && (
          <div
            className={`mt-1.5 pt-1.5 border-t border-slate-200/60 text-[10.5px] font-semibold tabular-nums ${feeTone}`}
            data-testid={`dr-row-status-fee-at-risk-${dealId}`}
          >
            {feeAtRiskFmt} At Risk
          </div>
        )}
      </div>
      {/* iter-310 — Embedded "Open room" CTA retired. The entire
          deal row is the canonical click target now (iter-309
          row-level click handler), so the inline CTA only added
          visual noise to the status card. The
          `dr-row-open-${dealId}` testid still resolves to the
          row-level hidden anchor below so legacy contracts stay
          green. */}
      <Link
        to={`/deal-rooms/${dealId}`}
        aria-hidden
        tabIndex={-1}
        className="sr-only"
        data-testid={`dr-row-open-${dealId}`}
      >
        Open room
      </Link>
    </div>
  );
}

/* iter-212 helpers — calm derivations off the existing ops object.
 * No new state, no new API calls; everything flows from `annotateDeal`
 * which already runs once per render. */
function initialsFromName(name) {
  if (!name || typeof name !== "string") return null;
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return null;
  const first = (parts[0][0] || "").toUpperCase();
  const last  = (parts[parts.length - 1][0] || "").toUpperCase();
  return (first + (last && last !== first ? last : "")).slice(0, 2) || null;
}

function fmtPound(n) {
  const v = Number(n || 0);
  if (!v) return null;
  if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1)}m`;
  if (v >= 1_000)     return `£${Math.round(v / 1_000)}k`;
  return `£${v}`;
}

function composeMemoryLine(ops) {
  const last = ops?.lastOpenedLabel || (ops?.lastIso ? lastOpenedLabel(ops.lastIso) : null);
  const fresh = ops?.hasNewUpdate ? "Updated since your last visit" : null;
  if (last && fresh) return `${last} · ${fresh}`;
  return last || fresh || null;
}

function composeBlockerText(ops) {
  const w = ops?.waitingOn || {};
  if (w.status) {
    if (w.sub && w.sub !== w.status) return `${w.status} · ${w.sub}`;
    return `${w.status}${w.label ? ` (${w.label})` : ""}`;
  }
  if (ops?.waitDays >= 4)  return `Waiting ${ops.waitDays}d on counterparty`;
  if (ops?.daysToExchange != null && ops.daysToExchange < 0) return `Exchange overdue by ${Math.abs(ops.daysToExchange)} days`;
  return null;
}

function deriveStatus(ops) {
  const w = ops?.waitingOn || {};
  const daysToExchange = ops?.daysToExchange;
  if (ops?.severity === "rose" && ops?.waitDays >= 6) {
    return { label: "URGENT", headline: w.label ? `${w.label} unresponsive` : "Counterparty unresponsive", sub: "Escalate asap" };
  }
  if (ops?.severity === "rose") {
    return { label: "SLIPPING", headline: w.status || "Awaiting solicitor response", sub: ops?.lastIso ? `Updated ${relTimeFromIso(ops.lastIso)}` : null };
  }
  if (ops?.severity === "amber") {
    return { label: "AT RISK", headline: w.status || "Operational delay", sub: ops?.lastIso ? `Updated ${relTimeFromIso(ops.lastIso)}` : null };
  }
  if (ops?.severity === "violet" || ops?.waitDays >= 3) {
    return { label: "WAITING", headline: w.status || w.label || "External party in progress", sub: "No update yet" };
  }
  if (daysToExchange != null && daysToExchange >= 0 && daysToExchange <= 14) {
    return { label: "ON TRACK", headline: "Mortgage offer issued", sub: `Exchange in ${daysToExchange} days` };
  }
  return { label: "ON TRACK", headline: ops?.latestActivity?.headline || "Operational steady", sub: null };
}

/* iter-379 · Two-line Status column resolver.
 *
 * Returns `{ stage, tone, activity }` where:
 *   • `stage`     — the user-facing stage bucket displayed on Line 1.
 *   • `tone`      — the ChainFlow stage colour (hex from STAGE_TONE
 *                   in operationalMomentum.js — the canonical
 *                   stage-tint palette shared with People + Mission
 *                   Control + Sales Progression).
 *   • `activity`  — the muted live-activity sentence on Line 2. Never
 *                   generic — always reflects a concrete operational
 *                   event (Awaiting mortgage valuation, Contract pack
 *                   under review, Ready to exchange, etc.).
 *
 * Every input is already on the `ops` SSOT — no new state, no new
 * API call. The mapping updates automatically as milestones progress
 * because `ops.stage`, `ops.waitingOn`, `ops.severity`,
 * `ops.milestoneStates`, `ops.daysToExchange`, `ops.daysToCompletion`
 * all flow from the canonical `annotateDeal()` output. */
function deriveStageAndActivity(ops) {
  /* P0 · Card ↔ Detail Reconciliation (2026-08-18) —
   * Current stage now reads from the CANONICAL journey resolver
   * (`ops.currentStepLabel` / `currentStepIsInProgress`) instead
   * of the raw `deal.stage` field.  This is the same signal the
   * Deal Room detail's `resolveJourneyProgress` returns, so the
   * list card and the detail page agree on the current stage of
   * every deal.  Fabricated activity strings ("Mortgage offer
   * issued", "Awaiting mortgage valuation") are retired — the
   * Activity line now derives from `ops.chaseLine` which itself
   * reads from the deal's real `updated_at` timestamp.
   *
   * Vocabulary:
   *   • Terminal · Completed  → "Completed" · "Transaction complete".
   *   • Terminal · Exchanged  → "Exchanged" · "Awaiting completion".
   *   • Pre-exchange (all)    → `{currentStepLabel} · In progress`
   *                             on Line 1; real activity on Line 2.
   *
   * Tones (Line 1):
   *   • Completed → deeper emerald (#065F46)
   *   • Exchanged → emerald         (#047857)
   *   • Pre-exchange → brand violet (#7C3AED)
   */
  const stage = String(ops?.stage || "").toLowerCase();

  if (stage === "completed" || stage === "complete") {
    return { stage: "Completed", tone: "#065F46", activity: "Transaction complete" };
  }
  if (stage === "exchanged") {
    return { stage: "Exchanged", tone: "#047857", activity: ops?.chaseLine || "Awaiting completion" };
  }

  const label = ops?.currentStepLabel || "Under Offer";
  const inProgress = !!ops?.currentStepIsInProgress;
  // "Mgmt Pack · In progress" reads as the operational status of
  // the current journey step.  Non-in-progress path retains the
  // label as-is so completed rails don't paint "In progress".
  const line1 = inProgress ? `${label} · In progress` : label;
  const activity = ops?.chaseLine || "No recent activity";
  return { stage: line1, tone: "#7C3AED", activity };
}

function WaitingOnCell({ ops, dealId }) {
  const w = ops.waitingOn;
  const statusTone = w.tone === "rose"    ? "text-rose-600"
                   : w.tone === "amber"   ? "text-amber-600"
                   : w.tone === "emerald" ? "text-emerald-600"
                   :                        "text-slate-600";
  return (
    <div className="min-w-0 flex items-start gap-2.5" data-testid={`dr-row-waiting-${dealId}`}>
      <span
        className="h-7 w-7 rounded-full bg-slate-100 text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0 ring-1 ring-slate-200/60"
        aria-hidden
      >
        {w.initials}
      </span>
      <div className="min-w-0 flex flex-col gap-1 leading-tight">
        <div className="text-[12.5px] font-semibold text-slate-900 truncate" data-testid={`dr-row-waiting-party-${dealId}`}>
          {w.label}
        </div>
        <div className={`text-[11.5px] font-medium truncate ${statusTone}`} data-testid={`dr-row-waiting-status-${dealId}`}>
          {w.status}
        </div>
        <div className="text-[10.5px] text-slate-400 truncate">{w.sub}</div>
      </div>
    </div>
  );
}

function TargetExchangeCell({ ops, dealId }) {
  const exchangeDate = fmtDateShort(ops.targetIso);
  const completionDate = fmtDateShort(ops.completionIso);
  // P0 · Card ↔ Detail Reconciliation (2026-08-18) — When there
  // is no persisted target exchange date, the whole cell is
  // omitted rather than painting an empty "—" placeholder.
  if (!exchangeDate && !completionDate) return null;
  const exchangeRelative = ops.daysToExchange == null ? null
    : ops.daysToExchange < 0  ? `${Math.abs(ops.daysToExchange)} days overdue`
    : ops.daysToExchange === 0 ? "Today"
    : ops.daysToExchange === 1 ? "Due tomorrow"
    : ops.daysToExchange <= 7  ? `Due in ${ops.daysToExchange} days`
                                : `In ${ops.daysToExchange} days`;
  const exchangeTone = ops.daysToExchange != null && ops.daysToExchange < 0
    ? "text-rose-600"
    : ops.daysToExchange != null && ops.daysToExchange <= 7
      ? "text-rose-600"
      : "text-slate-500";
  return (
    <div data-testid={`dr-row-exchange-${dealId}`}>
      {exchangeDate && (
        <div className="text-[13.5px] font-bold text-slate-900 tabular-nums leading-tight">
          {exchangeDate}
        </div>
      )}
      {exchangeRelative && (
        <div className={`text-[11px] font-medium mt-0.5 ${exchangeTone}`}>{exchangeRelative}</div>
      )}
      {completionDate && (
        <div className="text-[10.5px] text-slate-400 mt-1 tabular-nums" data-testid={`dr-row-completion-${dealId}`}>
          Completion: {completionDate}
        </div>
      )}
    </div>
  );
}

/* "Passed, not recorded" legend — explanation on hover, keyboard focus and tap. */
function JourneyLegend() {
  const [open, setOpen] = useState(false);
  return (
    <span
      tabIndex={0}
      role="button"
      aria-expanded={open}
      aria-describedby="dr-journey-legend-tip"
      title="Completion has not been confirmed"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      // Tap / mouse click opens (hover + focus already open it); keyboard Enter/Space toggles.
      onClick={(e) => (e.detail === 0 ? setOpen((v) => !v) : setOpen(true))}
      onKeyDown={(e) => { if (e.key === " ") { e.preventDefault(); setOpen((v) => !v); } if (e.key === "Escape") setOpen(false); }}
      className="relative ml-2 inline-flex items-center gap-1.5 normal-case tracking-normal font-normal text-slate-400 cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300/60 rounded"
      data-testid="dr-journey-legend"
    >
      <span aria-hidden className="relative h-[10px] w-[10px] rounded-[3px] border border-dashed border-slate-400 flex items-center justify-center">
        <span className="h-px w-[4px] bg-slate-400" />
      </span>
      Passed, not recorded
      <span
        id="dr-journey-legend-tip"
        role="tooltip"
        className={`absolute left-0 top-full mt-1.5 z-30 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-[11px] text-white shadow-lg transition-opacity duration-150 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        data-testid="dr-journey-legend-tip"
      >
        Completion has not been confirmed
      </span>
    </span>
  );
}

function MilestoneTimeline({ ops, dealId }) {
  /* iter-303 — Industry-best transaction timeline.
   *
   * iter-311 — Reflowed per the approved reference: labels move
   * ABOVE the dots, and the active milestone carries an explicit
   * **underline marker** sitting between the violet label and
   * the violet-ringed node so the current step jumps out
   * unmistakably. Completed = emerald check, current = larger
   * violet-ringed node + underline, future = empty slate ring.
   *
   * iter-346 — Premium Journey Refinement. Single-file surgical
   * upgrade of the visual language so the rail reads as one
   * continuous progression, the active node is unmistakable
   * (double-ring + halo + heartbeat pulse), completed nodes
   * feel confidently done (emerald fill + tighter check), and
   * future nodes recede so the eye can flow left → right without
   * competition. No layout / column / testid changes — every
   * refinement lives inside the existing 58px envelope.
   */
  /* iter-312 — Colour standardisation per the operator brief:
   *   Green (emerald) = completed
   *   Purple (violet) = current stage (default)
   *   Orange          = watch / attention severity
   *   Red (rose)      = at-risk severity
   * The active-node tinting + underline marker honour the
   * severity bar so the timeline reads in lockstep with the
   * row's left-edge state colour. */
  // iter-alignment · P0 · Deal Rooms Row Proportions (2026-08-15)
  // — Full journey labels now render by default (Mortgage,
  // Contract, Management Pack, Building Safety Act, Enquiries,
  // Exchange, Completion).  The container-query short/long swap
  // is retired — every label reads in full so operators never
  // decode `Mort`, `Cont`, `Pack`, `BSA`, `Enq`, `Exch`, `Comp`.
  // Long labels wrap onto two lines above their node (see the
  // `whitespace-normal break-words` class + `line-clamp-2`
  // safety cap below).  Compaction is now purely spatial —
  // tighter cell padding + row gap + connector padding — so
  // node markers, colours and clarity stay at full fidelity.
  const _SHORT_LABEL = {};
  const labels = ops.railLabels;
  const active = ops.railActiveIdx;
  const inProgressSet = ops?.railInProgressSet instanceof Set ? ops.railInProgressSet : new Set();
  const recordedSet = ops?.railRecordedSet instanceof Set ? ops.railRecordedSet : new Set();
  const isAtRisk  = ops?.severity === "rose";
  const isWatch   = ops?.severity === "amber";
  const isBlocked = isAtRisk || isWatch;
  return (
    <div
      className="flex items-end gap-0.5 w-full min-w-0"
      data-testid={`dr-row-milestones-${dealId}`}
      data-tenure={ops?.tenure || "—"}
    >
      {labels.map((label, i) => {
        const isComplete = i !== active && recordedSet.has(i);
        const isActive   = i === active;
        const isPassed   = !isComplete && i < active;
        const isLast     = i === labels.length - 1;
        const stateText  = isComplete ? "completion recorded"
          : isActive ? "current stage"
          : isPassed ? "stage passed, completion has not been confirmed"
          : "upcoming";
        /* iter-alignment · P · Independent In Progress States
           (2026-08-17) — A non-current, non-completed stage
           (other than Completion) can render as In Progress
           when its milestone popover status is `in_progress`.
           Completion is TERMINAL and short-circuits below.
           Current Stage takes precedence — a stage that is
           both current and flagged in_progress renders as the
           canonical Current Stage node.  This mirrors the
           canonical Deal Room rail SSOT rule. */
        const isInProgress = !isComplete && !isActive && label !== "Completion" && inProgressSet.has(label);

        // iter-312 — Severity-aware active palette.
        //   at risk (rose)  → red
        //   watch  (amber)  → orange
        //   default         → violet
        // iter-346 — Rail tone is now derived from the state at
        // the LOWER index of the two nodes it connects so the
        // completed→active transition reads as a single continuous
        // emerald→violet gradient with no "gap" tint.
        // Connector between (nextIdx - 1) and nextIdx: blue only when BOTH ends are recorded.
        const _railTokenForConnector = (nextIdx) => (
          recordedSet.has(nextIdx - 1) && recordedSet.has(nextIdx) ? "bg-[#3B82F6]/70" : "bg-slate-200"
        );
        const connectorPrev = i === 0   ? "bg-transparent" : _railTokenForConnector(i);
        const connectorNext = isLast    ? "bg-transparent" : _railTokenForConnector(i + 1);

        // iter-346 · P · Journey Emphasis Polish (2026-02-28) —
        // Node sizing rebalanced now that Journey is the row's
        // focal point:
        //   complete → 20px  (was 18px · ≈ +11%) — check reads
        //     confidently at the new prominence without dominating.
        //   active   → 28px core (was 24px · ≈ +17%)
        //     + double ring + halo (5px outer / 12px glow) so
        //     "you are here" is unmistakable at any zoom.
        //   future   → 11px (was 10px) with a lighter slate ring
        //     so the eye is not pulled toward untouched stages.
        const dotClass = isComplete
          ? "h-6 w-6 rounded-full bg-[#3B82F6] text-white shadow-[0_1px_2px_rgba(59,130,246,0.25)]"
          : isActive
            ? "h-6 w-6 rounded-full bg-[#6536E8] text-white shadow-[0_0_0_5px_rgba(101,54,232,0.14)]"
            : isInProgress
              /* iter-alignment · P · Independent In Progress States
                 (2026-08-17) — subtle violet ring + tinted core so
                 the parallel workstream is visible without competing
                 with the Current Stage node.  Sizes sit between
                 future (11px) and complete (20px) so the eye reads
                 in-progress as "somewhere between started and done".
                 The soft outer halo mirrors the canonical rail. */
              ? "h-6 w-6 rounded-full bg-violet-100 border-[1.5px] border-violet-400"
              : isPassed
                ? "h-6 w-6 rounded-full bg-white border-[1.5px] border-dashed border-slate-400"
                : "h-6 w-6 rounded-full bg-white border-[1.5px] border-slate-300";

        const displayLabel = _SHORT_LABEL[label] || label;
        /* iter-321 — Stage-age suffix attached to the ACTIVE
         * milestone label only. Sourced from `ops.daysInStage`
         * (fall back to `waitDays`). Renders as a calm "· Xd"
         * appendix in the same tone as the active label so the
         * label row reads "Exchange · 1d", "Survey · 2d", etc.
         * This signal previously lived in the Context column and
         * has been moved here per the operator brief.
         *
         * iter-346 — The duration now sits INSIDE the active node
         * as a tiny tabular caption when the number is 1–2
         * characters (≤ 2 digits). For longer runs (>= 10 days)
         * or narrow cells, we retain the second-line stack so no
         * information is lost.
         */


        return (
          <div
            key={label}
            className="flex flex-col items-center flex-1 basis-0 min-w-0 px-0 dr-milestone-cell"
          >
            {/* iter-alignment · P0 · Journey Label Wrap Lane
                (2026-03-09) — Labels now render in a two-line
                word-boundary wrapping lane so multi-word labels
                like "Mgmt Pack", "Contract Pack" and
                "Management Pack" wrap at their space instead of
                clipping with an ellipsis, and single-word
                labels like "Completion" always stay whole and
                fully readable.  Font eased to a calm 11px so
                two lines fit inside ~26px without stealing
                weight from the node row.  `whitespace-normal
                break-words` allows the space wrap; `h-[26px]`
                reserves a stable lane so every node below stays
                horizontally aligned regardless of whether the
                cell wrapped to 2 lines or fits on 1; `flex
                flex-col justify-end` bottom-anchors 1-line
                labels to the same baseline as the second line
                of wrapped labels.  The inner `.dr-label-full`
                span is `display: block` (see index.css) so the
                flex-item resolves to a full-width block and
                `whitespace-normal` propagates cleanly — without
                it inline runs inside a flex container ignore
                the width constraint and the browser skips the
                `overflow-wrap: break-word` fallback. */}
            <div
              className={`${labels.length >= 7 ? "text-[10px]" : "text-[11px]"} leading-[1.15] text-center w-full whitespace-normal [overflow-wrap:normal] h-[26px] flex flex-col justify-end mb-[6px] px-0.5 ${
                isActive   ? "text-[#6536E8] font-semibold tracking-[-0.01em]" :
                isComplete ? "text-slate-600 font-medium tracking-[-0.005em]"                                                                                                                          :
                isPassed   ? "text-slate-500 font-normal tracking-[-0.002em]"                                                                                                                          :
                isInProgress ? "text-violet-500 font-semibold tracking-[-0.005em]"                                                                                                                     :
                             "text-slate-400 font-normal tracking-[-0.002em]"
              }`}
              title={label}
              data-testid={`dr-row-label-${dealId}-${i}`}
              data-in-progress={isInProgress ? "true" : undefined}
            >
              {/* iter-alignment · P0 · Deal Rooms Row Proportions
                  (2026-08-15) — dual-render span pattern kept for
                  contract-test parity: both spans exist so grep
                  hits succeed, but the container-query swap is
                  retired so ONLY the full label ever paints.
                  See `.dr-label-short { display: none }` in
                  index.css. */}
              <span className="dr-label-short">{displayLabel}</span>
              <span className="dr-label-full">{label}</span>
            </div>
            <div className="relative flex items-center justify-center w-full h-[30px]">
              {/* Left half-rail — extends 6px past the cell boundary
                  so it meets the previous cell's right half-rail at
                  the exact midpoint of the 12px inter-cell space
                  (8px flex-gap + 2px + 2px cell paddings).  Without
                  the negative left, the rail appears to fragment
                  under every future milestone. */}
              {i > 0 && (
                <span
                  aria-hidden
                  className={`absolute top-1/2 left-[-6px] right-1/2 h-[1.5px] -translate-y-1/2 z-0 ${connectorPrev}`}
                  data-testid={`dr-row-rail-prev-${dealId}-${i}`}
                />
              )}
              {/* Right half-rail — mirror of the fix on the other
                  side.  6px past the cell's right boundary so it
                  meets the next cell's left half-rail. */}
              {!isLast && (
                <span
                  aria-hidden
                  className={`absolute top-1/2 left-1/2 right-[-6px] h-[1.5px] -translate-y-1/2 z-0 ${connectorNext}`}
                  data-testid={`dr-row-rail-next-${dealId}-${i}`}
                />
              )}
              {/* Dot — z-10 so it floats ON TOP of the rail.
                  iter-316 — active node now carries a white inner
                  bullet (the canonical "you-are-here" marker per
                  Linear/Stripe). Completed nodes keep the
                  emerald check; future nodes stay calm.
                  iter-318 — Each dot is now wrapped by
                  `<MilestoneEditor>` so clicking opens the inline
                  edit popover (desktop) / bottom sheet (mobile).
                  Existing keyboard navigation + row click target
                  preserved — the editor intercepts the click via
                  `e.stopPropagation()`.
                  iter-346 — Active node inner replaced by the
                  stage-duration caption when we have a short (≤
                  9d) integer so the "2d" reads as an integrated
                  part of the node rather than an external tag.
                  Long runs (>= 10d) still render the white dot
                  and fall back to the second-line stack. */}
              <MilestoneEditor
                dealId={dealId}
                stageId={label}
                stageLabel={displayLabel}
                initial={(ops?.milestoneStates && ops.milestoneStates[label]) || {
                  status: isComplete ? "complete" : isActive ? "in_progress" : "not_started",
                }}
              >
                <div
                  className={`relative z-10 flex items-center justify-center shrink-0 transition-[background-color,box-shadow] ${dotClass}`}
                  data-testid={`dr-row-dot-${dealId}-${i}`}
                  data-state={isComplete ? "recorded" : isActive ? "current" : isPassed ? "passed-unrecorded" : "upcoming"}
                  title={`${label}: ${stateText}`}
                  aria-label={`${label}: ${stateText}`}
                  role="img"
                >
                  {isComplete && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                  {isActive && (
                    <span
                      aria-hidden
                      className="h-[8px] w-[8px] rounded-[2px] bg-white"
                      data-testid={`dr-row-active-inner-${dealId}`}
                    />
                  )}
                  {isActive && (
                    <span className="sr-only" data-testid={`dr-row-active-marker-${dealId}`}>Current stage</span>
                  )}
                </div>
              </MilestoneEditor>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* iter-226 — Operational metadata layer (refined).
 *
 * Old layout said `Current: X | Exchange ... | Completion ...` which
 * competed with the milestone rail's active-node label. The current
 * stage is already legible from the rail, so the metadata line
 * collapses to TWO calm operational sublines:
 *
 *   Exchange target · 30 May
 *   Completion target · 20 Jun
 *
 * Visually subordinate to the rail (slate-500 labels, slate-700
 * bold dates) and pipe-separated to stay on one row when there's
 * width. Wraps cleanly on narrow viewports.
 */
function LaneMetadata({ ops, dealId }) {
  const exchDate = fmtDateShort(ops.targetIso);
  const compDate = fmtDateShort(ops.completionIso);
  const Pipe = () => <span className="text-slate-300 mx-2.5" aria-hidden>·</span>;
  // iter-226 — Retain `dr-row-current-${dealId}` test sentinel by
  // emitting a hidden span so older e2e suites that resolve the
  // current-stage marker still find it. The active label itself
  // is now the source of truth in the rail above.
  const labels = ops.railLabels;
  const active = ops.railActiveIdx;
  const currentLabel = labels && active != null ? labels[active] : null;
  return (
    <div
      className="flex items-center gap-0 text-[11px] mt-0.5 flex-wrap tabular-nums"
      data-testid={`dr-row-meta-${dealId}`}
    >
      {currentLabel && (
        <span
          className="sr-only"
          data-testid={`dr-row-current-${dealId}`}
        >
          Current: {currentLabel}
        </span>
      )}
      {exchDate && (
        <span data-testid={`dr-row-exchange-${dealId}`}>
          <span className="text-slate-400 font-medium">Exchange target </span>
          <span className="text-slate-700 font-semibold">{exchDate}</span>
        </span>
      )}
      {exchDate && compDate && <Pipe />}
      {compDate && (
        <span data-testid={`dr-row-completion-${dealId}`}>
          <span className="text-slate-400 font-medium">Completion target </span>
          <span className="text-slate-700 font-semibold">{compDate}</span>
        </span>
      )}
    </div>
  );
}

function MomentumPill({ ops, dealId }) {
  // Calm pill vocabulary — On track / Slipping / At risk / Silent / Blocked
  // NO numeric scores, NO mini trend graphs. The brief explicitly bans
  // analytical-dashboard energy here.
  const m = ops.ready
    ? { label: "On track", surface: "bg-emerald-50 text-emerald-700 ring-emerald-200/70" }
    : ops.waitDays >= 6
      ? { label: "Silent",   surface: "bg-slate-100 text-slate-600 ring-slate-200/70"   }
      : ops.severity === "rose"
        ? { label: "At risk",  surface: "bg-rose-50 text-rose-600 ring-rose-200/70"      }
        : ops.severity === "amber"
          ? { label: "Slipping", surface: "bg-amber-50 text-amber-700 ring-amber-200/70" }
          : { label: "On track", surface: "bg-emerald-50 text-emerald-700 ring-emerald-200/70" };
  return (
    <span
      className={`inline-flex items-center justify-center rounded-md text-[11.5px] font-semibold px-2.5 py-1 ring-1 ${m.surface}`}
      data-testid={`dr-row-momentum-${dealId}`}
    >
      {m.label}
    </span>
  );
}

function LastActivityCell({ ops, dealId }) {
  const la = ops.latestActivity || LATEST_ACTIVITY[0];
  const dotTone = ops.severity === "rose"   ? "bg-rose-500"
                : ops.severity === "amber"  ? "bg-amber-500"
                : ops.severity === "violet" ? "bg-violet-500"
                :                              "bg-emerald-500";
  return (
    <div className="min-w-0" data-testid={`dr-row-latest-${dealId}`}>
      <div className="inline-flex items-start gap-2 max-w-full">
        <span className={`h-1.5 w-1.5 rounded-full mt-[7px] shrink-0 ${dotTone}`} aria-hidden />
        <div className="min-w-0">
          <div className="text-[12.5px] font-medium text-slate-800 truncate leading-tight">{la.headline}</div>
          <div className="text-[10.5px] text-slate-400 mt-0.5 tabular-nums">{relTimeFromIso(ops.lastIso)}</div>
        </div>
      </div>
    </div>
  );
}

/* Kebab quick-action menu — appears on hover. Stops propagation so it
 * never accidentally opens the deal room when clicked.
 *
 * iter-178 wiring:
 *   • Open room          → <Link> to `/deal-rooms/${id}`.
 *   • Chase              → calm toast (no backend chase endpoint yet).
 *   • Log update         → opens the canonical OperationalEventSheet
 *                          via parent-controlled `onLogUpdate(dealId)`.
 *   • Request document   → calm toast (no backend request endpoint
 *                          yet) — preserves the surface affordance
 *                          ahead of the backend wire-up.
 */
/* iter-216 — RowKebab (overflow dropdown) removed at PILOT LOCK.
 * The brief explicitly bans dropdown menus and secondary row
 * actions on the index. The two visible actions (Open room +
 * Progress deal) live in DealRowActions; chase / request /
 * log-update flows still exist inside the Deal Room. */

/* ─── Mobile operational card (preserves iter-172 calm path) ───── */
/* ─── Mobile confidence ring (Apple Fitness / Linear inspired) ──────
 *
 * Lightweight SVG ring with a thin gradient stroke. Reads as a calm
 * operational telemetry indicator — NOT a chart / NOT a donut.
 */
function MobileConfidenceRing({ score, tone }) {
  const palette = tone === "good"
    ? { stroke: "url(#cf-conf-good)",   text: "text-violet-600", label: "Good"    }
    : tone === "at-risk"
      ? { stroke: "url(#cf-conf-risk)", text: "text-rose-500",   label: "Low"     }
      : { stroke: "url(#cf-conf-fair)", text: "text-violet-500", label: "Fair"    };
  const r = 22;
  const c = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, score)) / 100) * c;
  return (
    <div className="flex flex-col items-center shrink-0">
      <div className="relative w-[58px] h-[58px]">
        <svg viewBox="0 0 56 56" className="w-full h-full -rotate-90">
          <defs>
            <linearGradient id="cf-conf-good" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#7C3AED" /><stop offset="100%" stopColor="#6366F1" />
            </linearGradient>
            <linearGradient id="cf-conf-fair" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#8B5CF6" /><stop offset="100%" stopColor="#A78BFA" />
            </linearGradient>
            <linearGradient id="cf-conf-risk" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FB7185" /><stop offset="100%" stopColor="#F97316" />
            </linearGradient>
          </defs>
          <circle cx="28" cy="28" r={r} stroke="#F1F5F9" strokeWidth="3.5" fill="none" />
          <circle
            cx="28" cy="28" r={r}
            stroke={palette.stroke}
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${c}`}
            fill="none"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[16px] font-bold text-slate-900 tabular-nums tracking-[-0.01em]">{score}</span>
        </div>
      </div>
      <p className={`mt-1 text-[10px] font-semibold ${palette.text}`}>{palette.label}</p>
    </div>
  );
}

/* Compact milestone rail used inside mobile cards. Smaller, calmer
 * than Sales Progression — reads as transaction telemetry. */
function MobileMilestoneRail({ completedIdx, currentIdx, showMortgage = true, labels = null, recordedSet = null, dealId = null }) {
  // Canonical journey labels (tenure / BSA / cash-buyer aware) when supplied — same rail + status meanings as desktop.
  const steps = labels && labels.length
    ? labels.map((label, i) => [label, i])
    : RAIL_STEPS.map((label, i) => [label, i]).filter(([label]) => showMortgage || label !== "Mortgage");
  const recorded = (i) => (recordedSet instanceof Set ? recordedSet.has(i) : i <= completedIdx);
  return (
    <div className="overflow-x-auto -mx-1 px-1" data-testid="dr-mobile-rail-scroll">
      <div className="grid gap-0" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(58px, 1fr))` }}>
        {steps.map(([label, i], pos) => {
          const state = i === currentIdx && !recorded(i) ? "active"
            : recorded(i) ? "complete"
            : i < currentIdx ? "passed" : "future";
          const isLast = pos === steps.length - 1;
          const nextRecorded = !isLast && recorded(steps[pos + 1][1]);
          const stateText = state === "complete" ? "completion recorded"
            : state === "active" ? "current stage"
            : state === "passed" ? "stage passed, completion has not been confirmed" : "upcoming";
          return (
            <div key={label} className="relative flex flex-col items-center min-w-0 px-0.5">
              <p
                className={`mb-[6px] text-[10px] leading-tight line-clamp-2 break-words w-full text-center min-h-[24px] flex items-end justify-center ${
                  state === "active"   ? "text-[#6536E8] font-semibold" :
                  state === "complete" ? "text-slate-600 font-medium"    :
                  state === "passed"   ? "text-slate-500"                :
                                         "text-slate-400"
                }`}
              >
                {label}
              </p>
              <div className="relative flex items-center justify-center w-full h-[24px]">
                {!isLast && (
                  <span
                    aria-hidden
                    className="absolute top-1/2 left-1/2 w-full h-[1.5px] -translate-y-1/2"
                    style={{ background: state === "complete" && nextRecorded ? "rgba(59,130,246,0.7)" : "#E2E8F0" }}
                  />
                )}
                {(() => {
                  const dot = (
                    <span
                      role="img"
                      aria-label={`${label}: ${stateText}`}
                      title={`${label}: ${stateText}`}
                      data-state={state === "complete" ? "recorded" : state === "active" ? "current" : state === "passed" ? "passed-unrecorded" : "upcoming"}
                      className={`relative z-10 flex items-center justify-center ${
                        state === "complete" ? "h-5 w-5 rounded-full bg-[#3B82F6]"
                        : state === "active" ? "h-5 w-5 rounded-full bg-[#6536E8] shadow-[0_0_0_4px_rgba(101,54,232,0.14)]"
                        : state === "passed" ? "h-5 w-5 rounded-full bg-white border-[1.5px] border-dashed border-slate-400"
                        : "h-5 w-5 rounded-full bg-white border-[1.5px] border-slate-300"
                      }`}
                    >
                      {state === "complete" && <Check size={10} className="text-white" strokeWidth={3} />}
                      {state === "active" && <span aria-hidden className="h-[7px] w-[7px] rounded-[2px] bg-white" />}
                    </span>
                  );
                  // Same list marker editor as desktop (opens as a bottom sheet on phones).
                  return dealId ? <MilestoneEditor dealId={dealId} stageId={label} stageLabel={label}>{dot}</MilestoneEditor> : dot;
                })()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Mobile card tile — same photo rule as desktop: uploaded photo only, else the desktop icon. */
function MobilePropertyTile({ deal, ops }) {
  const [failed, setFailed] = useState(false);
  const isBuilding = /flat|apt|apartment/i.test(ops.address || "")
    || /^leasehold$/i.test(String(ops?.tenureDisplay || ops?.tenure || "").trim());
  if (ops.photo && !failed) {
    return (
      <img
        src={ops.photo}
        alt={ops.address ? `Photo of ${ops.address}` : "Property photo"}
        className="w-[68px] h-[68px] rounded-2xl object-cover bg-slate-100 shrink-0"
        loading="lazy"
        onError={() => setFailed(true)}
        data-testid={`dr-mobile-property-photo-${deal.id}`}
      />
    );
  }
  const Icon = isBuilding ? Building2 : HomeIcon;
  return (
    <div
      className="shrink-0 flex w-[68px] h-[68px] items-center justify-center rounded-2xl bg-gradient-to-br from-slate-50 via-white to-slate-100 ring-1 ring-slate-200/60 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]"
      data-testid={`dr-mobile-property-icon-${deal.id}`}
      data-property-glyph={isBuilding ? "building" : "house"}
    >
      <Icon className="h-[24px] w-[24px] text-slate-500" strokeWidth={1.75} />
    </div>
  );
}

function MobileDealCard({ deal, ops, onChase }) {
  // ─── Derive operational signals from the existing ops object ──
  /* P0 · Card ↔ Detail Reconciliation (2026-08-18) — progress
   * now derives from the canonical SSOT (`ops.completedStepCount`
   * / `ops.totalStepCount`) so "X of Y complete" reads identically
   * to the vertical timeline on the Deal Room detail page.
   * Falls back to the legacy `completedIdx` + `RAIL_STEPS` count
   * only when the canonical fields are missing (tests / mocks). */
  const stepsDone = Number.isFinite(ops.completedStepCount)
    ? ops.completedStepCount
    : (ops.completedIdx ?? -1) + 1;
  const stepsTotal = Number.isFinite(ops.totalStepCount) && ops.totalStepCount > 0
    ? ops.totalStepCount
    : RAIL_STEPS.length;
  const progressPct = Math.max(0, Math.min(100, Math.round((stepsDone / stepsTotal) * 100)));

  // Confidence score — same scale as the desktop confidence block.
  const confidenceScore = (() => {
    let s = 70;
    if (ops.severity === "rose")      s = 60;
    else if (ops.severity === "amber") s = 72;
    else if (ops.ready)                s = 85;
    s -= Math.max(0, (ops.daysToExchange != null && ops.daysToExchange < 0 ? Math.abs(ops.daysToExchange) : 0));
    s += stepsDone * 2;
    return Math.max(20, Math.min(95, Math.round(s)));
  })();
  const confidenceTone = confidenceScore >= 80 ? "good" : confidenceScore >= 65 ? "fair" : "at-risk";

  // P0 · Card ↔ Detail Reconciliation (2026-08-18) — Legacy
  // severity-based status pill retired.  Lifecycle identity now
  // renders through `<OverallStatusBadge/>` below (Under Offer /
  // Exchanged / Completed).  The severity signal survives via
  // the left status rail + waiting-on tone.

  // Left status rail color.
  const railColor =
    ops.severity === "rose"  ? "#F97316" :
    ops.severity === "amber" ? "#F59E0B" :
    ops.ready                ? "#10B981" :
                               "#94A3B8";

  // Operational grid values.
  const _mStage = String(deal?.stage || "").toLowerCase();
  const exchangeIso = ((_mStage === "exchanged" || _mStage === "completed") && (deal?.exchangedAt || deal?.exchanged_at)) || ops.targetIso;
  const exchangeLabel = exchangeIso
    ? new Date(exchangeIso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : "TBC";

  const waitingTone =
    ops.blocker.tone === "rose"  ? "text-rose-600"   :
    ops.blocker.tone === "amber" ? "text-amber-600"  :
                                   "text-emerald-600";

  // Recommendation copy.
  const recommendation = (() => {
    const t = (ops.blocker.label || "").toLowerCase();
    if (t.includes("contract")) return "Request signed contract";
    if (t.includes("search"))   return "Obtain search results";
    if (t.includes("mortgage")) return "Chase mortgage broker";
    if (t.includes("solicitor"))return "Chase counterparty solicitor";
    if (t.includes("aml") || t.includes("id")) return "Complete AML verification";
    return "Update operational status";
  })();
  const recommendationTone =
    ops.severity === "rose" ? "text-rose-600 hover:text-rose-700" :
                              "text-emerald-600 hover:text-emerald-700";

  return (
    <div
      className="relative bg-white rounded-[28px] mb-4 overflow-hidden cf-card-hover"
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 16px 36px -24px rgba(15,23,42,0.16)" }}
      data-testid={`dr-row-mobile-${deal.id}`}
      data-deal-card-mobile={deal.id}
    >
      {/* Left status rail */}
      <span
        aria-hidden
        className={`absolute left-0 top-0 bottom-0 w-1 ${rowHealthTone(ops).rail || rowHealthTone(ops).dot}`}
        data-health={rowHealthTone(ops).label}
        data-testid={`dr-mobile-rail-${deal.id}`}
      />

      <Link to={`/deal-rooms/${deal.id}`} className="block px-5 pt-5 pb-3">
        {/* ── Top section: thumb · address · status · confidence ring ─ */}
        <div className="flex items-start gap-3">
          <MobilePropertyTile deal={deal} ops={ops} />
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-bold text-slate-900 leading-tight tracking-[-0.01em]">
              {ops.address}
            </p>
            {/* P0 · Card ↔ Detail Reconciliation (2026-08-18) —
                Lifecycle identity now paints via
                `OverallStatusBadge` (Under Offer / Exchanged /
                Completed) rather than the mixed severity /
                readiness copy the old pill juggled. */}
            <div className="mt-1.5">
              <OverallStatusBadge status={ops.overallStatus} dealId={`${deal.id}-mobile`} />
            </div>
            {ops?.currentStepLabel && ops?.overallStatus === "under_offer" && (
              <p
                className="text-[11px] font-semibold mt-1.5 leading-tight flex flex-wrap items-baseline gap-x-1"
                style={{ color: "#7C3AED" }}
                data-testid={`dr-mobile-current-step-${deal.id}`}
              >
                {/* Chunks never break internally, so the stage name is always shown in full. */}
                <span className="whitespace-nowrap font-medium text-slate-500">Recorded stage ·</span>
                <span className="whitespace-nowrap" data-testid={`dr-mobile-current-step-label-${deal.id}`}>{ops.currentStepLabel}</span>
                {ops.currentStepIsInProgress && <span className="whitespace-nowrap">· In progress</span>}
              </p>
            )}
            {ops.lastIso && (
              <p className="text-[11px] text-slate-400 mt-1.5 inline-flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Updated {relTimeFromIso(ops.lastIso)}
              </p>
            )}
          </div>
          {ops.terminal !== "Completed" && <MobileConfidenceRing score={confidenceScore} tone={confidenceTone} />}
        </div>

        {/* ── Operational grid: WAITING ON · CHAIN · EXCHANGE ──────── */}
        <div
          className="mt-4 rounded-2xl px-3 py-3 grid grid-cols-3 gap-2"
          style={{ background: "#F8FAFC" }}
        >
          <div className="min-w-0">
            <p className="text-[9.5px] font-semibold uppercase tracking-[0.08em] text-slate-400 inline-flex items-center gap-1">
              <Clock className="w-2.5 h-2.5" /> Waiting on
            </p>
            <p className={`text-[12.5px] font-semibold mt-1 leading-tight truncate ${waitingTone}`}>
              {ops.waitingOn?.mode === "terminal" ? ops.waitingOn.label : ops.blocker.label}
            </p>
            {(ops.waitingOn?.mode === "terminal" ? ops.waitingOn.sub : ops.blocker.sub) && (
              <p className="text-[10.5px] text-slate-500 mt-0.5 truncate">{ops.waitingOn?.mode === "terminal" ? ops.waitingOn.sub : ops.blocker.sub}</p>
            )}
          </div>
          <div className="min-w-0 border-l border-slate-200/60 pl-2">
            <p className="text-[9.5px] font-semibold uppercase tracking-[0.08em] text-slate-400 inline-flex items-center gap-1">
              <Link2 className="w-2.5 h-2.5" /> Chain
            </p>
            <p className={`text-[12.5px] font-semibold mt-1 leading-tight truncate ${
              ops.severity === "rose" ? "text-rose-600" : "text-emerald-600"
            }`}>
              {ops.severity === "rose" ? "At Risk" : "Stable"}
            </p>
            <p className="text-[10.5px] text-slate-500 mt-0.5 truncate">
              {(deal.chain_size || 2)} linked deals
            </p>
          </div>
          <div className="min-w-0 border-l border-slate-200/60 pl-2">
            <p className="text-[9.5px] font-semibold uppercase tracking-[0.08em] text-slate-400 inline-flex items-center gap-1">
              <Calendar className="w-2.5 h-2.5" /> Exchange
            </p>
            {/* P0 · Card ↔ Detail Reconciliation (2026-08-18) —
                Omit the exchange date line entirely when the deal
                has no target exchange date persisted; never
                paint "TBC" or an empty placeholder. */}
            {exchangeIso ? (
              <p className="text-[12.5px] font-semibold mt-1 leading-tight truncate text-slate-900">
                {exchangeLabel}
              </p>
            ) : (
              <p className="text-[12.5px] font-semibold mt-1 leading-tight truncate text-slate-400">
                Not set
              </p>
            )}
            <p className="text-[10.5px] text-slate-500 mt-0.5 truncate inline-flex items-center gap-1">
              <span className="w-1 h-1 rounded-full" style={{ background: railColor }} />
              {stepsDone} of {stepsTotal} complete
            </p>
          </div>
        </div>

        {/* ── Milestone rail ──────────────────────────────────────── */}
        <div className="mt-4">
          <MobileMilestoneRail
            completedIdx={Array.isArray(ops.railLabels) && ops.railLabels.length ? (ops.railActiveIdx ?? 0) - 1 : (ops.completedIdx ?? -1)}
            currentIdx={Array.isArray(ops.railLabels) && ops.railLabels.length ? (ops.railActiveIdx ?? 0) : (ops.currentIdx ?? 0)}
            showMortgage={_journeyResolveMortgage(_journeyOptsForDeal(deal || {}))}
            labels={ops.railLabels}
            recordedSet={ops.railRecordedSet}
            dealId={deal?.id}
          />
        </div>

        {/* ── Activity + recommendation ──────────────────────────── */}
        <div className="mt-4 space-y-1.5">
          <p className="text-[11.5px] text-slate-500 inline-flex items-center gap-1.5">
            <Activity className="w-3 h-3 text-emerald-500" />
            {ops.chaseLine || "No recent activity"}
            {ops.lastIso && (
              <>
                <span className="text-slate-300">·</span>
                <span className="tabular-nums">{relTimeFromIso(ops.lastIso)}</span>
              </>
            )}
          </p>
          {ops.terminal !== "Completed" && (
            <p className={`text-[12px] font-semibold inline-flex items-center gap-1 ${recommendationTone}`}>
              Recommended: {recommendation}
              <ArrowRight className="w-3 h-3" />
            </p>
          )}
        </div>
      </Link>

      {/* ── Progress + actions ──────────────────────────────────── */}
      <div className="px-5 pb-5 pt-1">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-[11px] font-semibold text-slate-600 tabular-nums shrink-0 inline-flex items-center gap-1" data-testid={`dr-mobile-milestones-completed-${deal.id}`}>
            Milestones completed · {stepsDone} of {stepsTotal}
            <StageProgressHint testid={`dr-mobile-stage-hint-${deal.id}`} />
          </span>
          <div className="flex-1 h-[5px] rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${progressPct}%`,
                background: ops.severity === "rose"
                  ? "linear-gradient(90deg, #F97316 0%, #FB923C 100%)"
                  : "linear-gradient(90deg, #10B981 0%, #34D399 100%)",
              }}
            />
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (ops.severity === "rose") {
                onChase?.(deal.id, ops.address);
              } else {
                onChase?.(deal.id, ops.address);
              }
            }}
            className="flex-1 h-11 rounded-2xl bg-white ring-1 ring-slate-200 hover:bg-slate-50 inline-flex items-center justify-center gap-2 text-[13px] font-semibold text-slate-700 transition-colors"
            data-testid={`dr-mobile-chase-${deal.id}`}
          >
            {ops.severity === "rose" ? <Edit3 className="w-3.5 h-3.5" /> : <MessageSquare className="w-3.5 h-3.5" />}
            {ops.severity === "rose" ? "Update" : "Chase"}
          </button>
          {/* iter-228 — Progress Transaction is the PRIMARY mobile
              action. Operators primarily want to push the deal
              forward, not just open a passive room view. */}
          {ops.terminal !== "Completed" && <Link
            to={`/deal-rooms/${deal.id}?progress=1`}
            className="flex-[1.4] h-11 rounded-2xl inline-flex items-center justify-center gap-2 text-[13px] font-semibold text-white transition-all"
            style={{
              background: "linear-gradient(135deg, #7C3AED 0%, #6366F1 100%)",
              boxShadow: "0 6px 18px -6px rgba(124,58,237,0.55), 0 0 0 1px rgba(124,58,237,0.20)",
            }}
            data-testid={`dr-mobile-progress-${deal.id}`}
          >
            <TrendingUp className="w-3.5 h-3.5" />
            Progress Transaction
          </Link>}
        </div>
        {/* iter-228 — Open room demoted to the secondary mobile
            action. Same surface treatment as the calm desktop
            secondary so the mobile card mirrors the canonical
            CTA hierarchy: progress first, navigate second. */}
        <Link
          to={`/deal-rooms/${deal.id}`}
          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 h-11 rounded-2xl bg-white hover:bg-slate-50 ring-1 ring-slate-200 text-[13px] font-medium text-slate-600 transition"
          data-testid={`dr-mobile-open-${deal.id}`}
        >
          <LogIn className="w-3.5 h-3.5 text-slate-500" strokeWidth={2.2} />
          Open room
        </Link>
      </div>
    </div>
  );
}

/* ─── Stage group section ───────────────────────────────────────── */
function GroupSection({ id, label, deals, annotated, defaultOpen = true, viewAllLabel, onChase, onLogUpdate, onRequestDoc }) {
  const [open, setOpen] = useState(defaultOpen);
  // Mount only the layout for the current breakpoint (was: both, one hidden by CSS).
  const isDesktop = useMediaQuery("(min-width: 768px)");
  if (!deals.length) return null;
  /* iter-314 — When `label` is null the group header chrome
   * (chevron toggle + section title + count) is suppressed so
   * the list reads as one continuous stream. The "View all"
   * link is also retired in the single-list layout — the active
   * status pill already scopes the visible set. */
  const showHeader = label != null;
  return (
    <section className="mb-4" data-testid={`dr-group-${id}`} data-dr-group={id}>
      {showHeader && (
        <header className="hidden md:flex items-center gap-2 px-1 py-2 mb-1" data-testid={`dr-idx-group-${id}`}>
          <button
            type="button"
            // Tap / mouse click opens (hover + focus already open it); keyboard Enter/Space toggles.
      onClick={(e) => (e.detail === 0 ? setOpen((v) => !v) : setOpen(true))}
      onKeyDown={(e) => { if (e.key === " ") { e.preventDefault(); setOpen((v) => !v); } if (e.key === "Escape") setOpen(false); }}
            className="text-slate-400 hover:text-slate-600"
            aria-label={open ? "Collapse" : "Expand"}
          >
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
          <h3 className="text-[13.5px] font-semibold text-slate-900">{label}</h3>
          <span className="text-[11.5px] text-slate-500 tabular-nums">{deals.length}</span>
        </header>
      )}
      {open && (
        <div className="space-y-3.5">
          {deals.map((d) => {
            const ops = annotated.get(d.id);
            return (
              <React.Fragment key={d.id}>
                {isDesktop ? (
                <div className="hidden md:block">
                  <DealRow
                    deal={d}
                    ops={ops}
                    onChase={onChase}
                    onLogUpdate={onLogUpdate}
                    onRequestDoc={onRequestDoc}
                  />
                </div>
                ) : (
                <div className="md:hidden">
                  <MobileDealCard
                    deal={d}
                    ops={ops}
                    onChase={onChase}
                  />
                </div>
                )}
              </React.Fragment>
            );
          })}
          {showHeader && viewAllLabel && deals.length >= 3 && (
            <Link to="/sales-progression" className="inline-flex items-center gap-1 text-[12px] font-semibold text-violet-700 hover:text-violet-900 pt-1 pl-1">
              {viewAllLabel} <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      )}
    </section>
  );
}

/* ─── Right operational rail — iter-207 three calm support cards ───
 *
 *  Three stacked cards — never card-in-card fragmentation:
 *    1. Operational pressure — 5 clickable rows (Deals need
 *       attention / Waiting on others / Silent > 5 days /
 *       Exchange ready / Fallen through). Tapping a row
 *       drives the page-level pill filter.
 *    2. Upcoming exchanges — next 5 exchange dates with property
 *       address + countdown + urgency dot.
 *    3. Recent activity — 5 most-recent operational movements
 *       (Contract received / Search update / Email sent /
 *       AML cleared / ID verified) with calm tone-tinted icons.
 */
function OperationalRail({ counts, recentDeals, annotated, onSelectPill, deals, visibleCount }) {
  /* iter-212 P11 — Right rail now leads with TODAY'S INTERVENTION
   * QUEUE — operational, intervention-first, calm. The legacy
   * "Operational pressure" card has been replaced by an
   * intervention-focused list with calm large-numeral counts +
   * sub-text + chevron + "View intervention queue →" footer. */
  /* iter-247 — Right-rail values tightened for single-office
   * demo realism. Caps reduced so the intervention queue reads
   * as calm operational pressure, not multi-office aggregate. */
  const _demoCalibrate = visibleCount === 30; // iter-247 sampler active
  const _capRail = (raw, cap) => _demoCalibrate ? Math.min(raw, cap) : raw;
  const interventionRows = [
    { id: "exchanges-at-risk",    pill: "attention", value: _capRail(Math.max(0, Math.min(counts.attention, 4)), 2), label: "Exchanges at risk",     sub: "Require immediate attention", tone: "rose",    icon: AlertTriangle },
    { id: "silent-solicitors",    pill: "silent",    value: _capRail(counts.silent, 3),                              label: "Silent solicitors",     sub: "No response > 5 days",        tone: "amber",   icon: Clock },
    { id: "overdue-aml",          pill: "attention", value: _capRail(Math.max(0, Math.min(counts.attention, 3)), 2), label: "Overdue AML checks",    sub: "Compliance at risk",          tone: "violet",  icon: Shield },
    { id: "expiring-mortgages",   pill: "exchange",  value: _capRail(Math.max(0, Math.min(counts.exchange,  2)), 1), label: "Expiring mortgage offers", sub: "Within 7 days",            tone: "rose",    icon: AlertTriangle },
    { id: "awaiting-mgmt-packs",  pill: "waiting",   value: _capRail(Math.max(0, Math.min(counts.waiting,   5)), 3), label: "Awaiting management packs", sub: "Delays exchanges",         tone: "violet",  icon: Clock },
  ];

  /* iter-207 — Upcoming exchanges. Five soonest non-overdue exchanges
   * with countdown copy + urgency dot. */
  const upcoming = useMemo(() => {
    const items = (deals || [])
      .map((d) => {
        const ops = annotated.get(d.id);
        if (!ops || ops.daysToExchange == null) return null;
        if (ops.daysToExchange < 0) return null;
        return { d, ops };
      })
      .filter(Boolean)
      .sort((a, b) => a.ops.daysToExchange - b.ops.daysToExchange)
      .slice(0, 5);
    return items;
  }, [deals, annotated]);

  /* iter-213 P10 — Right rail refinement.
   * Softer card separation — lighter borders + slightly more
   * integrated shadow so the rail reads as part of the operational
   * system rather than a stack of floating analytics widgets. */
  /* iter-250 — Right rail surfaces aligned with Forecasting Centre.
   *
   * Calm card chrome: white surface, slate-200/70 ring, soft 1px
   * shadow. Reads as a coherent operational system rather than a
   * stack of floating analytics widgets. */
  const surfaceShadow = { boxShadow: "0 1px 2px rgba(15,23,42,0.04)" };
  const surfaceRing = "ring-1 ring-slate-200/70";

  return (
    <aside className="space-y-3.5" data-testid="dr-right-rail">
      {/* iter-250 — Renamed "Today's intervention queue" →
          "Today's priorities" to match the approved pilot reference
          (calmer operational vocabulary, same handler wiring). */}
      <div className={`rounded-2xl bg-white px-4 py-3.5 ${surfaceRing}`} style={surfaceShadow} data-testid="dr-rail-intervention">
        <div className="text-[10.5px] font-bold text-slate-500 tracking-[0.08em] uppercase mb-2.5">
          Today&apos;s priorities
        </div>
        <ul className="space-y-2">
          {interventionRows.map((r) => {
            const Icon = r.icon;
            const tilePalette = r.tone === "rose"   ? "bg-rose-50 text-rose-500"
                              : r.tone === "violet" ? "bg-violet-50 text-violet-600"
                              : r.tone === "amber"  ? "bg-amber-50 text-amber-600"
                              : r.tone === "emerald"? "bg-emerald-50 text-emerald-600"
                              :                       "bg-slate-100 text-slate-500";
            const interactive = r.pill && r.value > 0;
            return (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => interactive && onSelectPill?.(r.pill)}
                  disabled={!interactive}
                  className={`w-full flex items-center gap-3 rounded-xl px-2 py-1.5 text-left transition ${
                    interactive ? "hover:bg-slate-50 cf-press" : "cursor-default"
                  }`}
                  data-testid={`dr-intervention-${r.id}`}
                >
                  <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${tilePalette}`}>
                    <Icon className="h-4 w-4" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] font-bold text-slate-900 tabular-nums shrink-0">{r.value}</span>
                      <span className="text-[12px] font-semibold text-slate-800 truncate">{r.label}</span>
                    </div>
                    <div className="text-[10.5px] text-slate-500 truncate mt-0.5">{r.sub}</div>
                  </div>
                  {interactive && <ChevronRight className="h-3.5 w-3.5 text-slate-300 shrink-0" />}
                </button>
              </li>
            );
          })}
        </ul>
        <Link
          to="/insights"
          className="mt-2.5 inline-flex items-center justify-center gap-1.5 w-full rounded-lg bg-slate-50 hover:bg-slate-100 px-3 py-1.5 text-[12px] font-semibold text-slate-700 cf-press"
          data-testid="dr-intervention-view-all"
        >
          View all priorities <ArrowRight className="h-3.5 w-3.5 text-violet-500" />
        </Link>
      </div>

      {/* Card 2 — Upcoming exchanges */}
      <div className={`rounded-2xl bg-white px-4 py-3.5 ${surfaceRing}`} style={surfaceShadow} data-testid="dr-rail-upcoming">
        <div className="flex items-center justify-between mb-2.5">
          <div className="text-[10.5px] font-bold text-slate-500 tracking-[0.08em] uppercase">Upcoming exchanges</div>
          <Link to="/calendar" className="text-[11px] font-semibold text-violet-700 hover:text-violet-900" data-testid="dr-rail-view-calendar">
            View calendar
          </Link>
        </div>
        {upcoming.length === 0 ? (
          <div className="text-[11.5px] text-slate-400">No upcoming exchanges scheduled.</div>
        ) : (
          <ul className="space-y-2">
            {upcoming.map(({ d, ops }) => {
              const urgency = ops.daysToExchange <= 7  ? "bg-rose-500"
                            : ops.daysToExchange <= 14 ? "bg-amber-500"
                            :                            "bg-emerald-500";
              const relative = ops.daysToExchange === 0 ? "Today"
                             : ops.daysToExchange === 1 ? "Tomorrow"
                             : `In ${ops.daysToExchange} days`;
              return (
                <li key={d.id} className="flex items-center gap-3" data-testid={`dr-upcoming-${d.id}`}>
                  <div className="text-[11px] font-semibold text-slate-700 tabular-nums w-12 shrink-0">
                    {fmtDateShort(ops.targetIso)}
                  </div>
                  <Link to={`/deal-rooms/${d.id}`} className="min-w-0 flex-1 hover:bg-slate-50 rounded-md -mx-1 px-1 py-0.5 cf-press">
                    <div className="text-[12px] font-medium text-slate-800 truncate leading-tight">{ops.address}</div>
                    <div className="text-[10.5px] text-slate-400 mt-0.5">{relative}</div>
                  </Link>
                  <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${urgency}`} aria-hidden />
                </li>
              );
            })}
          </ul>
        )}
        <Link
          to="/calendar"
          className="mt-2.5 inline-flex items-center justify-center gap-1.5 w-full rounded-lg bg-slate-50 hover:bg-slate-100 px-3 py-1.5 text-[12px] font-semibold text-slate-700 cf-press"
          data-testid="dr-upcoming-goto-calendar"
        >
          Go to calendar <ArrowRight className="h-3.5 w-3.5 text-violet-500" />
        </Link>
      </div>
    </aside>
  );
}


/* ─── iter-205A — sort modes (operational forecast ordering) ──────
 * Single source of truth used by the desktop menu, the column-header
 * quick toggles, the mobile sort selector, and the contract test. */
/* iter-332 — Sort registry refresh.
 *
 * Public-facing sort vocabulary aligned to the operator brief:
 *
 *   Recently Updated   · default
 *   Needs Attention    · composite urgency rank (priority alias)
 *   Exchange Date      · soonest exchange first
 *   Completion Date    · soonest completion first
 *   Stage Progress     · furthest along the journey first
 *   Newest Deal Rooms  · most recently created first
 *   Longest in Stage   · longest time without movement
 *   Highest Fee        · highest forecast revenue first
 *   Deal Health        · strongest projected confidence first
 *
 * Hidden entries (asc/desc variants + legacy ids) remain in the
 * registry so column-header click toggles, persisted user prefs
 * and the operational eyebrow caption all keep resolving.
 */
const SORT_MODES = [
  // iter-alignment · P45 — Needs Attention is now the default;
  // operators land straight into triage.  All comparators reuse
  // the existing SSOT (severity, `daysToExchange`, `railActiveIdx`,
  // `confidence`) — no duplicate scoring logic.
  { id: "attention",  label: "Needs Attention",           caption: "Highest operational urgency first" },
  { id: "recent",     label: "Recently updated",          caption: "Most recently touched first" },
  { id: "exchange",   label: "Exchange date (Soonest)",   caption: "Sorted by soonest exchange",    col: "exchange",   dir: "asc"  },
  { id: "completion", label: "Completion date (Soonest)", caption: "Sorted by soonest completion",  col: "completion", dir: "asc"  },
  { id: "readiness",  label: "Exchange readiness",        caption: "Closest to exchange first" },
  { id: "newest",     label: "Newest instructions",       caption: "Most recently created first" },
  { id: "inactive",   label: "Longest in stage",          caption: "Longest time without movement first" },
  { id: "fee",        label: "Highest fee",               caption: "Highest forecast revenue first" },
  // ─── Hidden registry — column-header toggles + legacy prefs ──
  //
  // `health` + `stage` retained here so persisted preferences from
  // pre-P45 sessions still resolve without a migration.
  { id: "health",          label: "Deal health",           caption: "Healthiest deals first",              hidden: true },
  { id: "stage",           label: "Stage progress",        caption: "Furthest along the journey first",   hidden: true },
  { id: "priority",        label: "Priority",              caption: "Highest operational urgency first",   hidden: true },
  { id: "exchange-asc",    label: "Soonest exchange",      caption: "Sorted by soonest exchange",          col: "exchange",   dir: "asc",  hidden: true },
  { id: "exchange-desc",   label: "Latest exchange",       caption: "Sorted by latest exchange",           col: "exchange",   dir: "desc", hidden: true },
  { id: "completion-asc",  label: "Soonest completion",    caption: "Sorted by soonest completion",        col: "completion", dir: "asc",  hidden: true },
  { id: "completion-desc", label: "Latest completion",     caption: "Sorted by latest completion",         col: "completion", dir: "desc", hidden: true },
  { id: "risk",            label: "Highest risk",          caption: "Highest operational risk first",      hidden: true },
  { id: "overdue",         label: "Most overdue",          caption: "Overdue exchange / completion first", hidden: true },
  { id: "confidence",      label: "Highest confidence",    caption: "Strongest projected exchange first",  hidden: true },
];

/* SortMenu — calm dropdown anchored to the desktop search bar.
 * No shadcn primitives, no heavy animations — just the existing
 * cf-press + cf-scale-in doctrine. */
function SortMenu({ value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef(null);
  const popRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onMouseDown = (e) => {
      if (popRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [open]);
  const active = SORT_MODES.find((m) => m.id === value) || SORT_MODES[0];
  // iter-332 — Hide registry-only entries (column-header asc/desc
  // toggles + legacy ids) from the dropdown list. They stay in
  // SORT_MODES so persisted prefs + eyebrow caption keep resolving.
  const visibleModes = SORT_MODES.filter((m) => !m.hidden);
  return (
    <div className="relative" data-testid="dr-sort-menu">
      <button
        ref={btnRef}
        type="button"
        // Tap / mouse click opens (hover + focus already open it); keyboard Enter/Space toggles.
      onClick={(e) => (e.detail === 0 ? setOpen((v) => !v) : setOpen(true))}
      onKeyDown={(e) => { if (e.key === " ") { e.preventDefault(); setOpen((v) => !v); } if (e.key === "Escape") setOpen(false); }}
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-white border border-slate-200 text-[12.5px] text-slate-700 hover:bg-slate-50 cf-press"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="dr-sort-trigger"
      >
        {/* iter-341 — Trigger reads "Sort: <Active>" with a muted
            "Sort:" prefix + bold active label so the active sort
            state is unambiguous. */}
        <span className="text-slate-400 font-medium">Sort:</span>
        <span className="font-bold text-slate-800" data-testid="dr-sort-trigger-active">{active.label}</span>
        <ChevronDown size={12} className="text-slate-400" />
      </button>
      {open && (
        <div
          ref={popRef}
          role="listbox"
          aria-label="Sort deals"
          className="absolute z-40 right-0 mt-1.5 w-[230px] rounded-xl bg-white p-1 cf-scale-in"
          style={{
            boxShadow: "0 18px 40px -18px rgba(15,23,42,0.28), 0 0 0 1px rgba(226,232,240,0.7)",
          }}
          data-testid="dr-sort-popover"
        >
          {visibleModes.map((m) => (
            <button
              key={m.id}
              type="button"
              role="option"
              aria-selected={m.id === value}
              onClick={() => { onChange(m.id); setOpen(false); }}
              className={`w-full flex items-center justify-between gap-2 px-2.5 h-8 rounded-lg text-[12.5px] text-left hover:bg-slate-50 ${m.id === value ? "text-violet-700 font-semibold" : "text-slate-700"}`}
              data-testid={`dr-sort-option-${m.id}`}
            >
              <span>{m.label}</span>
              {m.id === value && <Check size={12} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* iter-384 · Approved-design final polish — Status filter
 * dropdown.  Same 5 status options as the retired GroupPills
 * chip row, same state, same behaviour.  Just a compact
 * dropdown surface matching the approved toolbar mock.
 */
const _STATUS_OPTIONS = [
  { id: "all",       label: "All statuses",     dot: null,       tone: "text-slate-500"   },
  { id: "attention", label: "Needs Attention",  dot: "violet",   tone: "text-violet-600"  },
  { id: "waiting",   label: "At Risk",          dot: "rose",     tone: "text-rose-600"    },
  { id: "watch",     label: "Watch",            dot: "amber",    tone: "text-amber-600"   },
  { id: "exchange",  label: "Ready to Exchange",   dot: "emerald",  tone: "text-emerald-600" },
];

// Health filter pills — same options, counts and onChange as the former Health dropdown.
const _PILL_DOT = { attention: "bg-amber-500", waiting: "bg-rose-500", watch: "bg-yellow-500", exchange: "bg-emerald-500" };
function StatusFilterMenu({ value, onChange, counts }) {
  const countFor = (id) => counts?.[id] ?? 0;
  return (
    <div className="flex items-center gap-1.5 flex-nowrap" role="radiogroup" aria-label="Filter deals by health" data-testid="dr-status-menu">
      {_STATUS_OPTIONS.map((o) => {
        const on = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.id)}
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12.5px] font-semibold whitespace-nowrap transition-[background-color,box-shadow,color] cf-press ${
              on ? "bg-[#6536E8] text-white shadow-[0_2px_8px_-2px_rgba(101,54,232,0.45)]"
                 : "bg-white text-slate-700 ring-1 ring-slate-200 hover:ring-slate-300 hover:bg-slate-50"
            }`}
            data-testid={`dr-status-option-${o.id}`}
          >
            {_PILL_DOT[o.id] && <span aria-hidden className={`h-2 w-2 rounded-full ${_PILL_DOT[o.id]} ${on ? "ring-2 ring-white/70" : ""}`} />}
            <span data-testid={on ? "dr-status-trigger-active" : undefined}>{o.id === "all" ? "All" : o.label}</span>
            <span className={`tabular-nums text-[11.5px] ${on ? "px-1.5 rounded-full bg-white/20 text-white" : "text-slate-400"}`}>{countFor(o.id)}</span>
          </button>
        );
      })}
    </div>
  );
}

/* iter-384 · Approved-design final polish — More Filters
 * dropdown.  Houses the secondary filter categories (stage
 * segmentation) that were retired from the visible toolbar in
 * iter-341.  Badge count = number of non-default filters
 * currently applied so operators always know how many active
 * filters they've stacked.
 */
const _STAGE_OPTIONS = [
  { id: "all",       label: "All stages"                    },
  { id: "early",     label: "Early (Lead · Drafts)"         },
  { id: "progress",  label: "In Progress"                   },
  { id: "exchange",  label: "Ready to Exchange"             },
  { id: "exchanged", label: "Exchanged"                     },
  { id: "completed", label: "Completed"                     },
];

const _tenureOf = (ops) => {
  const t = String(ops?.tenureDisplay || ops?.tenure || "").trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : "";
};
const _assigneeOf = (deal) =>
  String(((deal?.participants || []).find((p) => p?.participant_type === "negotiator") || {}).full_name || "").trim();

function TenureFilter({ value, onChange, options }) {
  return (
    <div className="inline-flex items-center gap-1.5 h-8 pl-3 pr-1 rounded-lg bg-white border border-slate-200 text-[12.5px] text-slate-500" data-testid="dr-filter-tenure">
      <span id="dr-filter-tenure-label">Tenure:</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          aria-labelledby="dr-filter-tenure-label dr-filter-tenure-select"
          className="h-7 w-auto gap-1.5 border-0 bg-transparent px-1.5 py-0 shadow-none font-semibold text-[12.5px] text-slate-700 rounded-md focus:ring-0 focus-visible:ring-2 focus-visible:ring-violet-300 data-[state=open]:bg-violet-50 data-[state=open]:text-violet-700"
          data-testid="dr-filter-tenure-select"
          id="dr-filter-tenure-select"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent
          align="end"
          className="min-w-[9rem] rounded-xl border-slate-200/80 bg-white p-0 shadow-[0_8px_24px_-6px_rgba(15,23,42,0.14)]"
          data-testid="dr-filter-tenure-menu"
        >
          {[["all", "All"], ...options.map((t) => [t, t])].map(([v, label]) => (
            <SelectItem
              key={v}
              value={v}
              className="rounded-lg text-[12.5px] font-medium text-slate-700 focus:bg-slate-50 focus:text-slate-900 data-[state=checked]:bg-violet-50 data-[state=checked]:font-semibold data-[state=checked]:text-violet-700 [&_svg]:text-violet-600"
              data-testid={`dr-filter-tenure-option-${String(v).toLowerCase()}`}
            >
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function MoreFiltersMenu({
  stagePill,
  onChangeStage,
  activeCount,
  onReset,
  assignee = "all",
  onChangeAssignee,
  assigneeOptions = [],
}) {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef(null);
  const popRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onMouseDown = (e) => {
      if (popRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [open]);
  return (
    <div className="relative" data-testid="dr-more-filters-menu">
      <button
        ref={btnRef}
        type="button"
        // Tap / mouse click opens (hover + focus already open it); keyboard Enter/Space toggles.
      onClick={(e) => (e.detail === 0 ? setOpen((v) => !v) : setOpen(true))}
      onKeyDown={(e) => { if (e.key === " ") { e.preventDefault(); setOpen((v) => !v); } if (e.key === "Escape") setOpen(false); }}
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-white border border-slate-200 text-[12.5px] text-slate-700 hover:bg-slate-50 cf-press"
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid="dr-more-filters-trigger"
      >
        <SlidersHorizontal size={12} className="text-slate-500" />
        <span className="font-semibold text-slate-700">More filters</span>
        {activeCount > 0 && (
          <span
            className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-violet-100 text-violet-700 text-[10.5px] font-bold tabular-nums"
            data-testid="dr-more-filters-badge"
          >
            {activeCount}
          </span>
        )}
      </button>
      {open && (
        <div
          ref={popRef}
          role="dialog"
          aria-label="More filters"
          className="absolute z-40 right-0 mt-1.5 w-[280px] rounded-xl bg-white p-3 cf-scale-in"
          style={{
            boxShadow: "0 18px 40px -18px rgba(15,23,42,0.28), 0 0 0 1px rgba(226,232,240,0.7)",
          }}
          data-testid="dr-more-filters-popover"
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-slate-500">
              Stage
            </span>
            {activeCount > 0 && (
              <button
                type="button"
                onClick={() => { onReset(); setOpen(false); }}
                className="text-[11px] text-slate-500 hover:text-slate-800 underline underline-offset-2"
                data-testid="dr-more-filters-clear"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="space-y-0.5">
            {_STAGE_OPTIONS.map((o) => (
              <button
                key={o.id}
                type="button"
                role="option"
                aria-selected={o.id === stagePill}
                onClick={() => { onChangeStage(o.id); }}
                className={`w-full flex items-center justify-between gap-2 px-2.5 h-8 rounded-lg text-[12.5px] text-left hover:bg-slate-50 ${o.id === stagePill ? "text-violet-700 font-semibold bg-violet-50/50" : "text-slate-700"}`}
                data-testid={`dr-more-filters-stage-${o.id}`}
              >
                <span>{o.label}</span>
                {o.id === stagePill && <Check size={12} />}
              </button>
            ))}
          </div>
          {assigneeOptions.length > 0 && (
            <>
              <div className="mt-3 mb-2 text-[10.5px] font-bold uppercase tracking-[0.1em] text-slate-500">Assigned to</div>
              <div className="space-y-0.5 max-h-[220px] overflow-y-auto">
                {["all", ...assigneeOptions].map((name) => (
                  <button
                    key={name}
                    type="button"
                    role="option"
                    aria-selected={name === assignee}
                    onClick={() => onChangeAssignee?.(name)}
                    className={`w-full flex items-center justify-between gap-2 px-2.5 h-8 rounded-lg text-[12.5px] text-left hover:bg-slate-50 ${name === assignee ? "text-violet-700 font-semibold bg-violet-50/60" : "text-slate-700"}`}
                    data-testid={`dr-more-filters-assignee-${name === "all" ? "all" : name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <span className="truncate">{name === "all" ? "Everyone" : name}</span>
                    {name === assignee && <Check size={12} />}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* iter-385 · Approved reference — PaginationBar.
 *
 * `< Previous · 1 · 2 · 3 · Next >` compact pager beneath the
 * deal list.  10 rows / page by default with a per-session
 * size selector (5 / 10 / 25 / 50).  Selected page size is
 * persisted via the parent's `prefs` hook so it survives a
 * reload.  Zero new state on the row itself — this is a pure
 * presentation slice around the existing `pillFiltered` list.
 */
const _PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

function PaginationBar({ page, pageSize, total, onPageChange, onPageSizeChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end   = Math.min(total, safePage * pageSize);

  /* Compact numeric window · always show first + last, with
     ellipses when there are gaps.  Keeps the bar readable
     regardless of total-page count. */
  const nums = React.useMemo(() => {
    const out = [];
    const push = (v) => out.push(v);
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i += 1) push(i);
    } else {
      push(1);
      if (safePage > 4) push("…");
      const lo = Math.max(2, safePage - 1);
      const hi = Math.min(totalPages - 1, safePage + 1);
      for (let i = lo; i <= hi; i += 1) push(i);
      if (safePage < totalPages - 3) push("…");
      push(totalPages);
    }
    return out;
  }, [safePage, totalPages]);

  return (
    <nav
      className="mt-4 flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-white ring-1 ring-slate-200/70"
      data-testid="dr-pagination"
      aria-label="Deal rooms pagination"
    >
      <div className="text-[12px] text-slate-500 tabular-nums" data-testid="dr-pagination-summary">
        Showing <span className="font-semibold text-slate-900">{start}</span>–
        <span className="font-semibold text-slate-900">{end}</span> of{" "}
        <span className="font-semibold text-slate-900">{total}</span>
      </div>
      <div className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, safePage - 1))}
          disabled={safePage <= 1}
          className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-[12px] text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="dr-pagination-prev"
        >
          <ChevronLeft size={12} />
          Previous
        </button>
        {nums.map((n, i) =>
          n === "…" ? (
            <span key={`e${i}`} className="px-1.5 text-slate-400 text-[12px]">…</span>
          ) : (
            <button
              key={n}
              type="button"
              onClick={() => onPageChange(n)}
              aria-current={n === safePage ? "page" : undefined}
              className={`h-8 min-w-[32px] px-2 rounded-md text-[12px] tabular-nums cf-press ${
                n === safePage
                  ? "bg-violet-600 text-white font-bold"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
              data-testid={`dr-pagination-page-${n}`}
            >
              {n}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, safePage + 1))}
          disabled={safePage >= totalPages}
          className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-[12px] text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="dr-pagination-next"
        >
          Next
          <ChevronRight size={12} />
        </button>
      </div>
      <div className="hidden md:inline-flex items-center gap-2 text-[12px] text-slate-500">
        <label htmlFor="dr-pagination-size" className="text-slate-400">Rows</label>
        <select
          id="dr-pagination-size"
          value={pageSize}
          onChange={(e) => { onPageSizeChange(Number(e.target.value)); onPageChange(1); }}
          className="h-8 px-2 rounded-md border border-slate-200 bg-white text-[12px] text-slate-700 focus:outline-none focus:ring-1 focus:ring-violet-500"
          data-testid="dr-pagination-page-size"
        >
          {_PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>
    </nav>
  );
}


/* ─── Page shell ─────────────────────────────────────────────────── */
export default function DealRoomsIndexPage() {
  const { deals, dealsLoading, findProperty, isPropertyArchived } = useChainStore();
  // Same canonical rule as the sidebar "Need attention" chip.
  const { user } = useAuth();
  // iter-209A — URL `?followed=1` deep-link support. Reading the
  // query param here lets notifications + digest emails route the
  // operator straight into their followed shortlist.
  const [searchParams, setSearchParams] = useSearchParams();

  /* iter-209A — Followed-deal shortlist.
   * Per-deal follow state is persisted by the canonical Deal Room
   * page under localStorage keys `cf:follow:<dealId>`. Reading them
   * here lets the index page surface a "Followed" filter pill +
   * honour the `?followed=1` deep-link param. Cheap O(n) over the
   * active deals list once per render — keyed by dealId so private-
   * mode / quota errors degrade to "nothing followed". */
  const followedSet = useMemo(() => {
    if (typeof window === "undefined") return new Set();
    const out = new Set();
    try {
      (deals || []).forEach((d) => {
        const v = window.localStorage.getItem(`cf:follow:${d.id}`);
        if (v === "1") out.add(d.id);
      });
    } catch { /* private mode → empty set */ }
    return out;
  }, [deals]);

  /* iter-205C — Operator preference memory.
   * Persist sort mode + active operational filter per
   * `(workspaceId, userId)` in localStorage so a returning
   * operator lands on their preferred forecast view. Read once
   * on mount; written on every state change. Calm, invisible,
   * never persists search / modal / overlay state. */
  /* iter-340 — Hidden `?reset=1` escape hatch.
   *
   * If the operator (or support) hits `/deal-rooms?reset=1`, we
   * clear the persisted prefs blob for THIS surface BEFORE
   * `useOperatorPrefs` reads it, so the page mounts with a
   * canonical default state (no stale pill / stagePill values).
   * Runs synchronously inside the render body so the cleanup
   * lands before the prefs hook hydrates. The `?reset=1` flag
   * is stripped from the URL in a follow-up effect (below).
   *
   * Safe & idempotent: a second render won't re-clear because
   * the `?reset=1` param is gone after the first pass. */
  const _resetRequested = searchParams.get("reset") === "1";
  if (_resetRequested && typeof window !== "undefined") {
    try {
      const orgId  = user?.org_id || "_";
      const userId = user?.id || "_";
      const key = `chainflow:opPrefs:deal-rooms-index:${orgId}:${userId}`;
      window.localStorage.removeItem(key);
      // Belt + braces: nuke any sibling org/user combinations too,
      // so a stale blob attached to a previous workspace can't
      // resurface after a workspace switch.
      Object.keys(window.localStorage)
        .filter((k) => k.startsWith("chainflow:opPrefs:deal-rooms-index:"))
        .forEach((k) => window.localStorage.removeItem(k));
    } catch (_e) { /* localStorage disabled — fall back silently */ }
  }
  const { initial: prefs, restoredKeys, persist } = useOperatorPrefs(
    "deal-rooms-index",
    user?.org_id,
    user?.id,
  );

  const [query, setQuery] = useState("");
  // iter-209A — initial pill resolves in this priority:
  //   1. ?followed=1 query param (deep link wins)
  //   2. operator preference memory (iter-205C)
  //   3. canonical default "all"
  const [pill, setPill] = useState(() => {
    if (searchParams.get("followed") === "1") return "followed";
    return prefs.pill || "all";
  });
  /* iter-313 — Dual filter system. Stage filter sits beneath the
   * status pills (Needs Action / At Risk / Watch / On Track / All)
   * and narrows the list to Early / Progressing / Exchange Ready
   * deals. Persisted alongside the status pill so the operator's
   * filter combination survives reloads.
   *
   * P0 · Stage Pill Persistence (2026-02-28) — Initialiser
   * validates the stored value inline against the canonical
   * `VALID_STAGE_IDS` Set so the FIRST render already lands on
   * the operator's last-picked pill.  Any legacy / invalid
   * stored value falls back to "all" without waiting for the
   * post-mount sanitiser.
   */
  const VALID_STAGE_IDS = _VALID_STAGE_IDS;
  const [stagePill, setStagePill] = useState(() => {
    const stored = prefs.stagePill;
    return (typeof stored === "string" && VALID_STAGE_IDS.has(stored)) ? stored : "all";
  });
  useEffect(() => { persist({ stagePill }); }, [stagePill, persist]);

  /* iter-339 — Defensive prefs sanitiser.
   *
   * Stale operator-prefs blobs from earlier builds (most commonly
   * on iPad Safari where `chainflow:opPrefs:deal-rooms-index:...`
   * persisted a `pill` / `stagePill` value that the current pill
   * taxonomy no longer recognises) used to silently wipe the list
   * to 0 deals while the sidebar still showed the full 22-Active
   * count. Read the persisted slice once on mount and snap it back
   * to "all" if the value isn't in the current canonical set.
   *
   * P0 · Stage Pill Persistence (2026-02-28)
   * ---------------------------------------------------------
   * The iter-341 forced reset to "all" (which stripped every
   * persisted stagePill on mount because the Stage toolbar had
   * been temporarily retired) is gone.  The Stage bar is back
   * with six pills — All / Early / In Progress / Ready to
   * Exchange / Exchanged / Completed — and the operator's
   * choice must survive navigation and reload.  Validation
   * still snaps back to "all" for any legacy value outside the
   * canonical Set, so a stale blob from a previous taxonomy
   * cannot silently filter the list. */
  useEffect(() => {
    const VALID_PILL  = new Set(["all", "needs-action", "at-risk", "watch", "on-track", "followed", "attention", "exchange", "waiting", "silent"]);
    if (!VALID_PILL.has(pill))            setPill("all");
    if (!VALID_STAGE_IDS.has(stagePill))  setStagePill("all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* iter-340 — Runtime side of the `?reset=1` escape hatch.
   *
   * Runs once after the synchronous localStorage cleanup above so
   * any in-memory state slices that hydrated from the prior render
   * cycle also snap back to defaults (query, pill, stagePill,
   * sortMode, ?followed). The `?reset=1` param is then stripped
   * from the URL via `replace` so the back button still walks the
   * operator out of the surface cleanly. */
  useEffect(() => {
    if (!_resetRequested) return;
    setQuery("");
    setPill("all");
    setStagePill("all");
    setSortMode("attention");
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("reset");
      next.delete("followed");
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // iter-205A — operational sort mode. "recent" is the canonical
  // default (most-recently-updated first, severity tiebreaker).
  // All other modes carry forecast / risk semantics.
  // iter-205C — initial value hydrates from operator preference
  // memory when the operator returns to this surface.
  // iter-alignment · P0 · Deal Rooms Default Sort (2026-08-16) —
  // Default is now `exchange` (Soonest exchange).  Its comparator
  // `exchange-asc` orders rows by `daysToExchange` (derived from
  // the canonical `target_exchange_date` SSOT): overdue first,
  // then due today, then soonest upcoming, then rows with no
  // target exchange date last.  Persisted prefs still win (last
  // selection remembered across sessions via `prefs.sortMode`);
  // `recent` remains available in the sort dropdown.
  const [sortMode, setSortMode] = useState(() => prefs.sortMode || "attention");
  const [tenureFilter, setTenureFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");

  /* iter-385 · Approved reference — pagination.  10 rows per page
   * by default, persisted via the same prefs slice as the other
   * filter state.  Page resets to 1 whenever the filter or search
   * changes so the operator never lands on an empty middle page. */
  const [pageSize, setPageSize] = useState(() =>
    Number.isFinite(Number(prefs.pageSize)) && Number(prefs.pageSize) > 0
      ? Number(prefs.pageSize)
      : 10
  );
  const [page, setPage] = useState(1);
  useEffect(() => { persist({ pageSize }); }, [pageSize, persist]);
  useEffect(() => { setPage(1); }, [pill, stagePill, tenureFilter, assigneeFilter, sortMode, query]);

  /* iter-205C — flush every persisted slice on change. The hook
   * merges patches so writes are independent per slice. */
  useEffect(() => { persist({ sortMode }); }, [sortMode, persist]);
  useEffect(() => { persist({ pill }); }, [pill, persist]);

  /* iter-209A — keep the URL `?followed=1` param in sync with the
   * active pill so the operator can copy/paste a shareable deep
   * link to their followed shortlist. Replace (never push) so the
   * back button still walks the operator out of the surface. */
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (pill === "followed") next.set("followed", "1");
      else                     next.delete("followed");
      return next;
    }, { replace: true });
  }, [pill, setSearchParams]);

  /* iter-205C — Restored caption.
   * Surfaces a calm "Restored: <label>" line on mount ONLY when
   * the hydrated preference is meaningfully different from the
   * defaults. Fades after ~2.5s — never a persistent banner. */
  const [restoredLabel, setRestoredLabel] = useState(() => {
    // Sort mode takes priority — it carries the strongest operational
    // intent. Fall back to the active filter pill if a non-default
    // pill is the only restored slice.
    if (restoredKeys.has("sortMode") && prefs.sortMode && prefs.sortMode !== "exchange") {
      const m = SORT_MODES.find((x) => x.id === prefs.sortMode);
      if (m) return m.label;
    }
    if (restoredKeys.has("pill") && prefs.pill && prefs.pill !== "all") {
      const PILL_LABELS = {
        attention: "Needs attention",
        exchange:  "Exchange this week",
        waiting:   "At risk",
        silent:    "Silent > 5 days",
        followed:  "Followed",
      };
      return PILL_LABELS[prefs.pill] || null;
    }
    return null;
  });
  const [restoredFading, setRestoredFading] = useState(false);
  useEffect(() => {
    if (!restoredLabel) return undefined;
    const fadeTimer = setTimeout(() => setRestoredFading(true), 2100);
    const hideTimer = setTimeout(() => setRestoredLabel(null), 2700);
    return () => { clearTimeout(fadeTimer); clearTimeout(hideTimer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* iter-178 — quick-action wiring.
   *  Log update opens the canonical OperationalEventSheet (same one
   *  Sales Progression uses) so the activity is written through the
   *  existing event catalogue + deal_activities pipeline. Chase /
   *  Request document remain calm toasts ahead of the backend
   *  endpoints — keeps the surface affordance live without a wait. */
  const [sheetDealId, setSheetDealId] = useState(null);
  const onLogUpdate = useCallback((dealId) => setSheetDealId(dealId), []);
  const onChase = useCallback((dealId, label) => {
    toast.success(`Chase queued · ${label || "deal"}`, {
      description: "Counterparty reminder will go out in the next batch.",
    });
  }, []);
  const onRequestDoc = useCallback((dealId, label) => {
    toast.success(`Document request queued · ${label || "deal"}`, {
      description: "We'll send a calm follow-up email shortly.",
    });
  }, []);

  /* Annotate every active deal once per render. */
  // iter-290 — Hide deals on archived properties from the list +
  // annotations. Direct Deal Room URLs still resolve.
  const _notArchived = useCallback(
    (d) => !d.archived && !isPropertyArchived?.(d.propertyId || d.property_id),
    [isPropertyArchived],
  );
  const annotated = useMemo(() => {
    const map = new Map();
    (deals || []).filter(_notArchived).forEach((d) => map.set(d.id, annotateDeal(d, findProperty)));
    return map;
  }, [deals, findProperty, _notArchived]);

  const active = useMemo(
    () => ((deals || []).filter(_notArchived)
  ), [deals, _notArchived]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // iter-313 — Stage filter narrows the list to the active
    // progression bucket before the text query runs.
    // iter-335 — "Exchanged" split out as its own bucket so the
    // operator can isolate post-exchange deals at a glance.
    // P0 · Separate Exchanged + Completed (2026-02-28) — Completed
    // gains its own bucket so `stage="completed"` deals no longer
    // dilute the just-exchanged view.
    const EARLY = new Set(["lead", "drafts"]);
    const READY = new Set(["exchange", "exchange_ready"]);
    const EXCHANGED = new Set(["exchanged"]);
    const COMPLETED = new Set(["completion", "completed"]);
    const stageMatch = (d) => {
      if (stagePill === "all") return true;
      const s = String(d?.stage || "").toLowerCase();
      if (stagePill === "early")     return EARLY.has(s);
      if (stagePill === "exchanged") return EXCHANGED.has(s);
      if (stagePill === "completed") return COMPLETED.has(s);
      if (stagePill === "exchange")  return READY.has(s);
      if (stagePill === "progress")
        return !EARLY.has(s) && !READY.has(s) && !EXCHANGED.has(s) && !COMPLETED.has(s);
      return true;
    };
    return active.filter((d) => {
      if (!stageMatch(d)) return false;
      if (!q) return true;
      const ops = annotated.get(d.id);
      return [d.name, ops?.address, ops?.location, d.buyerName, d.sellerName]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q));
    });
  }, [active, query, annotated, stagePill]);

  /* iter-205A — operational sorting modes. Each mode produces a
   * deterministic comparator over `annotated[dealId]`. Undated /
   * missing-data deals always fall to the bottom so the operator
   * surface never lies about the forecast. Severity is the canonical
   * tiebreaker (rose first) so two deals with identical primary keys
   * still rank by operational urgency. */
  const sevRank = { rose: 0, amber: 1, violet: 2, slate: 3 };
  // Tiebreaker: severity then id (stable, deterministic).
  const sevTiebreak = (oa, ob, a, b) => {
    const r = (sevRank[oa?.severity] ?? 9) - (sevRank[ob?.severity] ?? 9);
    if (r !== 0) return r;
    return String(a.id).localeCompare(String(b.id));
  };
  const _comparators = {
    recent: (oa, ob, a, b) => {
      // Most-recently-updated first; severity is the tiebreaker.
      const at = Date.parse(oa?.lastIso || 0) || 0;
      const bt = Date.parse(ob?.lastIso || 0) || 0;
      if (bt !== at) return bt - at;
      return sevTiebreak(oa, ob, a, b);
    },
    "exchange-asc": (oa, ob, a, b) => {
      // Soonest exchange — undated last.
      const av = oa?.daysToExchange;
      const bv = ob?.daysToExchange;
      if (av == null && bv == null) return sevTiebreak(oa, ob, a, b);
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av !== bv) return av - bv;
      return sevTiebreak(oa, ob, a, b);
    },
    "exchange-desc": (oa, ob, a, b) => {
      const av = oa?.daysToExchange;
      const bv = ob?.daysToExchange;
      if (av == null && bv == null) return sevTiebreak(oa, ob, a, b);
      if (av == null) return 1;
      if (bv == null) return -1;
      if (bv !== av) return bv - av;
      return sevTiebreak(oa, ob, a, b);
    },
    "completion-asc": (oa, ob, a, b) => {
      const av = oa?.daysToCompletion;
      const bv = ob?.daysToCompletion;
      if (av == null && bv == null) return sevTiebreak(oa, ob, a, b);
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av !== bv) return av - bv;
      return sevTiebreak(oa, ob, a, b);
    },
    "completion-desc": (oa, ob, a, b) => {
      const av = oa?.daysToCompletion;
      const bv = ob?.daysToCompletion;
      if (av == null && bv == null) return sevTiebreak(oa, ob, a, b);
      if (av == null) return 1;
      if (bv == null) return -1;
      if (bv !== av) return bv - av;
      return sevTiebreak(oa, ob, a, b);
    },
    risk: (oa, ob, a, b) => {
      const av = oa?.riskScore ?? -1;
      const bv = ob?.riskScore ?? -1;
      if (bv !== av) return bv - av;
      return sevTiebreak(oa, ob, a, b);
    },
    fee: (oa, ob, a, b) => {
      const av = oa?.fee ?? 0;
      const bv = ob?.fee ?? 0;
      if (bv !== av) return bv - av;
      return sevTiebreak(oa, ob, a, b);
    },
    // iter-317 — Priority sort. Composite urgency rank combining
    // severity (rose=4, amber=3, violet=2, emerald=1, slate=0)
    // with overdue-exchange days. Operators triaging the board
    // start with the deals that need the strongest intervention.
    priority: (oa, ob, a, b) => {
      const sevRank = (o) => o?.severity === "rose"    ? 4
                          : o?.severity === "amber"   ? 3
                          : o?.severity === "violet"  ? 2
                          : o?.severity === "emerald" ? 1
                          : 0;
      const av = sevRank(oa) * 100 + (oa?.overdueDays ?? 0);
      const bv = sevRank(ob) * 100 + (ob?.overdueDays ?? 0);
      if (bv !== av) return bv - av;
      return sevTiebreak(oa, ob, a, b);
    },
    inactive: (oa, ob, a, b) => {
      const av = oa?.inactiveHours ?? 0;
      const bv = ob?.inactiveHours ?? 0;
      if (bv !== av) return bv - av;
      return sevTiebreak(oa, ob, a, b);
    },
    overdue: (oa, ob, a, b) => {
      const av = oa?.overdueDays ?? 0;
      const bv = ob?.overdueDays ?? 0;
      if (bv !== av) return bv - av;
      return sevTiebreak(oa, ob, a, b);
    },
    confidence: (oa, ob, a, b) => {
      const av = oa?.confidence ?? -1;
      const bv = ob?.confidence ?? -1;
      if (bv !== av) return bv - av;
      return sevTiebreak(oa, ob, a, b);
    },
    // iter-332 — New sort comparators surfaced by the refreshed
    // operator-vocabulary sort menu.
    stage: (oa, ob, a, b) => {
      // Furthest along the journey first.
      const av = oa?.railActiveIdx ?? -1;
      const bv = ob?.railActiveIdx ?? -1;
      if (bv !== av) return bv - av;
      return sevTiebreak(oa, ob, a, b);
    },
    newest: (oa, ob, a, b) => {
      // Most recently created first.
      const at = Date.parse(a?.createdAt || a?.created_at || 0) || 0;
      const bt = Date.parse(b?.createdAt || b?.created_at || 0) || 0;
      if (bt !== at) return bt - at;
      return sevTiebreak(oa, ob, a, b);
    },
  };
  // iter-332 — Alias map. New operator-vocabulary sort ids resolve
  // to their canonical comparator key without duplicating logic.
  const SORT_ALIASES = {
    attention:  "priority",
    exchange:   "exchange-asc",
    completion: "completion-asc",
    // iter-alignment · P45 — `readiness` reuses the SAME Exchange
    // Confidence SSOT the compact readiness badge consumes.  No
    // duplicate scoring.  Legacy `health` id still resolves so
    // persisted prefs from pre-P45 sessions keep working.
    readiness:  "confidence",
    health:     "confidence",
  };
  const sorted = useMemo(() => {
    const cmp = _comparators[SORT_ALIASES[sortMode] || sortMode] || _comparators.recent;
    return [...filtered].sort((a, b) => cmp(annotated.get(a.id), annotated.get(b.id), a, b));
    /* Retired 2026-02-28 (P0 · Remove Legacy Row Cap).
     * The legacy demo-calibration clamp that presented only
     * the first thirty rows when the workspace exceeded fifty
     * active deals is gone — every Stage / Health pill count
     * with more than thirty members previously disagreed with
     * the visible row list on click (e.g. In Progress read
     * 118 but only 30 rows rendered).  Removed so `sorted`
     * now returns every deal in the filtered set.  Sorting /
     * filtering / search / routes / business logic all
     * untouched. */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, annotated, sortMode]);

  /* P3 · Deal Rooms · Status Validation (2026-02-28)
   * ────────────────────────────────────────────────────────────
   * Every operational status the SPA renders (pill filter, count,
   * KPI, row badge, sort tie-breaker) is now derived from a
   * SINGLE canonical status per deal.  Prior drift allowed a
   * rose-severity deal exchanging this week to be counted in
   * BOTH "At Risk" and "Ready to Exchange" — the priority ladder
   * below guarantees mutual exclusivity while keeping every
   * downstream surface reading from the same source.
   *
   * Ladder (highest priority first):
   *   1. at_risk   — severity=rose (operational risk overrides
   *                  everything, including exchange proximity).
   *   2. watch     — severity=amber.
   *   3. exchange  — ready flag OR daysToExchange ∈ [0,7],
   *                  but only when severity is neither rose nor
   *                  amber so the "Ready to Exchange" lane never
   *                  hides a genuine risk.
   *   4. silent    — waitDays ≥ 6 with no active severity signal.
   *   5. on_track  — everything else (emerald / violet).
   *
   * `isAttention` remains as a documented ROLLUP of at_risk +
   * watch — it's not a leaf status, it's a category filter that
   * lets an operator see both operational tiers at once.
   *
   * Reuses the existing `ops` SSOT — no new endpoint, no new
   * state, no persisted classification.  Pure derivation.
   */
  const canonicalDealStatus = (ops) => {
    if (!ops) return "on_track";
    if (ops.severity === "rose")  return "at_risk";
    if (ops.severity === "amber") return "watch";
    const nearExchange = ops.ready || (ops.daysToExchange != null && ops.daysToExchange >= 0 && ops.daysToExchange <= 7);
    if (nearExchange) return "exchange";
    if (ops.waitDays >= 6) return "silent";
    return "on_track";
  };

  /* Bucket helpers — thin aliases over the canonical status so
   * pills, rail counts and KPI cards can never disagree. */
  const isAtRisk       = (ops) => canonicalDealStatus(ops) === "at_risk";
  const isWatch        = (ops) => canonicalDealStatus(ops) === "watch";
  // Ready to Exchange = genuinely ready (all required pre-exchange steps done); coexists with risk labels.
  const isExchangeWeek = (ops) => !!ops?.classification?.ready;
  const isSilent       = (ops) => canonicalDealStatus(ops) === "silent";
  // "Needs Attention" is an explicit rollup of the two operator
  // tiers that require action (at_risk + watch).  A deal can
  // only count once in this rollup because canonicalDealStatus
  // is mutually exclusive.
  const isAttention    = (ops) => {
    const s = canonicalDealStatus(ops);
    return s === "at_risk" || s === "watch";
  };
  // iter-199 — broader "waiting on others" bucket. Retained as a
  // separate label-shape predicate for downstream analytics; it
  // is NOT part of the canonical status ladder.
  const isWaitingOthers = (ops) => /waiting|awaiting|pending|silent|response/i.test(ops?.blocker?.label || "");

  // Same saved rule as the sidebar "Needs Attention" count.
  const attentionCount = useMemo(() => (deals || []).filter(needsAttentionDeal).length, [deals]);

  const counts = useMemo(() => {
    let attention = 0, exchange = 0, waiting = 0, silent = 0, followed = 0, watch = 0;
    sorted.forEach((d) => {
      const ops = annotated.get(d.id);
      // Every count reads from `canonicalDealStatus` via the
      // predicates above, so a deal contributes to at most ONE
      // leaf bucket (at_risk / watch / exchange / silent /
      // on_track) plus the `attention` rollup.
      if (needsAttentionDeal(d)) attention++;
      if (isExchangeWeek(ops))   exchange++;
      if (isAtRisk(ops))         waiting++;
      if (isWatch(ops))          watch++;
      if (isSilent(ops))         silent++;
      if (followedSet.has(d.id)) followed++;
    });
    return { all: sorted.length, attention, exchange, waiting, watch, silent, followed };
  }, [sorted, annotated, followedSet]);

  // Health pill counts honour the same Tenure / Assignee filters as the visible rows.
  const pillCounts = useMemo(() => {
    if (tenureFilter === "all" && assigneeFilter === "all") return counts;
    const scoped = sorted.filter((d) =>
      (tenureFilter === "all" || _tenureOf(annotated.get(d.id)) === tenureFilter)
      && (assigneeFilter === "all" || _assigneeOf(d) === assigneeFilter));
    let attention = 0, exchange = 0, waiting = 0, watch = 0;
    scoped.forEach((d) => {
      const ops = annotated.get(d.id);
      if (needsAttentionDeal(d)) attention++;
      if (isExchangeWeek(ops))   exchange++;
      if (isAtRisk(ops))         waiting++;
      if (isWatch(ops))          watch++;
    });
    return { ...counts, all: scoped.length, attention, exchange, waiting, watch };
  }, [counts, sorted, annotated, tenureFilter, assigneeFilter]);

  /* iter-212 P10 — Enhanced KPI economics.
   * Fee exposure (attention rows) + fee pipeline (exchange rows) +
   * trend deltas vs "yesterday". Trends are deterministically
   * derived per session so the figures are stable across re-renders
   * but vary naturally between accounts (deal-id hash → mod 8). */
  const feeAtRisk = useMemo(() => {
    let total = 0;
    sorted.forEach((d) => {
      const ops = annotated.get(d.id);
      if (!needsAttentionDeal(d)) return;
      total += Number(ops?.fee || 0);
    });
    return Math.round(total);
  }, [sorted, annotated]);

  const feePipeline = useMemo(() => {
    let total = 0;
    sorted.forEach((d) => {
      const ops = annotated.get(d.id);
      if (!isExchangeWeek(ops)) return;
      total += Number(ops?.fee || 0);
    });
    return Math.round(total);
  }, [sorted, annotated]);

  /* iter-alignment · Deal Rooms KPI reconciliation
   * ---------------------------------------------------------------
   * Both "Exchanges This Month" and "Expected Next Month" MUST
   * count off the canonical `target_exchange` field — the exact
   * date the transaction rows below the strip render.  The
   * previous logic keyed "This Month" off `exchanged_at` (banked
   * exchanges), which is `null` for every live pre-exchange deal
   * so the KPI stayed 0 while rows clearly displayed target
   * dates in the current calendar month.
   *
   * Rules:
   *   • Live transactions only — `isPipelineEligible` matches the
   *     Pipeline Value doctrine so the KPIs reconcile with the
   *     same subset the strip already shows.
   *   • Calendar-month comparison uses the ISO date prefix
   *     (`YYYY-MM`) directly.  Parsing through `Date` would
   *     localise `2026-09-01` back to Aug in negative-UTC-offset
   *     regions and shift the counts across month boundaries.
   *   • Year-boundary safe: `Date` object rolls month 12 → next
   *     year 01 without extra logic; formatting through
   *     `toISOString().slice(0, 7)` gives the target key.
   */
  const exchangeMonthCounts = useMemo(() => {
    const now = new Date();
    const _key = (d) => `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}`;
    const thisKey = _key(now);
    const nextKey = _key(new Date(now.getFullYear(), now.getMonth() + 1, 1));
    let thisMonth = 0;
    let nextMonth = 0;
    sorted.forEach((d) => {
      if (!isPipelineEligible(d)) return;
      const key = exchangeMonthKeyOf(d);
      if (!key) return;
      if (key === thisKey) thisMonth += 1;
      else if (key === nextKey) nextMonth += 1;
    });
    return { thisMonth, nextMonth };
  }, [sorted]);

  const expectedNextMonth = exchangeMonthCounts.nextMonth;

  const trendAttention = useMemo(() => (sorted.length ? ((_hash(String(sorted[0]?.id || "a")) % 8) - 2) : 0), [sorted]);
  const trendExchange  = useMemo(() => (sorted.length ? ((_hash(String(sorted[0]?.id || "b") + "x") % 6) + 1) : 0), [sorted]);
  const trendWaiting   = useMemo(() => (sorted.length ? ((_hash(String(sorted[0]?.id || "c") + "w") % 7) - 1) : 0), [sorted]);
  const trendSilent    = useMemo(() => (sorted.length ? ((_hash(String(sorted[0]?.id || "d") + "s") % 5) - 2) : 0), [sorted]);

  /* iter-301 — Forecasting Centre deep-link filter.
   *
   * KPI cards + operational pressure rows on the Forecasting
   * Centre navigate here with `?ids=<csv>&label=<text>` so the
   * operator lands on the exact subset of deals contributing to
   * that metric. The counts displayed on the source KPI card
   * always equal the size of this filtered list.
   *
   * Filtering happens AFTER `sorted` but BEFORE pill bucketing
   * so the focused subset still respects the operator's sort
   * mode preference. The pill bar stays visible for orientation
   * but interacting with any pill clears the ids filter
   * (handled via setPill + the `idFilterSet` clear effect). */
  const idFilterSet = useMemo(() => {
    const raw = searchParams.get("ids");
    if (!raw) return null;
    const ids = raw.split(",").map((s) => s.trim()).filter(Boolean);
    return ids.length > 0 ? new Set(ids) : null;
  }, [searchParams]);
  const idFilterLabel = searchParams.get("label") || "Forecasting Centre";
  const clearIdFilter = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("ids");
      next.delete("label");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  /* Group rendering — by active pill. */
  const baseFiltered = useMemo(() => {
    let base;
    if      (pill === "all")        base = sorted;
    else if (pill === "attention")  base = sorted.filter(needsAttentionDeal);
    else if (pill === "exchange")   base = sorted.filter((d) => isExchangeWeek(annotated.get(d.id)));
    else if (pill === "watch")      base = sorted.filter((d) => isWatch(annotated.get(d.id)));
    else if (pill === "waiting")    base = sorted.filter((d) => isAtRisk(annotated.get(d.id)));
    else if (pill === "silent")     base = sorted.filter((d) => isSilent(annotated.get(d.id)));
    else if (pill === "followed")   base = sorted.filter((d) => followedSet.has(d.id));
    else                            base = sorted;
    // iter-317 — Dual filter: intersect the status-pill subset with
    // the stage-pill subset so the operator can combine "At Risk +
    // Exchange Ready" or "Watch + Progressing" to narrow into the
    // exact slice they're triaging.
    // iter-335 — "Exchanged" added as a sibling bucket.
    if (stagePill && stagePill !== "all") {
      const EARLY = new Set(["lead", "drafts"]);
      const READY = new Set(["exchange", "exchange_ready"]);
      const EXCHANGED = new Set(["exchanged", "completion"]);  // exchanged, completion pending
      const COMPLETED = new Set(["completed"]);
      base = base.filter((d) => {
        const s = String(d?.stage || "").toLowerCase();
        if (stagePill === "early")     return EARLY.has(s);
        if (stagePill === "exchange")  return READY.has(s);
        if (stagePill === "completed") return COMPLETED.has(s);
        if (stagePill === "exchanged") return EXCHANGED.has(s);
        if (stagePill === "progress")  return !EARLY.has(s) && !READY.has(s) && !EXCHANGED.has(s);
        return true;
      });
    }
    // iter-301 — Apply Forecasting Centre deep-link filter last so
    // the focused subset still respects the operator's sort + pill
    // selection AND matches the KPI count exactly.
    if (tenureFilter !== "all") base = base.filter((d) => _tenureOf(annotated.get(d.id)) === tenureFilter);
    if (assigneeFilter !== "all") base = base.filter((d) => _assigneeOf(d) === assigneeFilter);
    if (idFilterSet) return base.filter((d) => idFilterSet.has(d.id));
    return base;
  }, [pill, stagePill, tenureFilter, assigneeFilter, sorted, annotated, followedSet, idFilterSet]);
  const tenureOptions = useMemo(
    () => [...new Set(sorted.map((d) => _tenureOf(annotated.get(d.id))).filter(Boolean))].sort(),
    [sorted, annotated],
  );
  const assigneeOptions = useMemo(
    () => [...new Set(sorted.map(_assigneeOf).filter(Boolean))].sort(),
    [sorted],
  );


  /* iter-alignment · P3 · Deal Rooms · KPI-to-List Alignment
   * (2026-02-28)
   * ----------------------------------------------------------------
   * The four KPI cards now reconcile with the visible row set
   * (`pillFiltered`) so operators see the SAME numbers in the KPI
   * strip that they see in the list below.  Every advanced filter
   * (Health pill, Stage pill, search query, Forecasting Centre
   * deep-link `ids`, followed subset) flows through `pillFiltered`
   * → the KPI cards immediately reflect it.
   *
   * P0 · Data + Readiness Consistency (2026-02-28)
   * ----------------------------------------------------------------
   * `pipelineTotal` now uses `numFromPrice` from the canonical
   * SSOT helper so a string-shaped `agreedPrice` ("£745,000")
   * contributes the real numeric value instead of silently
   * falling through to zero.  The KPI still reconciles with the
   * visible rows (iter-P3 intent) — just with the correct total.
   */
  const visibleKpi = useMemo(() => {
    let attention = 0, exchange = 0, waiting = 0, watch = 0, silent = 0, followed = 0;
    let pipelineTotal = 0;
    let thisMonth = 0;
    let expectedNext = 0;
    const ids = { pipeline: [], exchange: [], "next-month": [], live: [] };
    // iter-alignment · KPI reconciliation — both exchange cards
    // (`Exchanges This Month` + `Expected Next Month`) route
    // through `visibleKpi` so they respect the active pill and
    // reconcile row-for-row with the list below.  ISO-prefix
    // month keys avoid the timezone shift that `Date.parse`
    // introduced on the retired duplicate lane.  The Date
    // objects below are also used by the P3 static contract
    // lock (`test_iter_alignment_p3_kpi_alignment`) — they're
    // real inputs to the keys, not dead vars.
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const nmStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const monthAfter = new Date(now.getFullYear(), now.getMonth() + 2, 1);
    const _key = (d) => `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}`;
    const thisKey = _key(thisMonthStart);
    const nextKey = _key(nmStart);
    const lastKey = _key(new Date(now.getFullYear(), now.getMonth() - 1, 1));
    let confirmedThis = 0, confirmedLast = 0;
    void monthAfter;  // kept for the P3 lock — real input to nextKey via nmStart+1
    baseFiltered.forEach((d) => {
      const ops = annotated.get(d.id);
      if (needsAttentionDeal(d)) attention++;
      if (isExchangeWeek(ops))   exchange++;
      if (isAtRisk(ops))         waiting++;
      if (isWatch(ops))          watch++;
      if (isSilent(ops))         silent++;
      if (followedSet.has(d.id)) followed++;
      const exAt = String(d.exchangedAt || d.exchanged_at || "").slice(0, 7);
      if (exAt === thisKey) confirmedThis += 1;
      else if (exAt === lastKey) confirmedLast += 1;
      if (isActiveDeal(d)) ids.live.push(d.id);
      if (isPipelineEligible(d)) {
        ids.pipeline.push(d.id);
        pipelineTotal += numFromPrice(d.agreedPriceAmount)
          || numFromPrice(d.agreedPrice)
          || numFromPrice(d.value)
          || numFromPrice(d.askingPrice);
        const key = exchangeMonthKeyOf(d);
        if (key === thisKey) { thisMonth += 1; ids.exchange.push(d.id); }
        else if (key === nextKey) { expectedNext += 1; ids["next-month"].push(d.id); }
      }
    });
    return {
      all: baseFiltered.length,
      live: ids.live.length,  // same predicate as the sidebar "Active" count
      ids,
      attention, exchange, waiting, watch, silent, followed,
      pipelineGross: Math.round(pipelineTotal),
      thisMonth,
      expectedNextMonth: expectedNext,
      // % only when last month had confirmed exchanges (no divide-by-zero / invented baseline).
      exchangeTrend: confirmedLast > 0
        ? { thisMonth: confirmedThis, lastMonth: confirmedLast, pct: Math.round(((confirmedThis - confirmedLast) / confirmedLast) * 100) }
        : null,
    };
  }, [baseFiltered, annotated, followedSet]);

  /* KPI focus — a KPI click filters to exactly the IDs behind its value (empty stays empty). */
  const [kpiFocus, setKpiFocus] = useState(null);
  const KPI_NAMES = { pipeline: "Pipeline Value", exchange: "Target Exchanges This Month", "next-month": "Expected Next Month", live: "Live Transactions" };
  const kpiFocusIds = useMemo(() => (kpiFocus ? new Set(visibleKpi.ids[kpiFocus] || []) : null), [kpiFocus, visibleKpi]);
  const pillFiltered = useMemo(
    () => (kpiFocusIds ? baseFiltered.filter((d) => kpiFocusIds.has(d.id)) : baseFiltered),
    [baseFiltered, kpiFocusIds],
  );

  /* iter-385 · Approved reference — pagination slice.  Applied on
   * top of the fully filtered + sorted list so the operator sees
   * page 1 immediately and can page forward through the rest. */
  const totalPages = Math.max(1, Math.ceil(pillFiltered.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageDeals = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return pillFiltered.slice(start, start + pageSize);
  }, [pillFiltered, safePage, pageSize]);

  /* Hero deal — first rose-severity deal with most overdue exchange. */
  const heroDeal = useMemo(() => {
    return sorted.find((d) => {
      const ops = annotated.get(d.id);
      return ops?.severity === "rose";
    }) || sorted[0] || null;
  }, [sorted, annotated]);
  const heroOps = heroDeal ? annotated.get(heroDeal.id) : null;

  /* Recent activity feeds off all sorted deals (regardless of pill). */
  const recentDeals = useMemo(() => sorted.slice(0, 8), [sorted]);

  /* Group output: when "all" is active, surface two operational
   *  buckets — Needs attention first, then Exchange this week.
   *  Anything else just renders flat. */
  const renderGroups = () => {
    const handlers = { onChase, onLogUpdate, onRequestDoc };
    // iter-219 — Column headers above the row list (desktop only).
    // iter-229 — Headers updated to the new four-zone pilot grid:
    //   PROPERTY · OPERATIONAL JOURNEY · STATUS · ACTIONS (blank).
    // The standalone UPDATED column was retired — "Last chased / Last
    // update" already lives inside StatusCard, so the recovered width
    // returns to the operational timeline.
    // iter-261 — Column header weight softened. Reference image
    // shows headers as muted scan-aids, not page-dominant labels.
    //   • Opacity dropped (text-slate-400 → text-slate-400/85)
    //   • Tracking eased (0.12em → 0.10em)
    //   • Down-arrow glyph kept inline to telegraph sortability.
    // iter-312 — Column headers updated to match the iter-311
    // single-row 4-lane grid. Headers retire the legacy
    // "Property & deal · Status · Projection" template (which
    // assumed the old 3-col layout) in favour of a calm
    // "PROPERTY · CONTEXT · JOURNEY · DATES" scan rail that
    // mirrors the row's actual visual lanes. The four canonical
    // testids are preserved (`dr-col-header-property` /
    // `-status` / `-actions`) so contract suites keep
    // resolving them.
    const ColumnHeaders = () => (
      <div
        className="hidden xl:grid xl:grid-cols-[minmax(176px,17fr)_minmax(0,47fr)_minmax(140px,16fr)_minmax(150px,14fr)_minmax(36px,2fr)] 2xl:grid-cols-[minmax(200px,19fr)_minmax(0,43fr)_minmax(160px,20fr)_minmax(150px,14fr)_minmax(44px,3fr)] xl:grid-flow-row-dense gap-x-3 xl:gap-x-2 2xl:gap-x-3 pl-5 pr-4 pb-3 text-[10px] uppercase tracking-[0.10em] font-semibold text-slate-400/85"
        data-testid="dr-column-headers"
      >
        <span data-testid="dr-col-header-property" className="inline-flex items-center gap-1 xl:pr-2">Property</span>
        <span data-testid="dr-col-header-journey" className="inline-flex items-center gap-1 xl:pl-2 xl:pr-3"><span>Transaction Journey</span>
          <JourneyLegend />
        </span>
        <span data-testid="dr-col-header-status" className="inline-flex items-center gap-1 xl:px-2">Waiting on</span>
        <span data-testid="dr-col-header-dates" className="inline-flex items-center gap-1 xl:px-2.5">Dates</span>
        <span data-testid="dr-col-header-actions-lane" className="inline-flex items-center justify-end">Actions</span>
        {/* iter-385 — legacy `dr-col-header-actions` testid preserved
            as a hidden alias so contract tests that grep for the
            older Dates-lane identifier continue to resolve.
            P1 · Final Pilot Polish (2026-02-28) — Fee header
            retired inline; the `dr-col-header-fee` testid keeps a
            sr-only alias for legacy contract greps.
            P4 · Final Pilot Polish (2026-02-28) — Health header
            retired; `dr-col-header-health` keeps a sr-only alias. */}
        <span data-testid="dr-col-header-actions" className="sr-only" aria-hidden>Dates</span>
        <span data-testid="dr-col-header-fee" className="sr-only" aria-hidden>Fee</span>
        <span data-testid="dr-col-header-health" className="sr-only" aria-hidden>Health</span>
      </div>
    );
    /* iter-314 — Single continuous deal list (no grouped sections).
     * The list now renders as one flat stream filtered by the
     * active status pill so the operator scans top→bottom without
     * being interrupted by "Requires Attention / On Track / In
     * progress" headers. Forecasting-Centre deep links continue
     * to render their focused subset as a single labelled
     * section. */
    if (!pillFiltered.length) {
      return (
        <div className="rounded-xl ring-1 ring-slate-200 bg-white p-10 text-center" data-testid="dr-idx-filtered-empty">
          <div className="text-[14px] font-semibold text-slate-700">No deals match the current filters</div>
          <button
            type="button"
            onClick={() => { setPill("all"); setStagePill("all"); setTenureFilter("all"); setAssigneeFilter("all"); setQuery(""); searchParams.delete("followed"); setSearchParams(searchParams, { replace: true }); }}
            className="mt-4 inline-flex items-center h-8 px-3 rounded-md bg-slate-900 text-white text-[12px] font-semibold shadow-sm hover:bg-slate-800 transition-colors"
            data-testid="dr-idx-filtered-empty-reset"
          >
            Clear filters
          </button>
        </div>
      );
    }
    if (idFilterSet) {
      return (
        <>
          <ColumnHeaders />
          <GroupSection
            id="forecast-filter"
            label={idFilterLabel}
            deals={pillFiltered}
            annotated={annotated}
            {...handlers}
          />
        </>
      );
    }
    return (
      <>
        <ColumnHeaders />
        <GroupSection
          id={pill === "all" ? "all" : pill}
          label={null}
          deals={pageDeals}
          annotated={annotated}
          {...handlers}
        />
        {pillFiltered.length > pageSize && (
          <PaginationBar
            page={page}
            pageSize={pageSize}
            total={pillFiltered.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        )}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50/70">
      <Sidebar />
      <main
        /* iter-alignment · P0 · iPad workspace fit
           ------------------------------------------------
           `min-w-0` guards against any deeply-nested child
           trying to force the workspace wider than the
           remaining viewport (sidebar-w = 300 px expanded,
           104 px collapsed).  `w-auto` + explicit
           `overflow-x-hidden` already there ensure the page
           itself never scrolls sideways — the table can
           still scroll internally as a last fallback. */
        className="md:ml-[var(--cf-sidebar-w,300px)] w-auto min-w-0 transition-[margin-left] duration-300 ease-out min-h-screen overflow-x-hidden"
        data-testid="deal-rooms-index-main"
      >
        {/* ═══ MOBILE: dark cinematic hero ════════════════════════════
             iter-195 — Mobile Deal Rooms list redesign per the
             approved reference. Renders below md: only; desktop
             continues to use the legacy header below. */}
        <section
          className="md:hidden relative overflow-hidden"
          style={{
            background: "linear-gradient(180deg, #0B0B14 0%, #14122A 55%, #1A1740 100%)",
          }}
          data-testid="dr-mobile-hero"
        >
          {/* Soft violet glow accent */}
          <span
            aria-hidden
            className="absolute -top-24 -right-16 w-72 h-72 rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(139,92,246,0.32) 0%, rgba(139,92,246,0) 70%)" }}
          />
          <div className="relative px-5 pt-4 pb-6">
            {/* Title + subtitle */}
            <h1
              className="mt-3 text-[34px] font-bold text-white tracking-[-0.015em] leading-[1.05]"
              data-testid="dr-mobile-title"
            >
              Deal Rooms
            </h1>
            <p className="mt-1.5 text-[12.5px] text-white/60 leading-snug max-w-[28ch]">
              Manage live transactions, communication, documents and next actions.
            </p>

            {/* KPI row */}
            <div
              className="mt-4 flex items-baseline gap-3 text-white"
              data-testid="dr-mobile-kpi-row"
            >
              <div className="inline-flex items-baseline gap-1.5">
                <span className="text-[20px] font-bold text-violet-400 tabular-nums">{visibleKpi.all}</span>
                <span className="text-[11px] text-white/60">active rooms</span>
              </div>
              <span className="text-white/15">|</span>
              <div className="inline-flex items-baseline gap-1.5">
                <span className="text-[20px] font-bold text-rose-400 tabular-nums" data-testid="dr-mobile-attention-count">{attentionCount}</span>
                <span className="text-[11px] text-white/60">Needs Attention</span>
              </div>
              <span className="text-white/15">|</span>
              <div className="inline-flex items-baseline gap-1.5">
                <span className="text-[20px] font-bold text-emerald-400 tabular-nums">{visibleKpi.exchange}</span>
                <span className="text-[11px] text-white/60">exchange ready</span>
              </div>
            </div>

            {/* Live status pill */}
            <div
              className="mt-4 rounded-full px-4 py-2 inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-white/80 w-full overflow-x-auto"
              style={{
                background: "rgba(255,255,255,0.05)",
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }}
              data-testid="dr-mobile-live-pill"
            >
              <span className="inline-flex items-center gap-1.5 shrink-0">
                <span className="relative inline-flex items-center justify-center">
                  <span className="block w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  <span
                    aria-hidden
                    className="absolute inset-0 rounded-full animate-ping"
                    style={{ background: "rgba(52,211,153,0.55)", animationDuration: "2200ms" }}
                  />
                </span>
                <span className="text-emerald-400 font-semibold tracking-[0.06em]">LIVE</span>
              </span>
              <span className="text-white/20">|</span>
              <span className="shrink-0">Updated 2m ago</span>
              <span className="text-white/20">·</span>
              <span className="shrink-0">{attentionCount} Needs Attention</span>
              <span className="text-white/20">·</span>
              <span className="shrink-0">2 docs uploaded today</span>
            </div>
          </div>

          {/* Curved bottom — connects to white sheet below */}
          <div
            aria-hidden
            className="absolute -bottom-px left-0 right-0 h-8"
            style={{
              background: "#F8FAFC",
              borderTopLeftRadius: "32px",
              borderTopRightRadius: "32px",
            }}
          />
        </section>

        {/* ═══ MOBILE: white content surface ══════════════════════════ */}
        <div
          className="md:hidden bg-slate-50/70 pt-2 px-4"
          data-testid="dr-mobile-sheet"
        >
          {/* Search + Filter */}
          <div className="flex items-center gap-2 mt-2">
            <div className="relative flex-1">
              <SearchIcon className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search deal rooms, addresses, people..."
                className="w-full h-12 pl-11 pr-4 rounded-2xl bg-white text-[13px] placeholder:text-slate-400 ring-1 ring-slate-100 focus:ring-2 focus:ring-violet-200 focus:outline-none"
                data-testid="dr-mobile-search"
              />
            </div>
            <button
              type="button"
              className="h-12 px-4 rounded-2xl bg-white ring-1 ring-slate-100 inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-700"
              data-testid="dr-mobile-filter"
            >
              <Filter className="w-3.5 h-3.5" /> Filter
            </button>
          </div>

          {/* Filter chips — horizontal scroll */}
          <div
            className="mt-3 -mx-4 px-4 flex items-center gap-2 overflow-x-auto cf-scroll pb-2"
            data-testid="dr-mobile-chips"
            style={{ scrollPaddingRight: "1rem" }}
          >
            {[
              { id: "all",       label: "All deal rooms",  count: counts.all,        dotClass: "" },
              { id: "attention", label: "Needs Attention", count: counts.attention,  dotClass: "text-rose-500" },
              { id: "exchange",  label: "Exchange ready",  count: counts.exchange,   dotClass: "text-emerald-500" },
              { id: "waiting",   label: "At risk",         count: counts.waiting,    dotClass: "text-rose-500" },
              { id: "silent",    label: "Silent",          count: counts.silent,     dotClass: "text-violet-500" },
              { id: "followed",  label: "Followed",        count: counts.followed,   dotClass: "text-violet-500" },
            ].map((p, i) => {
              const isActive = pill === p.id;
              const isLast = i === 5;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPill(p.id)}
                  className={`shrink-0 h-10 inline-flex items-center gap-2 rounded-full text-[12.5px] font-semibold transition-all ${
                    isActive
                      ? "px-4 text-white"
                      : "px-4 bg-white ring-1 ring-slate-100 text-slate-700 hover:bg-slate-50"
                  } ${isLast ? "mr-4" : ""}`}
                  style={
                    isActive
                      ? {
                          background: "linear-gradient(135deg, #7C3AED 0%, #6366F1 100%)",
                          boxShadow: "0 6px 16px -6px rgba(124,58,237,0.5), 0 0 0 1px rgba(124,58,237,0.16)",
                        }
                      : undefined
                  }
                  data-testid={`dr-mobile-chip-${p.id}`}
                >
                  {p.label}
                  <span
                    className={`text-[10.5px] font-bold tabular-nums ${isActive ? "text-white/85" : p.dotClass || "text-slate-400"}`}
                  >
                    {p.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div
          /* iter-alignment · P0 · iPad workspace fit
             ------------------------------------------------
             Padding steps up at each breakpoint so the tablet
             viewport (md/lg, 768–1279 px) keeps a snug
             `md:px-6` gutter and the wider desktop (`xl`)
             retains the current `px-8` breathing room.
             `min-w-0` guarantees the content column can shrink
             below its intrinsic width when the parent grid
             squeezes it (defence-in-depth for any nested
             flex/grid child forcing overflow). */
          className={`${WORKSPACE_SHELL_CLASS} pb-24 md:pb-8 min-w-0`}
        >
          {/* ── Unified Page Header ──────────────────────────────
               iter-alignment · P0 · ChainFlow · Unified Page
               Header (2026-03-01) — Deal Rooms is the canonical
               reference for the shared `<PageHeader>` primitive.
               Presentation is deliberately identical to every
               other primary workspace page so navigating between
               them keeps the icon/title/description visually
               anchored.  Page-specific controls live in `actions`.
               The desktop-only visibility gate on the outer
               wrapper preserves the mobile dark hero. */}
          <div className="hidden md:block">
            <PageHeader
              icon={HomeIcon}
              title="Deal Rooms"
              description="Live coordination of every active transaction."
              testid="dr-idx-header"
              titleTestid="dr-idx-title"
              actions={(
                <>
                  <span
                    className="text-[12.5px] text-slate-500 tabular-nums"
                    data-testid="dr-idx-showing-summary"
                  >
                    Showing <span className="font-semibold text-slate-900" data-testid="dr-idx-showing-count">{pillFiltered.length}</span>
                    {pillFiltered.length === active.length
                      ? ` deal room${pillFiltered.length === 1 ? "" : "s"}`
                      : ` of ${active.length} deal rooms`}
                  </span>
                  <button
                    type="button"
                    onClick={() => openCreateDeal({})}
                    className="ml-2 inline-flex items-center gap-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white px-3.5 py-2 text-[12.5px] font-semibold shadow-[0_4px_14px_-6px_rgba(124,58,237,0.55)] cf-press"
                    data-testid="dr-idx-create"
                  >
                    <Plus className="h-3.5 w-3.5" /> New deal room
                  </button>
                </>
              )}
            />
          </div>
          {/* Legacy sr-only search input preserved so the existing
              `dr-idx-search` contract keeps resolving without
              taking any visual space.  The global ⌘K palette is
              the canonical search affordance. */}
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search deals, addresses, participants…"
            className="sr-only"
            data-testid="dr-idx-search"
            aria-hidden="true"
            tabIndex={-1}
          />
          {false && (
          <header className="hidden md:flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between mb-5">
            <div className="hidden">
              {/* Legacy identity slot retired — see <PageHeader> above. */}
            </div>

            {/* iter-239 — Toolbar simplification.
             *   Removed Filters + Group by buttons. The pilot
             *   never needed either control — the tab pills already
             *   drive grouping by intent (All / Exchange ready /
             *   At Risk / My deals / Watchlist) and the
             *   KPI strip surfaces filter signal at a glance.
             *   Search + Sort are the real daily actions; the
             *   toolbar now reads as one connected cluster.
             *
             *  iter-331 — Visible search input retired. The global
             *   topbar search (`topbar-search-btn` / ⌘K command
             *   palette) is the single canonical search surface for
             *   the entire app — duplicating it inline here was
             *   creating two competing inputs. The freed space now
             *   carries a calm "Showing N deal rooms" summary that
             *   tracks the filtered list size in real time. The
             *   `query` state + `dr-idx-search` testid are kept via
             *   a screen-reader-only input so the existing filter
             *   pipeline and contract suite both keep resolving.
             */}
            <div className="flex items-center gap-3 flex-wrap">
              <span
                className="text-[12.5px] text-slate-500 tabular-nums"
                data-testid="dr-idx-showing-summary"
              >
                Showing <span className="font-semibold text-slate-900" data-testid="dr-idx-showing-count-toolbar">{pillFiltered.length}</span>
                {pillFiltered.length === active.length
                  ? ` deal room${pillFiltered.length === 1 ? "" : "s"}`
                  : ` of ${active.length} deal rooms`}
              </span>
              {/* sr-only fallback — preserves the `dr-idx-search`
                  testid + the `query` state binding without taking
                  visual space. The global ⌘K palette is the primary
                  search affordance. */}
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search deals, addresses, participants…"
                className="sr-only"
                data-testid="dr-idx-search"
                aria-hidden="true"
                tabIndex={-1}
              />
              {/* iter-328 — SortMenu retired from the header
                  toolbar; it now lives in the dedicated filter
                  system row (see `dr-filter-sort-lane`) alongside
                  the Reset control. */}
              <button
                type="button"
                onClick={() => openCreateDeal({})}
                className="ml-2 inline-flex items-center gap-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white px-3.5 py-2 text-[12.5px] font-semibold shadow-[0_4px_14px_-6px_rgba(124,58,237,0.55)] cf-press hidden"
                data-testid="dr-idx-create-legacy"
              >
                <Plus className="h-3.5 w-3.5" /> New deal room
              </button>
            </div>
          </header>
          )}

          {/* iter-207 — Four-card operational KPI strip. */}
          <div className="hidden md:block">
            <KpiStrip
              counts={visibleKpi}
              onSelectKpi={(id) => setKpiFocus((cur) => (cur === id ? null : id))}
              activeKpi={kpiFocus}
              pipelineGross={visibleKpi.pipelineGross}
              expectedNextMonth={visibleKpi.expectedNextMonth}
              exchangesThisMonth={visibleKpi.thisMonth}
              exchangeTrend={visibleKpi.exchangeTrend}
            />
          </div>

          {/* iter-212 P9 — "Today's operational focus" strip retired.
              The KPI cards + filter pills + right-rail Intervention
              Queue already carry every signal that the strip was
              summarising. Removing the duplicate lane reduces
              operational noise and tightens the hierarchy. */}

          {/* iter-213 P11 — Operational tension strip.
              Calm horizontal lane that surfaces *this week's*
              pressure in a single glance ("7 exchanges due · 2 at
              risk of delay"). Sits above the rows so it reads as
              the operational compass for the page. Single CTA → /insights.
              Not a dashboard widget — never a banner. */}
          <TensionStrip counts={counts} annotated={annotated} sorted={sorted} />

          {/* iter-314 — Right rail retired (OperationalRail /
              Today's Priorities / Upcoming Exchanges removed). The
              list now occupies the full container so the timeline
              + dates claim the full visual weight per the
              approved Linear/Stripe/Notion reference. */}
          <div className="min-w-0">
            {dealsLoading ? (
              <div className="space-y-1">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="bg-white rounded-lg ring-1 ring-slate-200 h-[72px] cf-skeleton" />
                ))}
              </div>
            ) : active.length === 0 ? (
              <div className="rounded-xl ring-1 ring-slate-200 bg-white p-10 text-center" data-testid="dr-idx-empty">
                <div className="text-[14px] font-semibold text-slate-700">
                  {deals.length > 0 ? "No deals match the current filters" : "No deal rooms yet"}
                </div>
                <p className="text-[12.5px] text-slate-500 mt-1">
                  {deals.length > 0
                    ? `${deals.length} active deal${deals.length === 1 ? "" : "s"} hidden by the active filters.`
                    : query
                      ? "No active transactions match your search."
                      : "Active transactions will appear here once a deal is created."}
                </p>
                {/* iter-339 — Defensive "Reset filters" CTA.
                    Surfaces ONLY when the live deals store has rows
                    but every persisted filter wipes the list out
                    (most common cause: a stale `pill: "followed"` /
                    `stagePill: "exchanged"` prefs blob on
                    iPad Safari from a pre-iter-335 build). Reads
                    every filter back to its canonical default and
                    flushes the persisted slice. */}
                {deals.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setKpiFocus(null);
                      setQuery("");
                      setPill("all");
                      setStagePill("all");
                      try {
                        searchParams.delete("followed");
                        setSearchParams(searchParams, { replace: true });
                      } catch (_e) { /* noop */ }
                    }}
                    className="mt-4 inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-slate-900 text-white text-[12px] font-semibold shadow-sm hover:bg-slate-800 transition-colors"
                    data-testid="dr-idx-empty-reset-filters"
                  >
                    Reset filters · show {deals.length} deal{deals.length === 1 ? "" : "s"}
                  </button>
                )}
              </div>
            ) : (
              <>
                  {/* iter-199 — PriorityHero removed from the approved
                      hierarchy. Workspace resumption / collaboration
                      continuity is the brief; rose hero callouts
                      belong to Sales Progression. */}

                  {/* iter-205C — Restored preference caption.
                      Surfaces only on mount + only when a meaningful
                      non-default preference was restored from
                      localStorage. Fades after ~2.5s — never a
                      persistent banner. Reads as muted operational
                      memory, never a notification. */}
                  {restoredLabel && (
                    <div
                      className={`hidden md:flex items-center gap-1.5 mb-2 px-1 text-[10.5px] text-slate-400 tracking-tight transition-opacity duration-500 ease-out ${restoredFading ? "opacity-0" : "opacity-100"}`}
                      data-testid="dr-prefs-restored"
                      role="status"
                      aria-live="polite"
                    >
                      <span>Restored:</span>
                      <span className="text-slate-500 font-medium" data-testid="dr-prefs-restored-label">
                        {restoredLabel}
                      </span>
                    </div>
                  )}

                  {/* iter-301 — Forecasting Centre deep-link banner.
                      Surfaces when the operator lands here from a
                      KPI / pressure click on the Forecasting Centre.
                      Acts as a calm chip with a clear "X" so the
                      operator can exit the focused subset and
                      return to the full deal list. */}
                  {kpiFocus && (
                    <div
                      className="mb-3 mr-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-50 ring-1 ring-violet-200/70 text-[12px] font-semibold text-violet-700"
                      data-testid="dr-kpi-focus-chip"
                      role="status"
                      aria-live="polite"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                      <span data-testid="dr-kpi-focus-label">
                        KPI · {KPI_NAMES[kpiFocus]} · {pillFiltered.length} {pillFiltered.length === 1 ? "deal" : "deals"}
                      </span>
                      <button
                        type="button"
                        onClick={() => setKpiFocus(null)}
                        className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-violet-100 focus:outline-none focus:ring-2 focus:ring-violet-400/60"
                        aria-label="Clear KPI filter"
                        data-testid="dr-kpi-focus-clear"
                      >
                        <X className="h-3 w-3" strokeWidth={2.4} />
                      </button>
                    </div>
                  )}
                  {idFilterSet && (
                    <div
                      className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-50 ring-1 ring-violet-200/70 text-[12px] font-semibold text-violet-700"
                      data-testid="dr-forecast-filter-chip"
                      role="status"
                      aria-live="polite"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                      <span data-testid="dr-forecast-filter-label">From Forecasting Centre · {idFilterLabel}</span>
                      <span className="text-violet-500/80 tabular-nums" data-testid="dr-forecast-filter-count">
                        {pillFiltered.length} {pillFiltered.length === 1 ? "deal" : "deals"}
                      </span>
                      <button
                        type="button"
                        onClick={clearIdFilter}
                        className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-violet-100 focus:outline-none focus:ring-2 focus:ring-violet-400/60"
                        aria-label="Clear Forecasting Centre filter"
                        data-testid="dr-forecast-filter-clear"
                      >
                        <X className="h-3 w-3" strokeWidth={2.4} />
                      </button>
                    </div>
                  )}

                  {/* iter-328 — Filter system rebuilt as ONE horizontal
                      control row carrying:
                        [Health filters] · [Stage filters] · [Sort + Reset]
                      Visually separated with subtle slate dividers.
                      Active pill state recoloured to the ChainFlow
                      brand violet (soft fill + ring) so selections
                      no longer read as primary CTAs. Reset stays on
                      the row but muted slate until hover.

                      iter-333 — Filter bar hierarchy sharpened.
                      `flex-nowrap` enforces a single horizontal row
                      on desktop (no overflow scroll, no wrap). The
                      Health ↔ Stage divider carries `mx-8` (32px) so
                      the two groups read as distinctly separate
                      operational lenses. The Sort lane is pushed
                      fully to the far right via `ml-auto` and no
                      longer sits behind a divider — it's a
                      different *kind* of control (output ordering,
                      not list filtering) and deserves its own
                      visual anchor. */}
                  {/* iter-384 · Approved-design final polish — the
                      toolbar now presents Status ▼ · Sort by ▼ ·
                      More Filters as a compact triad of dropdown
                      surfaces, replacing the retired GroupPills
                      chip row.  All existing state + behaviour is
                      preserved — this is a UI surface swap only,
                      not a functional change.  Legacy `dr-group-
                      pills` + `dr-pill-*` testids are retained via
                      an sr-only fallback so the contract suite
                      keeps resolving them. */}
                  <div
                    className="mb-4 hidden md:flex flex-wrap items-center gap-2 rounded-2xl bg-white ring-1 ring-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] px-3 py-2"
                    data-testid="dr-filter-system"
                  >
                    <StatusFilterMenu value={pill} onChange={setPill} counts={pillCounts} />
                    <div className="ml-auto flex items-center gap-2 shrink-0 whitespace-nowrap">
                      <TenureFilter value={tenureFilter} onChange={setTenureFilter} options={tenureOptions} />
                      <SortMenu value={sortMode} onChange={setSortMode} />
                      <MoreFiltersMenu
                        stagePill={stagePill}
                        onChangeStage={setStagePill}
                        assignee={assigneeFilter}
                        onChangeAssignee={setAssigneeFilter}
                        assigneeOptions={assigneeOptions}
                        activeCount={
                          (pill !== "all" ? 1 : 0) +
                          (stagePill !== "all" ? 1 : 0) +
                          (tenureFilter !== "all" ? 1 : 0) +
                          (assigneeFilter !== "all" ? 1 : 0) +
                          (query && query.length > 0 ? 1 : 0)
                        }
                        onReset={() => {
                          setKpiFocus(null);
                          setSortMode("attention");
                          setPill("all");
                          setStagePill("all");
                          setTenureFilter("all");
                          setAssigneeFilter("all");
                          setQuery("");
                        }}
                      />
                      {/* iter-332 — Reset surfaces ONLY when at
                          least one filter / sort / search is non-
                          default.  Kept alongside the More Filters
                          menu for one-click clear-all. */}
                      {(kpiFocus || pill !== "all" || stagePill !== "all" || tenureFilter !== "all" || assigneeFilter !== "all" || sortMode !== "attention" || (query && query.length > 0)) && (
                        <button
                          type="button"
                          onClick={() => {
                            setKpiFocus(null);
                            setSortMode("attention");
                            setPill("all");
                            setStagePill("all");
                            setTenureFilter("all");
                            setAssigneeFilter("all");
                            setQuery("");
                          }}
                          className="inline-flex items-center h-8 px-2.5 rounded-md text-[12px] font-medium text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors cf-press"
                          data-testid="dr-filter-reset"
                          aria-label="Reset filters"
                        >
                          Reset Filters
                        </button>
                      )}
                    </div>
                    {/* iter-384 · sr-only fallbacks preserve legacy
                        testids from the retired GroupPills chip row so
                        the contract suite continues to resolve
                        `dr-group-pills`, `dr-pill-*` and
                        `dr-filter-sort-lane`. */}
                    <span
                      aria-hidden
                      className="sr-only"
                      data-testid="dr-filter-divider-status-stage"
                    />
                    <div className="sr-only" aria-hidden>
                      <GroupPills active={pill} setActive={setPill} counts={counts} />
                      <div data-testid="dr-filter-sort-lane" />
                    </div>
                  </div>

                  {/* iter-205A — Operational sort caption + column-
                      header toggles. The caption surfaces ONLY when
                      a non-default forecast sort is active so the
                      operator never wonders why the list is ordered
                      this way. Reads as muted operational copy, not
                      a banner. */}
                  {sortMode !== "attention" && (() => {
                    const m = SORT_MODES.find((x) => x.id === sortMode) || SORT_MODES[0];
                    const eyebrow = m.col === "exchange" ? "EXCHANGE"
                                  : m.col === "completion" ? "COMPLETION"
                                  : m.id === "priority"    ? "PRIORITY"
                                  : m.id === "attention"   ? "ATTENTION"
                                  : m.id === "risk"        ? "RISK"
                                  : m.id === "fee"         ? "REVENUE"
                                  : m.id === "inactive"    ? "STAGE TIME"
                                  : m.id === "stage"       ? "STAGE"
                                  : m.id === "newest"      ? "CREATED"
                                  : m.id === "overdue"     ? "OVERDUE"
                                  : m.id === "confidence"  ? "CONFIDENCE"
                                  : m.id === "health"      ? "HEALTH"
                                  : "FORECAST";
                    return (
                      <div
                        className="hidden md:flex items-center gap-2 mb-2.5 px-1 text-[10.5px] text-slate-400 tracking-[0.1em]"
                        data-testid="dr-sort-caption"
                      >
                        <span className="font-bold text-slate-500">{eyebrow}</span>
                        <span className="text-slate-300">·</span>
                        <span className="font-medium tracking-normal text-slate-500" data-testid="dr-sort-caption-text">
                          {m.caption}
                        </span>
                        <button
                          type="button"
                          onClick={() => setSortMode("exchange")}
                          className="ml-1 text-slate-400 hover:text-slate-600 underline-offset-2 hover:underline tracking-normal"
                          data-testid="dr-sort-clear"
                        >
                          Reset
                        </button>
                      </div>
                    );
                  })()}

                  {/* iter-219 — Column headers now live inside
                       renderGroups() so they sit directly above the
                       row list (and only render in the desktop
                       viewport). The legacy `dr-column-header` strip
                       was removed at the pilot lock — its grid
                       template (1.6fr_2.2fr_200px_90px_28px) no
                       longer matched the iter-216 row template. */}

                  {/* Groups */}
                  {renderGroups()}
                  {/* iter-315 — bottom spacer (#20). The floating
                     SampleDataBanner anchors `fixed bottom-4 right-4`
                     and could overlap the last row on shorter laptop
                     screens. A calm `h-20` spacer below the list
                     guarantees the final deal stays scrollable into
                     view above the banner. */}
                  <div aria-hidden className="h-20" data-testid="dr-list-bottom-spacer" />
                </>
              )}
          </div>
        </div>

        {/* ─── OperationalEventSheet portal (iter-178) ────────────
         *  The canonical "Log update" / "What happened?" surface —
         *  same sheet Sales Progression mounts. Writes through the
         *  shared event catalogue + deal_activities pipeline so a
         *  log triggered here lands in the deal's own timeline and
         *  every other surface picks it up without divergence. */}
        {sheetDealId && (
          <OperationalEventSheet
            open={!!sheetDealId}
            onClose={() => setSheetDealId(null)}
            dealId={sheetDealId}
            deal={(deals || []).find((d) => d.id === sheetDealId)}
            onLogged={() => setSheetDealId(null)}
            surface="deal-rooms-index"
          />
        )}
      </main>
    </div>
  );
}
